export type HelpTab = 'platform' | 'lca' | 'ecoinvent' | 'dpp' | 'regulations';

export type QuickAction = {
  description: string;
  id: HelpTab;
  title: string;
};

export type KnowledgeCard = {
  bullets: string[];
  link?: {
    label: string;
    url: string;
  };
  status: string;
  summary: string;
  tab: HelpTab;
  title: string;
};

export type ChecklistItem = {
  id: string;
  label: string;
};

export type FaqItem = {
  answer: string;
  question: string;
  tab: HelpTab | 'all';
};

export type OfficialResource = {
  label: string;
  summary: string;
  url: string;
};

export type WorkflowStep = {
  owner: string;
  outputs: string[];
  summary: string;
  tab: HelpTab;
  title: string;
};

export type GuideEntry = {
  bullets: string[];
  description: string;
  title: string;
};

export type GuideSection = {
  badge: string;
  description: string;
  entries: GuideEntry[];
  tab: HelpTab;
  title: string;
  visualId?: 'analytics-guide' | 'passport-guide' | 'report-guide';
};

export type FlowchartBuilderStep = {
  bullets: string[];
  description: string;
  title: string;
};

export type FlowchartExampleStage = {
  items: string[];
  title: string;
};

export type HelpCentreContent = {
  badge: string;
  breadcrumbs: Array<{ name: string; url: string }>;
  checklistItems: ChecklistItem[];
  checklistLabel: string;
  checklistProgressLabel: string;
  checklistTitle: string;
  disclaimer: string;
  faqTitle: string;
  faqs: FaqItem[];
  flowchartAreaLabel: string;
  flowchartBoundaryLabel: string;
  flowchartCanvasHint: string;
  flowchartCanvasTitle: string;
  flowchartDescription: string;
  flowchartExampleStages: FlowchartExampleStage[];
  flowchartExampleTitle: string;
  flowchartFilterByStageLabel: string;
  flowchartGroupCradleLabel: string;
  flowchartGroupGraveLabel: string;
  flowchartLegendLabel: string;
  flowchartLegendItems: string[];
  flowchartNoObjectsLabel: string;
  flowchartStepLabel: string;
  flowchartSteps: FlowchartBuilderStep[];
  flowchartSummaryLabel: string;
  flowchartSummaryValue: string;
  flowchartSummaryUnit: string;
  flowchartTabs: {
    analytics: string;
    flowchart: string;
    impact: string;
  };
  flowchartTitle: string;
  guideDescription: string;
  guideEmptyDescription: string;
  guideEmptyTitle: string;
  guideSections: GuideSection[];
  guideTitle: string;
  heroDescription: string;
  heroTitle: string;
  knowledgeCards: KnowledgeCard[];
  videoInstructionsLabel: string;
  noMatchingCardsDescription: string;
  noMatchingCardsTitle: string;
  noMatchingQuestionsLabel: string;
  officialResourceButtonLabel: string;
  officialResources: OfficialResource[];
  officialResourcesTitle: string;
  quickActions: QuickAction[];
  quickActionsTitle: string;
  searchPlaceholder: string;
  searchNoExactMatchDescription: string;
  searchNoExactMatchTitle: string;
  searchResultsLabel: string;
  searchSuggestionOpenLabel: string;
  searchSuggestionsLabel: string;
  stats: string[];
  tabs: Record<HelpTab, string>;
  workflowDescription: string;
  workflowEmptyDescription: string;
  workflowEmptyTitle: string;
  workflowOutputLabel: string;
  workflowOwnerLabel: string;
  workflowSteps: WorkflowStep[];
  workflowTitle: string;
};

const ECOINVENT_FAQS_EN: FaqItem[] = [
  {
    question:
      'When should an analyst choose a market dataset instead of a production dataset?',
    answer:
      'Choose a production-type dataset when you are modelling how a product or intermediate is manufactured by a specific process. Choose a market dataset when you need the average supply mix available to a purchaser in a geography, including domestic production and imports as modelled by ecoinvent. A market dataset is usually the better choice for purchased inputs when you do not know the exact producer route. A production dataset is usually the better choice when you do know the manufacturing route, technology and geography and want to model that route explicitly.',
    tab: 'ecoinvent',
  },
  {
    question:
      'How should geography be selected between country, region, RoW and GLO?',
    answer:
      'Start with the geography that best matches where the activity physically happens. Prefer country-specific datasets when the supply chain is country-specific and material to results. Use regional datasets such as RER when the supply base is truly regional or when no robust country dataset exists. Use RoW only when the activity clearly represents a non-specified rest-of-world mix, and use GLO only when a global market or globally averaged supply is the intended modelling choice. Global is not automatically safer; it can dilute an otherwise relevant national or regional signal.',
    tab: 'ecoinvent',
  },
  {
    question:
      'Why can the Envirly LCA platform and ecoQuery show different results for what looks like the same activity?',
    answer:
      'The first checks are consistency of ecoinvent version, system model and selected geography. In the Envirly LCA platform, the product version is fixed at product creation and ecoinvent is accessed from the platform, with a link-out to ecoQuery in the cut-off system model. If a user compares that against another version, APOS, consequential or an activity in another geography, differences are expected. Results can also differ when the platform uses a grouped activity and the user later chooses a different geography variant than the one inspected in ecoQuery.',
    tab: 'ecoinvent',
  },
  {
    question: 'What should be done when there is no exact ecoinvent match?',
    answer:
      'Use a documented proxy rather than a superficially similar but methodologically weak match. The proxy should preserve the main function, physical form, unit logic, technology level and geography as far as reasonably possible. Record the proxy rationale in assumptions or comments so that a reviewer can understand why it was chosen, what it stands in for and where primary data would be needed to improve the model later.',
    tab: 'ecoinvent',
  },
  {
    question:
      'How should an expert interpret RoW, RER, market group and treatment labels?',
    answer:
      'These labels are modelling signals, not just naming noise. RoW means rest of world and should be read as a residual geography, not a generic default. RER represents Europe in ecoinvent geography logic and can differ materially from a single-country dataset. Market group datasets aggregate supply beyond one market boundary and may differ from a direct market dataset in composition and intended interpretation. Treatment datasets model waste treatment or end-of-life operations and should not be used as ordinary production proxies for materials or components.',
    tab: 'ecoinvent',
  },
  {
    question:
      'When is it better to use a custom factor in the Envirly LCA platform instead of an ecoinvent dataset?',
    answer:
      'Use a custom factor when the relevant process is company-specific and the available ecoinvent proxy would hide a material technology difference, when a supplier provides auditable primary impact data that is methodologically aligned with the study, or when no credible ecoinvent proxy exists for the intended modelling object. A custom factor should still be documented with source, scope, unit and quality limitations because a custom value without traceability is weaker than a well-justified secondary dataset.',
    tab: 'ecoinvent',
  },
];

const ECOINVENT_GUIDE_SECTIONS_EN: GuideSection[] = [
  {
    badge: 'Ecoinvent workflow',
    description:
      'This section explains how the Envirly LCA platform structures ecoinvent access and search, why the grouped activity step matters and how to move from a product idea to a defendable dataset choice.',
    tab: 'ecoinvent',
    title: 'How ecoinvent selection works in the Envirly LCA platform',
    entries: [
      {
        title: 'Start with the modelling object, not with the first keyword',
        description:
          'Before searching, define what the object actually represents in the model: purchased input, manufacturing process, transport leg, treatment route or market-average supply.',
        bullets: [
          'Write down the object function in one sentence before opening search.',
          'State whether the object is a material, process, service, transport or waste operation.',
          'Identify the expected reference flow and unit so that search results can be rejected quickly when they mismatch.',
          'If the object is part of a product-specific route, note what primary data is already known before relying on secondary datasets.',
        ],
      },
      {
        title: 'Use the grouped activity step deliberately',
        description:
          'In the Envirly LCA platform, users first choose a grouped activity, which combines one activity name and one reference product across multiple geographies. Only after that does the user select the exact geography variant.',
        bullets: [
          'This flow prevents users from treating geography as an afterthought and mirrors how dataset families are structured in ecoinvent.',
          'The first choice should answer what process or market is relevant; the second choice should answer where that activity happens.',
          'Review geographies count as a signal that multiple regional variants exist and require a conscious decision.',
          'Do not assume the first result is the correct geography variant even if the activity wording looks exact.',
        ],
      },
      {
        title: 'Use organizational memory, but verify it',
        description:
          'The Envirly LCA platform surfaces recent or previously used activities because repeated categories often converge on a small set of acceptable proxies. That convenience should accelerate review, not replace it.',
        bullets: [
          'Previously used activities are useful when the same subcategory is repeatedly modelled across products.',
          'Reusing a past dataset is justified only if function, technology, unit and geography remain comparable.',
          'If the product context changed, treat the quick pick as a shortlist candidate and re-check the dataset family.',
          'Keep comments clear enough that the next analyst understands whether a reused dataset is exact, approximate or a temporary proxy.',
        ],
      },
      {
        title:
          'Use ecoQuery as a verification tool, not as a separate modelling universe',
        description:
          'The Envirly LCA platform gives access to ecoinvent from the product workflow and links users to ecoQuery in the cut-off system model. The purpose is to inspect metadata, compare sibling geographies and understand the dataset, not to silently switch system models mid-study.',
        bullets: [
          'Check that the ecoQuery page uses the same ecoinvent version as the product in the Envirly LCA platform.',
          'Use the ecoQuery view to inspect the activity family, metadata and neighbouring geography variants.',
          'When comparing results, stay within the same system model and version or the comparison becomes methodologically invalid.',
          'Document any conclusion drawn from ecoQuery back into the Envirly LCA platform assumptions or comments so the reasoning stays with the product record.',
        ],
      },
    ],
  },
  {
    badge: 'Dataset choice',
    description:
      'Most mistakes happen because a dataset sounds right linguistically but is wrong functionally. This section focuses on the checks experts should apply before accepting a dataset.',
    tab: 'ecoinvent',
    title: 'How to choose the right dataset family',
    entries: [
      {
        title: 'Distinguish market, market group, production and treatment',
        description:
          'Activity type is a methodological choice. Similar labels can represent different system logic and therefore different inventories and impacts.',
        bullets: [
          'Use production-type datasets when modelling a specific manufacturing route or transformation process.',
          'Use market datasets when the object represents purchased supply from a geography-specific mix rather than one known producer.',
          'Treat market group datasets as broader supply aggregations that may differ from a direct market dataset in composition and intended interpretation.',
          'Use treatment datasets only for waste management or end-of-life operations and never as a shortcut for ordinary material production.',
        ],
      },
      {
        title: 'Verify the reference product before reading the impact result',
        description:
          'The reference product defines what one unit of the dataset delivers. If that logic does not match the modelled object, the dataset is wrong even if the impact number looks plausible.',
        bullets: [
          'Check the reference product name and make sure it reflects the same output as the object in the Envirly LCA platform.',
          'Check the reference product unit and make sure quantity conversion is explicit and reviewable.',
          'Check the reference product amount where relevant, especially for services or process outputs that are not expressed as one neat physical unit.',
          'Reject a dataset early if it represents a different physical form, concentration, treatment state or service boundary than the object in scope.',
        ],
      },
      {
        title: 'Resolve geography hierarchically',
        description:
          'Choose geography by following the physical supply route from most specific to least specific, not by picking the largest available region.',
        bullets: [
          'Start with the country of production or the known supply location whenever it is materially relevant.',
          'If no country-specific dataset exists, move to the closest defensible regional dataset and note that limitation explicitly.',
          'Use RoW or GLO only when they genuinely reflect the intended supply logic or when no stronger geography is available.',
          'Be especially careful with electricity, transport and waste management because geography often shifts results materially in these categories.',
        ],
      },
      {
        title: 'Choose proxies transparently when no exact match exists',
        description:
          'Proxy selection is normal expert work in LCA, but it must be explicit. A hidden proxy is a governance problem even when the technical choice is reasonable.',
        bullets: [
          'Prioritize function, process technology, material composition and geography in that order when building a proxy rationale.',
          'Avoid choosing a dataset only because the commercial wording looks similar to the product name.',
          'State what the proxy excludes or overstates so reviewers can judge the likely bias direction.',
          'Record where supplier or plant-specific data would replace the proxy in the next iteration of the model.',
        ],
      },
    ],
  },
  {
    badge: 'Consistency and review',
    description:
      'Dataset choice is only one part of good ecoinvent use. Experts also need version control, system model consistency and quality review discipline.',
    tab: 'ecoinvent',
    title: 'Version control, system model discipline and data quality',
    entries: [
      {
        title: 'Keep version stable at product level',
        description:
          'The Envirly LCA platform binds a product to one ecoinvent version because switching background data mid-project makes trend analysis and review difficult to defend.',
        bullets: [
          'Do not compare two product iterations as if they were equivalent if the background database version changed in between.',
          'When a new ecoinvent version becomes available, treat migration as a controlled methodology update rather than a silent refresh.',
          'Check activity availability after duplication or copying between products because a previously linked activity may not have datapoints in the new version.',
          'When benchmarking against external studies, verify both the version and the system model before drawing conclusions from differences.',
        ],
      },
      {
        title: 'Do not mix system models when validating results',
        description:
          'Experts often compare outputs across tools, but a comparison is meaningful only when the same system model is used. The Envirly LCA platform currently links to ecoQuery in cut-off.',
        bullets: [
          'Cut-off, APOS, consequential and EN15804-oriented views should not be treated as interchangeable interpretations of the same result.',
          'If a result differs from another tool, first check whether the other result was generated under another system model.',
          'Document the chosen system model in the study assumptions and keep comparisons inside that methodological frame.',
          'Be careful with recycling and end-of-life claims because system model choice can materially change burden allocation logic.',
        ],
      },
      {
        title: 'Assess data quality beyond the impact number',
        description:
          'ecoinvent datasets come with methodological context, representativeness assumptions and uncertainty logic. Experts should review adequacy, not only ranking.',
        bullets: [
          'Assess technological, geographical and temporal representativeness relative to the modelled object.',
          'Treat pedigree and uncertainty information as signals for review depth, not as a substitute for engineering judgement.',
          'Focus quality review on hotspots and high-contribution objects first because that is where dataset choice matters most.',
          'Use sensitivity or scenario testing when several plausible proxies exist and the choice can materially shift the result.',
        ],
      },
      {
        title: 'Know when to escalate to custom data',
        description:
          'Secondary datasets are efficient, but some modelling questions require primary or custom data to remain credible.',
        bullets: [
          'Escalate when the company process is materially different from the generic proxy in energy mix, yield, scrap rate, recycled content or technology.',
          'Escalate when a supplier provides auditable product-specific data that is more representative than the generic background route.',
          'Escalate when a regulatory or customer use case requires a stronger evidence trail than a generic proxy can provide.',
          'If a custom factor is used, keep the source, unit, scope and boundary explanation as rigorous as for any dataset choice.',
        ],
      },
    ],
  },
];

const ECOINVENT_KNOWLEDGE_CARDS_EN: KnowledgeCard[] = [
  {
    bullets: [
      'The Envirly LCA platform separates grouped activity selection from geography selection on purpose.',
      'The first question is what process family is relevant; the second is where that activity happens.',
      'This reduces false precision caused by picking a geography before understanding the dataset family.',
    ],
    status: 'Ecoinvent in Envirly LCA platform',
    summary:
      'A good search process in the Envirly LCA platform mirrors ecoinvent structure: define the activity family first, then choose the exact geography variant.',
    tab: 'ecoinvent',
    title:
      'Why the Envirly LCA platform ecoinvent flow has two decisions instead of one',
  },
  {
    bullets: [
      'A convincing title is not enough; reference product, unit and activity type must also fit.',
      'Market, market group, production and treatment names may look similar but represent different modelling logic.',
      'The right dataset is the one that preserves functional meaning, not the one with the nicest wording.',
    ],
    status: 'Dataset review',
    summary:
      'The most common expert error is accepting a dataset because its wording looks right before checking what the dataset actually delivers.',
    tab: 'ecoinvent',
    title: 'Why dataset names are useful clues but weak evidence',
  },
  {
    bullets: [
      'Check ecoinvent version, system model and geography before comparing numbers across tools.',
      'The Envirly LCA platform currently links to ecoQuery in cut-off, so APOS or consequential comparisons will not be like-for-like.',
      'Use ecoQuery to inspect datasets, not to silently swap methodology during review.',
    ],
    status: 'Validation',
    summary:
      'Most apparent discrepancies between the Envirly LCA platform and external checks are methodological mismatches, not software defects.',
    tab: 'ecoinvent',
    title:
      'How to validate Envirly LCA platform results against ecoQuery without false discrepancies',
  },
];

const ECOINVENT_WORKFLOW_STEPS_EN: WorkflowStep[] = [
  {
    owner: 'LCA practitioner / product owner',
    outputs: [
      'Object function defined',
      'Expected unit identified',
      'Known geography constraints listed',
    ],
    summary:
      'Frame the modelling object before searching so the dataset choice is driven by function, unit and supply logic rather than by wording alone.',
    tab: 'ecoinvent',
    title: '1. Define what the object represents',
  },
  {
    owner: 'LCA practitioner',
    outputs: [
      'Shortlist of grouped activities',
      'Rejected false positives documented mentally or in notes',
      'Relevant activity type identified',
    ],
    summary:
      'Access ecoinvent from the Envirly LCA platform, search grouped activities there and reject options that fail on activity type, reference product or unit before geography is considered.',
    tab: 'ecoinvent',
    title: '2. Build a defensible shortlist',
  },
  {
    owner: 'LCA practitioner / engineering / procurement',
    outputs: [
      'Chosen geography variant',
      'Proxy or exact match classified',
      'Key limitation identified',
    ],
    summary:
      'Resolve the exact geography based on where the activity happens and decide whether the final dataset is exact or a transparent proxy.',
    tab: 'ecoinvent',
    title: '3. Finalize geography and proxy logic',
  },
  {
    owner: 'LCA reviewer / sustainability lead',
    outputs: [
      'Assumption trail updated',
      'Version and system model confirmed',
      'Hotspot review completed',
    ],
    summary:
      'Review the selected dataset against version, system model and hotspot contribution so that the final link is technically and methodologically coherent.',
    tab: 'ecoinvent',
    title: '4. Review and lock the choice',
  },
];

const ECOINVENT_OFFICIAL_RESOURCES_EN: OfficialResource[] = [
  {
    label: 'ecoinvent Knowledge Base',
    summary:
      'Official documentation hub for methodology, releases, ecoQuery guides, FAQs and training materials.',
    url: 'https://support.ecoinvent.org/',
  },
  {
    label: 'Searching for datasets in ecoQuery',
    summary:
      'Official ecoQuery guidance on how to search, interpret dataset families and navigate the database efficiently.',
    url: 'https://support.ecoinvent.org/searching-for-datasets',
  },
  {
    label: 'Viewing datasets in ecoQuery',
    summary:
      'Official guide explaining how to inspect dataset metadata, exchanges and context once a candidate dataset has been found.',
    url: 'https://support.ecoinvent.org/viewing-datasets-in-ecoquery',
  },
  {
    label: 'Example datasets in ecoQuery',
    summary:
      'Official examples of dataset structures that help analysts understand how activities, products and markets are represented.',
    url: 'https://support.ecoinvent.org/example-datasets',
  },
  {
    label: 'Fundamentals of the ecoinvent database',
    summary:
      'Official training entry point covering database logic, modelling principles and background assumptions relevant to expert use.',
    url: 'https://support.ecoinvent.org/migration/e-learning-fundamentals-database',
  },
  {
    label: 'ecoinvent version 3.12',
    summary:
      'Official release page confirming that ecoinvent v3.12 was released on November 5, 2025 and summarizing major sector updates.',
    url: 'https://support.ecoinvent.org/ecoinvent-version-3.12',
  },
  {
    label: 'ecoinvent v3.12 webinar Q&A',
    summary:
      'Official answers from ecoinvent on version 3.12 topics, including cut-off logic, recycling treatment and common interpretation questions.',
    url: 'https://support.ecoinvent.org/hubfs/Comms/2025%20v3.12%20Release/ecoinvent%20Introduction%20to%20version%203.12%20Webinar%20Q%26A.pdf',
  },
  {
    label: 'Licensing and access',
    summary:
      'Official licensing guidance explaining access models and what is required when ecoinvent is made available through software tools.',
    url: 'https://support.ecoinvent.org/licensing',
  },
];

const ECOINVENT_FAQS_PL: FaqItem[] = [
  {
    question:
      'Kiedy analityk powinien wybrać zbiór danych typu market zamiast zbioru danych typu production?',
    answer:
      'Zbiór danych typu production wybieraj wtedy, gdy modelujesz sposób wytworzenia produktu lub półproduktu przez konkretny proces. Zbiór danych typu market wybieraj wtedy, gdy potrzebujesz średniego miksu dostaw dostępnego dla nabywcy w danej geografii, obejmującego produkcję krajową i import zgodnie z logiką ecoinvent. Typ market zwykle lepiej pasuje do kupowanych wejść, gdy nie znasz dokładnej ścieżki producenta. Typ production zwykle lepiej pasuje wtedy, gdy znasz trasę wytwarzania, technologię i geografię i chcesz zamodelować ją wprost.',
    tab: 'ecoinvent',
  },
  {
    question: 'Jak wybierać geografię między krajem, regionem, RoW i GLO?',
    answer:
      'Zacznij od geografii, która najlepiej odpowiada miejscu rzeczywistego wykonania aktywności. Preferuj krajowe zbiory danych, gdy łańcuch dostaw jest powiązany z konkretnym krajem i ma to istotny wpływ na wynik. Używaj regionalnych zbiorów danych, takich jak RER, gdy baza dostaw jest faktycznie regionalna albo gdy nie istnieje wiarygodny krajowy zbiór danych. RoW stosuj tylko wtedy, gdy aktywność rzeczywiście reprezentuje niesprecyzowany miks rest-of-world, a GLO tylko wtedy, gdy intencją jest model globalnego rynku lub globalnie uśrednionej podaży. Global nie jest automatycznie bezpieczniejszy, bo może rozmyć istotny sygnał krajowy albo regionalny.',
    tab: 'ecoinvent',
  },
  {
    question:
      'Dlaczego platforma Envirly LCA i ecoQuery mogą pokazywać różne wyniki dla pozornie tej samej aktywności?',
    answer:
      'Najpierw sprawdź spójność wersji ecoinvent, modelu systemowego i wybranej geografii. W platformie Envirly LCA wersja produktu jest ustalana przy tworzeniu produktu, a ecoinvent jest dostępny z poziomu platformy z linkiem do ecoQuery w modelu cut-off. Jeśli użytkownik porówna to z inną wersją, APOS, consequential albo aktywnością z innej geografii, różnice są oczekiwane. Wyniki mogą się też różnić wtedy, gdy platforma używa grupy aktywności, a użytkownik później wybierze inny wariant geograficzny niż ten sprawdzony w ecoQuery.',
    tab: 'ecoinvent',
  },
  {
    question: 'Co zrobić, gdy nie ma dokładnego odpowiednika w ecoinvent?',
    answer:
      'Użyj udokumentowanego przybliżenia zamiast pozornie podobnego, ale metodologicznie słabego dopasowania. Przybliżenie powinno możliwie dobrze zachowywać główną funkcję, postać fizyczną, logikę jednostki, poziom technologii i geografię. Uzasadnienie wyboru zapisz w założeniach albo komentarzach, tak aby weryfikujący rozumiał, dlaczego zbiór danych został wybrany, co reprezentuje zastępczo i gdzie w przyszłości potrzebne będą dane pierwotne, aby poprawić model.',
    tab: 'ecoinvent',
  },
  {
    question:
      'Jak interpretować oznaczenia RoW, RER, market group i treatment?',
    answer:
      'Te oznaczenia są sygnałami modelowymi, a nie tylko elementem nazwy. RoW oznacza rest of world i należy je czytać jako geografię rezydualną, a nie domyślną opcję ogólną. RER reprezentuje Europę w logice geograficznej ecoinvent i może istotnie różnić się od zbioru danych jednego kraju. Zbiory danych typu market group agregują dostawy szerzej niż pojedynczy market i mogą różnić się składem oraz interpretacją. Zbiory danych typu treatment modelują zagospodarowanie odpadów albo operacje końca życia i nie powinny być używane jako zwykłe przybliżenia produkcyjne dla materiałów lub komponentów.',
    tab: 'ecoinvent',
  },
  {
    question:
      'Kiedy lepiej użyć własnego współczynnika w platformie Envirly LCA zamiast zbioru danych ecoinvent?',
    answer:
      'Własny współczynnik stosuj wtedy, gdy istotny proces jest specyficzny dla firmy i dostępne przybliżenie z ecoinvent ukrywałoby ważną różnicę technologiczną, gdy dostawca dostarcza audytowalne dane pierwotne o wpływie zgodne metodologicznie ze studium albo gdy dla modelowanego obiektu nie istnieje wiarygodne przybliżenie w ecoinvent. Własny współczynnik nadal trzeba opisać źródłem, zakresem, jednostką i ograniczeniami jakości, bo wartość bez ścieżki pochodzenia jest słabsza niż dobrze uzasadniony wtórny zbiór danych.',
    tab: 'ecoinvent',
  },
];

const ECOINVENT_GUIDE_SECTIONS_PL: GuideSection[] = [
  {
    badge: 'Praca z ecoinvent',
    description:
      'Ta sekcja wyjaśnia, jak platforma Envirly LCA organizuje dostęp do ecoinvent i wyszukiwanie, dlaczego krok wyboru grupy aktywności ma znaczenie oraz jak przejść od pomysłu na produkt do obronionego wyboru zbioru danych.',
    tab: 'ecoinvent',
    title: 'Jak działa wybór ecoinvent w platformie Envirly LCA',
    entries: [
      {
        title:
          'Zacznij od obiektu modelowania, a nie od pierwszego słowa kluczowego',
        description:
          'Zanim zaczniesz szukać, zdefiniuj, co obiekt faktycznie reprezentuje w modelu: kupowany strumień wejściowy, proces produkcyjny, odcinek transportu, ścieżkę treatment czy uśrednioną podaż rynkową.',
        bullets: [
          'Zapisz funkcję obiektu w jednym zdaniu, zanim otworzysz wyszukiwarkę.',
          'Określ, czy obiekt jest materiałem, procesem, usługą, transportem czy operacją odpadową.',
          'Ustal oczekiwany przepływ referencyjny i jednostkę, aby szybko odrzucać wyniki, które do nich nie pasują.',
          'Jeżeli obiekt jest częścią ścieżki specyficznej dla produktu, zapisz, jakie dane pierwotne są już znane, zanim oprzesz się na danych wtórnych.',
        ],
      },
      {
        title: 'Świadomie korzystaj z kroku wyboru grupy aktywności',
        description:
          'W platformie Envirly LCA użytkownik najpierw wybiera grupę aktywności, która łączy jedną nazwę aktywności i jeden produkt referencyjny przez wiele geografii. Dopiero później wybiera dokładny wariant geograficzny.',
        bullets: [
          'Ten układ nie pozwala traktować geografii jako dodatku i odzwierciedla to, jak rodziny zbiorów danych są zorganizowane w ecoinvent.',
          'Pierwszy wybór powinien odpowiadać na pytanie, jaki proces albo rynek jest istotny, a drugi na pytanie, gdzie ta aktywność zachodzi.',
          'Liczba dostępnych geografii jest sygnałem, że istnieje wiele wariantów regionalnych i trzeba podjąć świadomą decyzję.',
          'Nie zakładaj, że pierwszy wynik jest poprawnym wariantem geograficznym tylko dlatego, że nazwa aktywności brzmi dokładnie.',
        ],
      },
      {
        title: 'Korzystaj z pamięci organizacyjnej, ale ją weryfikuj',
        description:
          'Platforma Envirly LCA pokazuje ostatnio albo wcześniej używane aktywności, bo powtarzalne kategorie często zbieżają się do niewielkiej grupy akceptowalnych przybliżeń. To ma przyspieszać weryfikację, a nie ją zastępować.',
        bullets: [
          'Wcześniej używane aktywności są przydatne, gdy ten sam podobszar jest wielokrotnie modelowany w różnych produktach.',
          'Ponowne użycie zbioru danych jest uzasadnione tylko wtedy, gdy funkcja, technologia, jednostka i geografia nadal są porównywalne.',
          'Jeśli kontekst produktu się zmienił, potraktuj szybki wybór jako krótką listę i ponownie sprawdź rodzinę zbiorów danych.',
          'Komentarze powinny jasno wskazywać, czy ponownie użyty zbiór danych jest dopasowaniem dokładnym, przybliżeniem czy tymczasowym zastępstwem.',
        ],
      },
      {
        title:
          'Traktuj ecoQuery jako narzędzie weryfikacji, a nie osobny świat modelowania',
        description:
          'Platforma Envirly LCA daje dostęp do ecoinvent z przepływu pracy produktu i linkuje do ecoQuery w modelu cut-off. Celem jest sprawdzenie metadanych, porównanie sąsiednich geografii i zrozumienie zbioru danych, a nie cicha zmiana modelu systemowego w trakcie studium.',
        bullets: [
          'Sprawdź, czy strona ecoQuery używa tej samej wersji ecoinvent co produkt w platformie Envirly LCA.',
          'Używaj widoku ecoQuery do przeglądu rodziny aktywności, metadanych i sąsiednich wariantów geograficznych.',
          'Porównując wyniki, trzymaj się tego samego modelu systemowego i wersji, inaczej porównanie traci ważność metodologiczną.',
          'Każdy wniosek wyciągnięty z ecoQuery zapisuj z powrotem w założeniach albo komentarzach w platformie Envirly LCA, aby logika pozostała przy rekordzie produktu.',
        ],
      },
    ],
  },
  {
    badge: 'Dobór zbioru danych',
    description:
      'Większość błędów wynika z tego, że zbiór danych brzmi dobrze językowo, ale jest funkcjonalnie błędny. Ta sekcja skupia się na kontrolach, które ekspert powinien wykonać przed akceptacją zbioru danych.',
    tab: 'ecoinvent',
    title: 'Jak wybrać właściwą rodzinę zbiorów danych',
    entries: [
      {
        title: 'Rozróżniaj market, market group, production i treatment',
        description:
          'Typ aktywności jest wyborem metodologicznym. Podobne nazwy mogą oznaczać inną logikę systemu, a więc inne inwentarze i wyniki wpływu.',
        bullets: [
          'Używaj zbiorów danych typu production, gdy modelujesz konkretną ścieżkę wytwarzania albo proces transformacji.',
          'Używaj zbiorów danych typu market, gdy obiekt reprezentuje zakup z miksu dostaw zależnego od geografii, a nie od jednego znanego producenta.',
          'Zbiory danych typu market group traktuj jako szerszą agregację dostaw, która może różnić się składem i interpretacją od bezpośredniego zbioru danych market.',
          'Zbiory danych typu treatment stosuj wyłącznie do gospodarki odpadowej albo operacji końca życia i nigdy jako skrót dla zwykłej produkcji materiału.',
        ],
      },
      {
        title: 'Sprawdź produkt referencyjny, zanim spojrzysz na wynik wpływu',
        description:
          'Produkt referencyjny definiuje, co dostarcza jedna jednostka zbioru danych. Jeśli ta logika nie pasuje do modelowanego obiektu, zbiór danych jest błędny nawet wtedy, gdy liczba wygląda wiarygodnie.',
        bullets: [
          'Sprawdź nazwę produktu referencyjnego i upewnij się, że odpowiada temu samemu rezultatowi co obiekt w platformie Envirly LCA.',
          'Sprawdź jednostkę produktu referencyjnego i upewnij się, że przeliczenie ilości jest jawne i możliwe do zweryfikowania.',
          'Sprawdź ilość produktu referencyjnego tam, gdzie to istotne, szczególnie dla usług albo rezultatów procesowych, które nie są wyrażone jako jedna prosta jednostka fizyczna.',
          'Odrzuć zbiór danych wcześnie, jeśli reprezentuje inną postać fizyczną, koncentrację, stan przetworzenia albo granicę usługi niż obiekt w zakresie studium.',
        ],
      },
      {
        title: 'Rozwiązuj geografię hierarchicznie',
        description:
          'Wybieraj geografię, idąc od najbardziej specyficznej do najmniej specyficznej zgodnie z fizyczną ścieżką dostaw, a nie wybierając największy dostępny region.',
        bullets: [
          'Zacznij od kraju produkcji albo znanej lokalizacji dostawcy, jeśli ma to istotne znaczenie dla wyniku.',
          'Jeżeli nie ma krajowego zbioru danych, przejdź do najbliższego obronionego regionalnego zbioru danych i jawnie opisz to ograniczenie.',
          'RoW albo GLO stosuj tylko wtedy, gdy rzeczywiście odzwierciedlają zamierzoną logikę podaży albo gdy nie ma mocniejszej geografii.',
          'Szczególnie uważaj przy energii elektrycznej, transporcie i odpadach, bo geografia często istotnie zmienia wyniki w tych obszarach.',
        ],
      },
      {
        title:
          'Wybieraj przybliżenia transparentnie, gdy nie ma dokładnego dopasowania',
        description:
          'Dobór przybliżeń to normalna praca ekspercka w LCA, ale musi być jawny. Ukryte przybliżenie to problem zarządczy nawet wtedy, gdy technicznie jest rozsądne.',
        bullets: [
          'Budując uzasadnienie przybliżenia, priorytetyzuj kolejno funkcję, technologię procesu, skład materiałowy i geografię.',
          'Nie wybieraj zbioru danych tylko dlatego, że nazwa handlowa brzmi podobnie do nazwy produktu.',
          'Opisz, co przybliżenie pomija albo przeszacowuje, aby weryfikujący mógł ocenić prawdopodobny kierunek odchylenia.',
          'Zapisz, w którym miejscu dane dostawcy albo zakładu powinny zastąpić przybliżenie w kolejnej iteracji modelu.',
        ],
      },
    ],
  },
  {
    badge: 'Spójność i weryfikacja',
    description:
      'Wybór zbioru danych to tylko część dobrej pracy z ecoinvent. Ekspert potrzebuje także kontroli wersji, dyscypliny modelu systemowego i rzetelnej weryfikacji jakości danych.',
    tab: 'ecoinvent',
    title: 'Kontrola wersji, dyscyplina modelu systemowego i jakość danych',
    entries: [
      {
        title: 'Utrzymuj stabilną wersję na poziomie produktu',
        description:
          'Platforma Envirly LCA wiąże produkt z jedną wersją ecoinvent, bo zmiana danych tła w trakcie projektu utrudnia obronę analiz trendów i weryfikację.',
        bullets: [
          'Nie porównuj dwóch iteracji produktu tak, jakby były równoważne, jeśli po drodze zmieniła się wersja bazy tła.',
          'Gdy pojawia się nowa wersja ecoinvent, traktuj migrację jako kontrolowaną aktualizację metodologiczną, a nie ciche odświeżenie.',
          'Po duplikacji albo kopiowaniu produktu sprawdzaj dostępność aktywności, bo wcześniej podlinkowana aktywność może nie mieć punktów danych w nowej wersji.',
          'Porównując się do studiów zewnętrznych, zawsze sprawdzaj zarówno wersję, jak i model systemowy, zanim wyciągniesz wnioski z różnic.',
        ],
      },
      {
        title: 'Nie mieszaj modeli systemowych przy walidacji wyników',
        description:
          'Eksperci często porównują wyniki między narzędziami, ale porównanie ma sens tylko wtedy, gdy używany jest ten sam model systemowy. Platforma Envirly LCA obecnie linkuje do ecoQuery w modelu cut-off.',
        bullets: [
          'Cut-off, APOS, consequential oraz widoki zorientowane na EN15804 nie powinny być traktowane jako wymienne interpretacje tego samego wyniku.',
          'Jeśli wynik różni się od innego narzędzia, najpierw sprawdź, czy tamten wynik nie został wygenerowany w innym modelu systemowym.',
          'Wybrany model systemowy zapisuj w założeniach studium i utrzymuj porównania w tych samych ramach metodologicznych.',
          'Uważaj szczególnie na recykling i twierdzenia dotyczące końca życia, bo wybór modelu systemowego może istotnie zmienić logikę alokacji obciążeń.',
        ],
      },
      {
        title: 'Oceniaj jakość danych szerzej niż tylko po liczbie wpływu',
        description:
          'Datasety ecoinvent mają kontekst metodologiczny, założenia reprezentatywności i logikę niepewności. Ekspert powinien oceniać adekwatność, a nie tylko ranking.',
        bullets: [
          'Oceniaj reprezentatywność technologiczną, geograficzną i czasową względem modelowanego obiektu.',
          'Informacje o pedigree i niepewności traktuj jako sygnały głębokości weryfikacji, a nie substytut osądu inżynierskiego.',
          'Weryfikację jakości zaczynaj od hotspotów i obiektów o największym wkładzie, bo tam wybór zbioru danych ma największe znaczenie.',
          'Używaj analizy wrażliwości albo scenariuszy wtedy, gdy istnieje kilka wiarygodnych przybliżeń i wybór może istotnie przesunąć wynik.',
        ],
      },
      {
        title: 'Wiedz, kiedy eskalować do danych własnych',
        description:
          'Wtórne zbiory danych są efektywne, ale niektóre pytania modelowe wymagają danych pierwotnych albo danych własnych, aby wynik pozostał wiarygodny.',
        bullets: [
          'Eskaluj, gdy proces firmowy istotnie różni się od ogólnego przybliżenia pod względem miksu energii, uzysku, poziomu odpadu, udziału recyklatu albo technologii.',
          'Eskaluj, gdy dostawca dostarcza audytowalne dane specyficzne dla produktu, bardziej reprezentatywne niż ogólna ścieżka tła.',
          'Eskaluj, gdy wymagania regulacyjne albo klienta oczekują mocniejszej ścieżki dowodowej, niż może dać ogólne przybliżenie.',
          'Jeśli używasz własnego współczynnika, opis źródła, jednostki, zakresu i granic powinien być równie rygorystyczny jak przy wyborze dowolnego zbioru danych.',
        ],
      },
    ],
  },
];

const ECOINVENT_KNOWLEDGE_CARDS_PL: KnowledgeCard[] = [
  {
    bullets: [
      'Platforma Envirly LCA celowo oddziela wybór grupy aktywności od wyboru geografii.',
      'Pierwsze pytanie brzmi, jaka rodzina procesów jest istotna, a drugie, gdzie ta aktywność zachodzi.',
      'To ogranicza fałszywą precyzję wynikającą z wyboru geografii przed zrozumieniem rodziny zbiorów danych.',
    ],
    status: 'Ecoinvent w platformie Envirly LCA',
    summary:
      'Dobry proces wyszukiwania w platformie Envirly LCA odzwierciedla strukturę ecoinvent: najpierw definiujesz rodzinę aktywności, a dopiero potem wybierasz dokładny wariant geograficzny.',
    tab: 'ecoinvent',
    title:
      'Dlaczego przepływ pracy ecoinvent w platformie Envirly LCA składa się z dwóch decyzji, a nie jednej',
  },
  {
    bullets: [
      'Przekonująca nazwa to za mało; muszą pasować także produkt referencyjny, jednostka i typ aktywności.',
      'Nazwy market, market group, production i treatment mogą wyglądać podobnie, ale oznaczają inną logikę modelowania.',
      'Właściwy zbiór danych to ten, który zachowuje sens funkcjonalny, a nie ten z najładniejszym opisem.',
    ],
    status: 'Weryfikacja zbioru danych',
    summary:
      'Najczęstszy błąd ekspercki polega na akceptacji zbioru danych dlatego, że jego nazwa brzmi dobrze, zanim ktoś sprawdzi, co ten zbiór danych faktycznie dostarcza.',
    tab: 'ecoinvent',
    title: 'Dlaczego nazwy zbiorów danych są tylko wskazówką, a nie dowodem',
  },
  {
    bullets: [
      'Przed porównywaniem wyników między narzędziami sprawdzaj wersję ecoinvent, model systemowy i geografię.',
      'Platforma Envirly LCA obecnie linkuje do ecoQuery w modelu cut-off, więc porównania do APOS albo consequential nie są metodologicznie porównywalne.',
      'ecoQuery służy do sprawdzania zbiorów danych, a nie do cichej zmiany metodologii podczas weryfikacji.',
    ],
    status: 'Walidacja',
    summary:
      'Większość pozornych rozbieżności między platformą Envirly LCA a zewnętrzną weryfikacją wynika z niedopasowania metodologii, a nie z błędów oprogramowania.',
    tab: 'ecoinvent',
    title:
      'Jak weryfikować wyniki platformy Envirly LCA względem ecoQuery bez fałszywych rozbieżności',
  },
];

const ECOINVENT_WORKFLOW_STEPS_PL: WorkflowStep[] = [
  {
    owner: 'praktyk LCA / właściciel produktu',
    outputs: [
      'Zdefiniowana funkcja obiektu',
      'Określona oczekiwana jednostka',
      'Wypisane znane ograniczenia geograficzne',
    ],
    summary:
      'Zanim zaczniesz szukać, opisz obiekt modelowania tak, aby wybór zbioru danych wynikał z funkcji, jednostki i logiki dostaw, a nie tylko z brzmienia nazwy.',
    tab: 'ecoinvent',
    title: '1. Zdefiniuj, co reprezentuje obiekt',
  },
  {
    owner: 'praktyk LCA',
    outputs: [
      'Krótka lista grup aktywności',
      'Mentalnie albo w notatkach odrzucone fałszywe dopasowania',
      'Zidentyfikowany właściwy typ aktywności',
    ],
    summary:
      'Wejdź do ecoinvent z poziomu platformy Envirly LCA, przeszukaj grupy aktywności i odrzuć opcje, które nie pasują typem aktywności, produktem referencyjnym albo jednostką, zanim przejdziesz do geografii.',
    tab: 'ecoinvent',
    title: '2. Zbuduj obronioną krótką listę',
  },
  {
    owner: 'praktyk LCA / inżynieria / zakupy',
    outputs: [
      'Wybrany wariant geograficzny',
      'Określone, czy to przybliżenie czy dokładne dopasowanie',
      'Zidentyfikowane główne ograniczenie',
    ],
    summary:
      'Doprecyzuj dokładną geografię na podstawie miejsca wykonywania aktywności i zdecyduj, czy końcowy zbiór danych jest dopasowaniem dokładnym czy transparentnym przybliżeniem.',
    tab: 'ecoinvent',
    title: '3. Domknij geografię i logikę przybliżeń',
  },
  {
    owner: 'weryfikujący LCA / lider ds. zrównoważonego rozwoju',
    outputs: [
      'Uzupełniona ścieżka założeń',
      'Potwierdzona wersja i model systemowy',
      'Zakończona weryfikacja hotspotów',
    ],
    summary:
      'Sprawdź wybrany zbiór danych pod kątem wersji, modelu systemowego i wkładu do hotspotów, aby finalne powiązanie było spójne technicznie i metodologicznie.',
    tab: 'ecoinvent',
    title: '4. Zweryfikuj i zamknij wybór',
  },
];

const ECOINVENT_OFFICIAL_RESOURCES_PL: OfficialResource[] = [
  {
    label: 'Baza wiedzy ecoinvent',
    summary:
      'Oficjalne centrum dokumentacji dotyczące metodologii, wydań, przewodników ecoQuery, FAQ i materiałów szkoleniowych.',
    url: 'https://support.ecoinvent.org/',
  },
  {
    label: 'Wyszukiwanie zbiorów danych w ecoQuery',
    summary:
      'Oficjalny przewodnik ecoQuery pokazujący, jak wyszukiwać, interpretować rodziny zbiorów danych i sprawnie poruszać się po bazie.',
    url: 'https://support.ecoinvent.org/searching-for-datasets',
  },
  {
    label: 'Przeglądanie zbiorów danych w ecoQuery',
    summary:
      'Oficjalny przewodnik wyjaśniający, jak sprawdzać metadane zbioru danych, przepływy i kontekst po znalezieniu kandydata.',
    url: 'https://support.ecoinvent.org/viewing-datasets-in-ecoquery',
  },
  {
    label: 'Przykładowe zbiory danych w ecoQuery',
    summary:
      'Oficjalne przykłady struktur zbiorów danych pomagające zrozumieć, jak w bazie reprezentowane są aktywności, produkty i rynki.',
    url: 'https://support.ecoinvent.org/example-datasets',
  },
  {
    label: 'Podstawy bazy ecoinvent',
    summary:
      'Oficjalny punkt startowy szkolenia obejmujący logikę bazy, zasady modelowania i założenia tła istotne w pracy eksperckiej.',
    url: 'https://support.ecoinvent.org/migration/e-learning-fundamentals-database',
  },
  {
    label: 'Wersja ecoinvent 3.12',
    summary:
      'Oficjalna strona wydania potwierdzająca, że ecoinvent v3.12 opublikowano 5 listopada 2025 roku, wraz z podsumowaniem najważniejszych aktualizacji sektorowych.',
    url: 'https://support.ecoinvent.org/ecoinvent-version-3.12',
  },
  {
    label: 'Q&A webinaru ecoinvent v3.12',
    summary:
      'Oficjalne odpowiedzi ecoinvent dotyczące wersji 3.12, w tym logiki cut-off, procesów recyklingu i najczęstszych pytań interpretacyjnych.',
    url: 'https://support.ecoinvent.org/hubfs/Comms/2025%20v3.12%20Release/ecoinvent%20Introduction%20to%20version%203.12%20Webinar%20Q%26A.pdf',
  },
  {
    label: 'Licencjonowanie i dostęp',
    summary:
      'Oficjalne wyjaśnienie modeli licencyjnych oraz wymagań przy udostępnianiu ecoinvent przez narzędzia programowe.',
    url: 'https://support.ecoinvent.org/licensing',
  },
];

const HELP_CENTRE_CONTENT: Record<'en' | 'pl' | 'sv', HelpCentreContent> = {
  en: {
    badge: 'Help Centre',
    breadcrumbs: [
      { name: 'Product list', url: '/lca/products' },
      { name: 'Help Centre', url: '/lca/help-centre' },
    ],
    checklistItems: [
      { id: 'owner', label: 'Assign one product data owner and reviewers' },
      { id: 'scope', label: 'Agree the LCA scope, functional unit and method' },
      {
        id: 'primary',
        label: 'Collect primary data from plants and suppliers',
      },
      {
        id: 'secondary',
        label: 'Map gaps to secondary datasets and assumptions',
      },
      {
        id: 'evidence',
        label: 'Attach evidence, certificates and source files',
      },
      {
        id: 'passport',
        label: 'Prepare product data for DPP and sharing flows',
      },
    ],
    checklistLabel: 'Operational checklist',
    checklistProgressLabel: 'complete',
    checklistTitle: 'What a deployment-ready product record should contain',
    disclaimer:
      'This hub is meant for day-to-day execution and onboarding. It supports compliance work but does not replace formal legal advice.',
    faqTitle: 'Frequently asked questions',
    faqs: [
      {
        question: 'What should users complete first in the platform?',
        answer:
          'Start with the product record, identifiers, producer context and intended use. Then define LCA scope, collect material and process data, and only then move to passport or reporting outputs.',
        tab: 'platform',
      },
      {
        question: 'Why is ecoinvent chosen at product creation?',
        answer:
          'Because database version is part of the modelling logic. Keeping the version stable for a product avoids silent changes in background datasets between iterations.',
        tab: 'lca',
      },
      {
        question: 'Is GTIN mandatory for every product?',
        answer:
          'Not always, but it is a strong identifier whenever the organization uses GS1. It becomes especially valuable for digital passport, product master data, packaging and downstream interoperability.',
        tab: 'platform',
      },
      {
        question: 'How should we collect data across teams?',
        answer:
          'Use one owner per product, define a data request template by life-cycle stage, ask plants and procurement for primary data, and keep assumptions explicit when exact values are unavailable.',
        tab: 'platform',
      },
      {
        question: 'What is the difference between CPC, NACE and GPC?',
        answer:
          'CPC classifies products, NACE classifies economic activity, and GPC is a GS1 product classification used in commerce data exchange. They solve different mapping problems and can coexist on the same product.',
        tab: 'platform',
      },
      {
        question: 'What makes an LCA credible enough for external use?',
        answer:
          'A clear goal and scope, functional unit, transparent boundaries, documented data sources, explicit assumptions, coherent dataset choices and evidence that can be reviewed later.',
        tab: 'lca',
      },
      {
        question: 'Does the passport builder replace the LCA workflow?',
        answer:
          'No. The builder is an output and communication layer. The underlying product and LCA data still need to be structured correctly in the product record first.',
        tab: 'dpp',
      },
      {
        question: 'Can one product record support multiple regulations?',
        answer:
          'Yes, if master data, evidence, environmental data and identifiers are maintained in one disciplined operating model. That is the reason this help centre links platform work, LCA, DPP and regulation topics.',
        tab: 'regulations',
      },
      ...ECOINVENT_FAQS_EN,
    ],
    flowchartAreaLabel: 'Area of impact',
    flowchartBoundaryLabel: 'cradle-to-grave example',
    flowchartCanvasHint:
      'Example screen: read stages from left to right and place objects under the life-cycle phase where they actually happen.',
    flowchartCanvasTitle: 'Example flowchart screen',
    flowchartDescription:
      'The flowchart is where the LCA stops being abstract. It helps users decide which life-cycle phases are in scope, which processes belong to each phase and where materials, transport, packaging or end-of-life routes should be placed.',
    flowchartExampleStages: [
      {
        title: 'Raw materials extraction & processing',
        items: ['Aluminium sheet', 'Recycled polymer granulate'],
      },
      {
        title: 'Transport of raw materials',
        items: ['Truck from supplier', 'Inbound pallet transport'],
      },
      {
        title: 'Production & packaging',
        items: ['Cutting and forming', 'Assembly and retail box'],
      },
      {
        title: 'Distribution & storage',
        items: ['Distribution centre storage', 'Outbound transport'],
      },
      {
        title: 'Installation',
        items: ['On-site installation', 'Installer travel'],
      },
      {
        title: 'Use stage',
        items: ['Electricity in use', 'Maintenance / spare part'],
      },
      {
        title: 'End of life',
        items: ['Disassembly', 'Recycling and residual waste'],
      },
    ],
    flowchartExampleTitle: 'How to think about building the flowchart',
    flowchartFilterByStageLabel: 'Filter by stage',
    flowchartGroupCradleLabel: 'CRADLE',
    flowchartGroupGraveLabel: 'GRAVE',
    flowchartLegendLabel: 'What to place on the diagram',
    flowchartLegendItems: [
      'Create one box for each material, transport leg, process or end-of-life route that materially affects the result.',
      'Place the box in the stage where the activity physically happens, not where the invoice sits.',
      'Keep process names short and specific so another analyst can understand the model without opening a spreadsheet.',
    ],
    flowchartNoObjectsLabel: 'No objects added yet',
    flowchartStepLabel: 'Build logic',
    flowchartSteps: [
      {
        title: '1. Choose the system boundary first',
        description:
          'Decide whether the product should be modelled cradle-to-gate, cradle-to-grave or with another boundary before adding detailed objects.',
        bullets: [
          'Boundary determines which columns should appear in the flowchart.',
          'Use the goal of the study and reporting requirement to justify the choice.',
          'Write down exclusions early so they do not disappear into later assumptions.',
        ],
      },
      {
        title: '2. Add the main processes before detailed inputs',
        description:
          'Start from the high-level value chain and create the main process structure first. After that, attach materials, transport and energy-consuming activities.',
        bullets: [
          'Think in processes such as casting, assembly, warehousing or recycling.',
          'Do not begin with dozens of small nodes before the stage logic is clear.',
          'Use one main box per meaningful transformation or route.',
        ],
      },
      {
        title: '3. Map data and datasets to the right stage',
        description:
          'Every object should be backed either by primary data, a supplier declaration or a secondary dataset such as ecoinvent.',
        bullets: [
          'Materials usually sit in raw materials or production depending on what is being represented.',
          'Transport legs should be separate when geography or mode matters.',
          'Use comments or attached flowchart files when the visual model needs extra context.',
        ],
      },
      {
        title: '4. Keep the diagram reviewable',
        description:
          'A useful flowchart is not the most detailed one. It is the one another person can review and connect back to assumptions, product fields and source files.',
        bullets: [
          'Merge trivial nodes when they do not change interpretation.',
          'Name boxes so they match the evidence package and uploaded documentation.',
          'Review whether the diagram tells a consistent story from materials to end of life.',
        ],
      },
    ],
    flowchartSummaryLabel: 'Summary',
    flowchartSummaryValue: '42.3',
    flowchartSummaryUnit: 'kg CO2e',
    flowchartTabs: {
      analytics: 'Analytics',
      flowchart: 'Flowchart',
      impact: 'Impact',
    },
    flowchartTitle: 'How to build the LCA flowchart',
    guideDescription:
      'These sections explain what each field group is for, how to fill it in, and what good evidence looks like in practice.',
    guideEmptyDescription:
      'Try another keyword or switch to another workstream to see more detailed guidance.',
    guideEmptyTitle: 'No matching guidance sections',
    guideSections: [
      {
        badge: 'Fields',
        description:
          'The opening product record is where master data quality is decided. These fields drive categorisation, dataset selection and downstream passport consistency.',
        tab: 'platform',
        title: 'Product record and business context',
        entries: [
          {
            title: 'Product name and description',
            description:
              'Write the commercial or internal product name and a description that helps a reviewer understand function, composition and intended market use.',
            bullets: [
              'Use the same naming logic as ERP or PLM where possible.',
              'Describe what the product does, not only what marketing calls it.',
              'Avoid vague text such as "high-quality product" without operational meaning.',
            ],
          },
          {
            title: 'Ecoinvent-version',
            description:
              'Select the background database version at product creation and keep it stable across modelling updates unless there is a deliberate methodological change.',
            bullets: [
              'A version change can alter background datasets and results.',
              'Use the newest approved version only when the project rules allow it.',
              'Document the reason when switching versions between studies.',
            ],
          },
          {
            title: 'Product category, industry and regulatory frameworks',
            description:
              'CPC helps classify the product, NACE maps the main industry context, and regulatory framework tags show which reporting or compliance logic should be considered from the start.',
            bullets: [
              'Choose CPC for the product itself, not for the whole company.',
              'Choose NACE according to the activity that creates the value chain context.',
              'Use framework tags to signal whether EPD, DPP or product carbon footprint outputs are expected.',
            ],
          },
          {
            title: 'GTIN / product code and producer details',
            description:
              'Product code fields anchor the record to product master data. Producer name, address and website should identify the legal or operational entity behind the product.',
            bullets: [
              'If GTIN exists, keep only the numeric code and validate it before import.',
              'Use producer details that can later appear on a passport or compliance-facing document.',
              'Keep naming aligned with labels, technical files or other source systems.',
            ],
          },
        ],
      },
      {
        badge: 'Fields',
        description:
          'This section helps users upload evidence, explain intended use and complete business information that later appears in passport or audit contexts.',
        tab: 'platform',
        title: 'Additional information and evidence fields',
        entries: [
          {
            title: 'Development flowcharts and technical documentation',
            description:
              'Use these uploads for process diagrams, assembly maps, technical files, design drawings or engineering notes that help explain how the product is made.',
            bullets: [
              'Upload evidence that supports manufacturing logic or bill-of-material interpretation.',
              'Prefer files with clear versioning and source ownership.',
              'Describe what the file proves in the accompanying text field.',
            ],
          },
          {
            title: 'Intended use, product type and additional information',
            description:
              'Explain how the product is actually used, whether it is a physical product or service, and any context that a modeller or reviewer should know before building the LCA.',
            bullets: [
              'Use intended use to define realistic assumptions for use phase and lifetime.',
              'Select product type carefully because labels and field expectations change for services.',
              'Store context here when it does not fit in structured fields but still matters for interpretation.',
            ],
          },
          {
            title: 'Production date, warranty, CE mark and imported GS1 data',
            description:
              'These fields support traceability and downstream digital product communication. When GTIN import is available, validate the code first and then review imported values before saving.',
            bullets: [
              'Use production date when batch or period traceability matters.',
              'Describe warranty and conditions in a way that is customer-facing and reviewable.',
              'Mark CE only when the product is actually CE-marked under applicable rules.',
            ],
          },
          {
            title: 'GPC and related identifiers',
            description:
              'If GTIN import populates GPC or related fields, check that the classification matches the actual product placed on the market.',
            bullets: [
              'GS1 data can accelerate setup but should still be reviewed by a product owner.',
              'Do not treat imported fields as automatically correct for every market variant.',
              'Escalate mismatches between GTIN, label data and technical documentation.',
            ],
          },
        ],
      },
      {
        badge: 'Fields',
        description:
          'Geography and supplier fields are essential for primary data collection, traceability and later explanations of upstream assumptions.',
        tab: 'platform',
        title: 'Producer, geography, suppliers and supporting records',
        entries: [
          {
            title: 'Producer, site and distribution fields',
            description:
              'Separate who is responsible for the product from where production happens and where the product is distributed.',
            bullets: [
              'Use producer fields for the entity behind the product.',
              'Use country of production and production address for manufacturing geography.',
              'Use distribution regions to show where the product is sold or supplied.',
            ],
          },
          {
            title: 'Short producer activity description',
            description:
              'This field should explain the core activity of the producer in one operational paragraph so reviewers understand the industrial context.',
            bullets: [
              'Mention the manufacturing role or service delivered.',
              'Use wording that helps procurement or auditors identify the operation quickly.',
              'Keep it factual and short rather than promotional.',
            ],
          },
          {
            title: 'Key suppliers',
            description:
              'List the suppliers that materially influence the product footprint, composition or evidence package.',
            bullets: [
              'Capture supplier name, address, supplied item and a contact person when possible.',
              'Prioritise suppliers linked to core materials, critical processes or compliance-sensitive inputs.',
              'Use the same supplier naming as in procurement systems to avoid duplicates.',
            ],
          },
          {
            title: 'Technical specifications and certificates',
            description:
              'Technical specifications should capture measurable product parameters, while certificates should capture controlled documents with status and validity periods.',
            bullets: [
              'Use one specification row per metric, for example capacity, voltage or load range.',
              'For certificates, include document type, scope, validity dates and whether the document is environmental.',
              'Attach the PDF whenever the file exists so evidence is not separated from the record.',
            ],
          },
        ],
      },
      {
        badge: 'Workflow',
        description:
          'Goals and disclosure settings define why the study exists, who will use it and what level of confidentiality or external communication is expected.',
        tab: 'platform',
        title: 'Goals, scope framing and disclosure logic',
        entries: [
          {
            title: 'Intended applications, reasons and target audience',
            description:
              'These fields explain why the LCA is being done and who needs the result. They anchor later methodological choices.',
            bullets: [
              'Use intended applications for decisions or deliverables such as internal ecodesign, customer request or regulatory preparation.',
              'Study reasons should explain the business trigger or risk.',
              'Target audience can include internal teams, customers, certifiers or regulators.',
            ],
          },
          {
            title: 'Commissioner and external experts',
            description:
              'Record who commissioned the study and whether external specialists contributed to modelling, review or verification.',
            bullets: [
              'Use commissioner name when the project was initiated by a specific customer or business unit.',
              'Switch on external experts only when they were actually involved.',
              'Name the expert organization or role so later reviewers know who supported the work.',
            ],
          },
          {
            title: 'Disclosure, confidentiality and assertions',
            description:
              'These switches help separate data that can be shared publicly from data that remains internal or subject to restrictions.',
            bullets: [
              'Be explicit about whether results or assertions will be disclosed externally.',
              'Use the description field to explain what can be shared and under what conditions.',
              'Keep confidential notes clear so a future passport or report does not expose restricted data by mistake.',
            ],
          },
          {
            title: 'Additional notes',
            description:
              'Use comments for study context, governance notes, review issues or caveats that matter for interpretation but do not belong in structured questions.',
            bullets: [
              'Document unresolved issues instead of leaving them implicit.',
              'Note planned updates when data is expected from suppliers later.',
              'Use comments to hand over context between internal teams.',
            ],
          },
        ],
      },
      {
        badge: 'LCA',
        description:
          'These sections explain the logic of an LCA and how the platform mirrors the standard workflow from scope definition to interpretation.',
        tab: 'lca',
        title: 'LCA basics and study architecture',
        entries: [
          {
            title: 'What LCA is',
            description:
              'Life Cycle Assessment is a structured method for quantifying environmental impacts across the product life cycle, not only carbon emissions.',
            bullets: [
              'It usually covers raw materials, manufacturing, transport, use and end of life.',
              'Results are only meaningful when the goal, scope and boundaries are transparent.',
              'The platform is designed to capture both structured data and the assumptions behind it.',
            ],
          },
          {
            title: 'The four LCA phases',
            description:
              'A complete study normally includes goal and scope definition, inventory analysis, impact assessment and interpretation.',
            bullets: [
              'Goal and scope explain why the study exists and what is included.',
              'Inventory gathers the flows of materials, energy, transport and waste.',
              'Impact assessment translates inventory flows into impact indicators.',
            ],
          },
          {
            title: 'Why functional unit matters',
            description:
              'The functional unit describes what service the product delivers and creates the basis for a fair model and fair comparisons.',
            bullets: [
              'Use a function-based statement, not only a simple product count.',
              'For services, describe the delivered service, duration or throughput.',
              'Check that suppliers and internal teams can map their data to the same reference.',
            ],
          },
          {
            title: 'What makes a study reviewable',
            description:
              'A study can be defended only when datasets, assumptions, sources and exclusions are visible to another expert.',
            bullets: [
              'Document why primary data was used or why a proxy dataset was chosen.',
              'Explain major assumptions and cut-off logic.',
              'Keep the evidence pack close to the model, not in separate email threads.',
            ],
          },
        ],
      },
      {
        badge: 'Fields',
        description:
          'Product characteristics provide the modelling reference for the declared product or service and support use-phase and end-of-life logic.',
        tab: 'lca',
        title: 'Product characteristics and material data',
        entries: [
          {
            title: 'Functional unit and data collection period',
            description:
              'These fields define what is being assessed and which time window the primary data represents.',
            bullets: [
              'Write the functional or service unit in a way that can be used consistently across calculations.',
              'Use data collection dates that reflect the real operating period of collected data.',
              'Do not mix multi-year averages and one-month snapshots without explanation.',
            ],
          },
          {
            title: 'Weight, dimensions and volume',
            description:
              'These physical characteristics support logistics assumptions, material checks, packaging logic and downstream passport content.',
            bullets: [
              'Use measured values whenever possible.',
              'Keep units consistent and realistic for the product type.',
              'If dimensions are placeholders, mark that clearly in assumptions or comments.',
            ],
          },
          {
            title: 'Material composition',
            description:
              'List basic and additional materials with percentage share and recycled share so the composition can be understood and reused in environmental modelling.',
            bullets: [
              'Start with the materials that dominate mass or impact.',
              'Use recycled share only when there is evidence or a justified assumption.',
              'Check whether percentages are directionally complete before sharing results externally.',
            ],
          },
          {
            title: 'Mass-balance discipline',
            description:
              'Material rows should together form a coherent picture of the product. Even when the total is not yet perfect, the model should show where the gaps are.',
            bullets: [
              'Review if main materials explain most of the product mass.',
              'Use additional rows for coatings, additives or minor components when relevant.',
              'Escalate large unexplained mass gaps before publishing a passport or report.',
            ],
          },
        ],
      },
      {
        badge: 'Method',
        description:
          'Assumption fields document how the product is made, what reference lifetime is used and where the study deliberately excludes low-significance flows.',
        tab: 'lca',
        title: 'Processes, assumptions and service life',
        entries: [
          {
            title: 'Production process description',
            description:
              'Describe the core process steps that materially influence footprint, such as forming, assembly, finishing, testing or packaging.',
            bullets: [
              'Focus on the operations that matter for impact, not every micro-step.',
              'Mention the site or technology when it changes energy or yield assumptions.',
              'Keep the description understandable for someone outside the plant team.',
            ],
          },
          {
            title: 'Assumptions and cut-off rules',
            description:
              'These fields explain how missing data, low-significance flows and estimation logic were treated in the model.',
            bullets: [
              'Document where primary data was unavailable and what proxy was used instead.',
              'Use cut-off rules consistently so exclusions can be reviewed later.',
              'Explain any assumptions that materially affect interpretation or comparison.',
            ],
          },
          {
            title: 'Reference service life',
            description:
              'Reference service life connects use phase, durability and end-of-life thinking. It should reflect a defensible expectation, not a marketing claim.',
            bullets: [
              'Tie service life to test data, warranty logic, standards or expert judgement.',
              'Keep it consistent with maintenance or spare-part assumptions.',
              'Update it when new evidence changes durability expectations.',
            ],
          },
          {
            title: 'Additional interpretation notes',
            description:
              'Use optional notes for uncertainties, scenario caveats or methodological choices that do not fit elsewhere but influence how results should be read.',
            bullets: [
              'Call out where sensitivity testing may be needed.',
              'Note if customer-specific variants could change the footprint materially.',
              'Use this area to preserve reviewer context for future updates.',
            ],
          },
        ],
      },
      {
        badge: 'Database',
        description:
          'Ecoinvent is the background database behind many LCA models. Users should understand what it is good for, how to choose datasets and how to document proxy use.',
        tab: 'lca',
        title: 'Ecoinvent and secondary data usage',
        entries: [
          {
            title: 'What ecoinvent is',
            description:
              'Ecoinvent is a life-cycle inventory database with background datasets for materials, energy, transport, waste treatment and many industrial processes.',
            bullets: [
              'It is used when primary product-specific data is unavailable or incomplete.',
              'It should complement primary data, not replace it without reflection.',
              'Version and dataset choice should be recorded as part of study logic.',
            ],
          },
          {
            title: 'How to use ecoinvent responsibly',
            description:
              'Choose datasets that match geography, technology and product type as closely as possible, then explain the fit and any known mismatch.',
            bullets: [
              'Start from the real process and then find the closest defensible proxy.',
              'Prefer datasets that match region and technology before generic global proxies.',
              'Capture why a dataset was selected so another modeller can repeat the logic.',
            ],
          },
          {
            title: 'How to combine primary and secondary data',
            description:
              'Primary data should describe the organization-specific activity, while secondary data fills gaps in background processes or generic upstream modules.',
            bullets: [
              'Use primary data for core materials, plant energy, yields and transport where possible.',
              'Use secondary datasets for upstream or generic background processes that are not practical to measure directly.',
              'Be explicit about where the model stops being primary-data-driven.',
            ],
          },
          {
            title: 'What to tell users about data quality',
            description:
              'Users should know when a result is based on measured data versus proxy assumptions. That transparency matters more than pretending the model is perfect.',
            bullets: [
              'Flag datasets with weak geographical or technological fit.',
              'Keep a list of the most material proxy datasets to improve later.',
              'Use follow-up tasks to replace proxies when better supplier or plant data becomes available.',
            ],
          },
        ],
      },
      {
        badge: 'Analytics',
        description:
          'The Analytics tab helps users interpret the current model, test quick what-if scenarios and understand how the chosen area of impact behaves before changing the product data itself.',
        tab: 'lca',
        title: 'How to use the analytical features',
        visualId: 'analytics-guide',
        entries: [
          {
            title: 'Start from the selected area of impact',
            description:
              'Analytics always works for the currently selected impact category and unit, for example kg CO2-Eq. Before reading charts, make sure the selected area of impact matches the business or methodological question you actually want to answer.',
            bullets: [
              'Change the area of impact in the diagram filters first, then open Analytics.',
              'Read every result together with its unit because different impact categories are not directly comparable.',
              'Use the same area of impact when discussing baseline and scenarios with other teams.',
              'If the discussion is about climate impact, stay in kg CO2-Eq throughout the comparison instead of switching categories mid-review.',
            ],
          },
          {
            title: 'Use Scenario Explorer for quick what-if decisions',
            description:
              'Scenario Explorer is the practical decision tool. It estimates how the current result would move if recycled content increased, product mass decreased or transport burden was reduced. It is meant to support prioritization before someone edits the real product model.',
            bullets: [
              'Use recycled content when discussing supplier improvements or material substitutions.',
              'Use lightweighting when engineering wants to test lower mass or lower material intensity.',
              'Use transport optimization when logistics, sourcing or packaging changes are being considered.',
              'Use the sliders to test realistic ranges first, not idealized best-case values that the business cannot implement.',
            ],
          },
          {
            title: 'How to read the scenario cards',
            description:
              'Each card shows the baseline footprint, the estimated scenario footprint and the delta. Negative delta means the scenario lowers the impact relative to the current model, while a small delta may indicate that the parameter is not a strong lever for this product.',
            bullets: [
              'Treat the baseline as the current product model, not as a market benchmark.',
              'Look at delta and delta percent together to understand both absolute and relative effect.',
              'Use scenarios to prioritize actions, then update the underlying model if the change is accepted.',
              'If one scenario changes little, that is still a useful result because it tells the team where not to spend effort first.',
            ],
          },
          {
            title: 'What Monte Carlo is useful for here',
            description:
              'Monte Carlo in this screen helps visualize variability around the current total result for the selected impact area. It is useful as an uncertainty-style view when users need to explain that the result is not a single perfectly precise number, but it should not replace review of assumptions, datasets and stage logic.',
            bullets: [
              'Use it to communicate that results are estimates rather than perfectly exact numbers.',
              'Do not treat it as proof that a model is correct; it still depends on the quality of the underlying inputs.',
              'When a decision depends on one parameter, Scenario Explorer is usually more actionable than Monte Carlo alone.',
              'Use Monte Carlo mainly to frame confidence and variability, not to choose between operational options by itself.',
            ],
          },
          {
            title: 'What Analytics does not replace',
            description:
              'Analytics helps interpretation and prioritization, but it does not replace model governance. A good-looking scenario result does not mean the product record, assumptions or stage mapping are already correct.',
            bullets: [
              'Do not use Analytics as a substitute for checking materials, transport legs, end-of-life routes or activity data.',
              'Do not treat slider outputs as approved product changes until the underlying source data has been updated.',
              'Do not compare two scenarios created on different impact categories as if they answered the same question.',
              'If the model is missing a material stage or uses a weak proxy dataset, fix that first before drawing strong conclusions.',
            ],
          },
          {
            title: 'Recommended workflow for analysts',
            description:
              'A practical workflow is: review the flowchart and impact view first, then open Analytics to test alternatives, and only afterwards decide whether the product record or flowchart should be updated. This keeps the team from changing source data before understanding what really drives the result.',
            bullets: [
              'First confirm which stages dominate the footprint in the current model.',
              'Next test 2-3 realistic scenarios instead of many extreme slider values.',
              'Finally convert the chosen scenario into a real data or modelling change so the baseline stays trustworthy.',
              'After the model update, reopen Analytics and confirm whether the expected reduction is still visible in the new baseline.',
            ],
          },
        ],
      },
      ...ECOINVENT_GUIDE_SECTIONS_EN,
      {
        badge: 'Passport',
        description:
          'Use this section when the output is a product passport for DPP. The screen on the right reflects how the platform arranges blocks, preview and sharing controls.',
        tab: 'dpp',
        title: 'How passport output works',
        visualId: 'passport-guide',
        entries: [
          {
            title: 'What the passport builder does',
            description:
              'The passport builder turns governed product and LCA data into a stakeholder-facing DPP view. It is a publication layer, not a place to invent missing content.',
            bullets: [
              'Build passport sections from product identification, environmental, material, durability, compliance and service data already stored in the platform.',
              'Review which blocks should be visible for the product group and external audience before publication.',
              'Treat missing-field warnings as source-data issues to fix in the record, not as something to patch directly in the output.',
            ],
          },
          {
            title: 'Preview, QR and sharing',
            description:
              'Passport output should be checked in the same way it will later be shared publicly or with stakeholders.',
            bullets: [
              'Validate the visible passport view on desktop and mobile before sharing it externally.',
              'Treat QR and share links as controlled publication outputs, not as draft collaboration spaces.',
              'Make sure internal notes or supplier-restricted evidence are not exposed in the public view.',
            ],
          },
          {
            title: 'Update governance after publication',
            description:
              'A passport should stay updateable after release, so the product record needs a clear review trail and ownership model.',
            bullets: [
              'Keep source PDFs, validity dates and review comments inside the product record.',
              'Recheck the passport whenever supplier data, certificates, product variants or service-life assumptions change.',
              'Assign one owner for post-publication updates and public output quality.',
            ],
          },
        ],
      },
      {
        badge: 'Reports',
        description:
          'Use this section when the same product backbone has to generate formal reports. The screen on the right reflects the report-builder flow inside the platform.',
        tab: 'dpp',
        title: 'How report output works',
        visualId: 'report-guide',
        entries: [
          {
            title: 'What the report builder does',
            description:
              'The report builder assembles the product study into a structured deliverable. It should reuse the same governed product backbone instead of duplicating content outside the platform.',
            bullets: [
              'Treat report generation as the last step after product data, evidence and LCA logic are already reviewed.',
              'Use the same product record to feed different report formats instead of rebuilding the same story in parallel.',
              'Keep report sections aligned with the actual model, assumptions and evidence trail.',
            ],
          },
          {
            title: 'Reports for EPD, PEF and scope 3',
            description:
              'One report layer can support multiple reporting paths, but the logic of the deliverable must still match the target framework.',
            bullets: [
              'Make sure the report logic matches the declared unit, scope, assumptions and method used in the study.',
              'Keep report-ready evidence, background assumptions and version history in the same product record.',
              'Use one governed source of truth so changes in the model can flow into reports without duplicate editing.',
            ],
          },
          {
            title: 'Review, export and version control',
            description:
              'A report is only useful when the exported file can be traced back to a specific reviewed state of the product and model.',
            bullets: [
              'Review section completeness before export so the generated document reflects the intended scope.',
              'Track versions and export only from a reviewed configuration, especially for external deliverables.',
              'Keep ownership clear for future report updates when data, scope or methodology changes.',
            ],
          },
        ],
      },
      {
        badge: 'Operations',
        description:
          'Most organizations fail not because the methodology is unknown, but because data collection is fragmented. These steps turn the platform into an operating model.',
        tab: 'dpp',
        title: 'How to organize product data collection inside the company',
        entries: [
          {
            title: 'Start with one owner and one template',
            description:
              'Assign one internal owner per product and build one recurring request structure for plants, procurement, engineering and quality teams.',
            bullets: [
              'Avoid sending different spreadsheets from different departments.',
              'Reuse the platform field structure as the collection template.',
              'Make ownership visible before asking suppliers for anything.',
            ],
          },
          {
            title: 'Collect by life-cycle stage',
            description:
              'Ask for data in the order materials, manufacturing, logistics, use and end of life. That mirrors how the model will later be built and reviewed.',
            bullets: [
              'Procurement can usually provide materials and key suppliers first.',
              'Plants can provide process, energy, scrap and yield logic.',
              'Service or quality teams often know maintenance and lifetime assumptions.',
            ],
          },
          {
            title: 'Separate evidence from assumptions',
            description:
              'Every incoming number should be marked as measured, declared by supplier or estimated by the project team.',
            bullets: [
              'Use evidence uploads for files and declarations.',
              'When a number is estimated, say who estimated it and why.',
              'This separation makes later review and updates much faster.',
            ],
          },
          {
            title: 'Build an improvement backlog',
            description:
              'Do not wait for perfect data before starting. Instead, publish the current model discipline and keep a visible backlog of the highest-impact gaps.',
            bullets: [
              'Replace the largest proxy datasets first.',
              'Prioritise suppliers or plants that dominate mass, spend or impact.',
              'Use the same backlog to improve both LCA and passport readiness over time.',
            ],
          },
        ],
      },
      {
        badge: 'GS1',
        description:
          'GS1 identifiers support product master data inside the platform and become useful when records need stronger interoperability across systems, packaging, labels or downstream exchange.',
        tab: 'platform',
        title: 'GS1 codes, product identifiers and traceability',
        entries: [
          {
            title: 'GTIN: Global Trade Item Number',
            description:
              'GTIN is the core GS1 identifier used to identify trade items in supply chains, labels, catalogues and digital systems.',
            bullets: [
              'Use GTIN when the product already has an assigned GS1 trade item number.',
              'Validate length and check digit before import.',
              'Review imported content because packaging-level GTINs and market variants may differ.',
            ],
          },
          {
            title: 'GPC: Global Product Classification',
            description:
              'GPC is a GS1 classification that groups products for data exchange and categorisation. It is not the same thing as CPC or NACE.',
            bullets: [
              'Use GPC to support commercial and interoperability use cases.',
              'Keep CPC for LCA or reporting category logic when required.',
              'Do not force one code system to solve every classification need.',
            ],
          },
          {
            title: 'Model, batch and serial logic',
            description:
              'These identifiers support traceability below the product-family level and are useful when passport content or service information changes by variant or batch.',
            bullets: [
              'Model identifies the product version or configuration.',
              'Batch supports manufacturing-period traceability.',
              'Serial number is relevant when unit-level traceability is required.',
            ],
          },
          {
            title: 'Where GS1 helps most',
            description:
              'GS1 becomes most useful when outputs must connect cleanly to ERP, PLM, e-commerce, packaging, labels or QR-driven journeys.',
            bullets: [
              'GS1 improves interoperability and reduces manual remapping.',
              'It helps link passport content to physical labels or QR-driven journeys.',
              'It creates a stronger bridge between sustainability data and product master data.',
            ],
          },
        ],
      },
      {
        badge: 'Compliance',
        description:
          'Treat this area as one shared product-data backbone with four output paths: ESPR product passport, EPD with CPR report, PEF report and LCA feeding CSRD scope 3.',
        tab: 'regulations',
        title: 'How to structure four regulatory and reporting paths',
        entries: [
          {
            title: 'Recommended structure for this section',
            description:
              'Start with one shared product record and branch into deliverable-specific workflows only where methodology, review or publication rules diverge.',
            bullets: [
              'Shared backbone: identifiers, product hierarchy, BOM, supplier evidence, manufacturing data, logistics, geography, assumptions and version history.',
              'Branch 1: ESPR to product passport for identity, traceability, circularity and controlled data-sharing.',
              'Branch 2: EPD and CPR to report for construction-oriented declarations that need structured LCA evidence and review.',
              'Branch 3: PEF to report for method-heavy comparability and stricter modelling discipline.',
              'Branch 4: LCA to CSRD scope 3 for company-level value-chain emissions that still depend on product and supplier evidence.',
            ],
          },
          {
            title: 'ESPR to product passport',
            description:
              'Use this path when the objective is DPP readiness under ESPR and a governed, updateable product-passport dataset.',
            bullets: [
              'Data: stable identifiers, product and variant attributes, manufacturer or economic-operator details, material composition, repair or service information, and linked certificates or evidence.',
              'Flow: build the core product record, map variants and identifiers, attach supporting evidence, then configure the passport blocks and access rules.',
              'Watch out: delegated acts may change the exact payload by product group, so keep the data model flexible instead of hardcoding one passport structure.',
              'Watch out: separate public passport content from confidential supplier or compliance evidence before publication.',
            ],
          },
          {
            title: 'EPD, CPR and report',
            description:
              'Use this path when a defendable product study must end in an EPD-style declaration or a report supporting CPR-related documentation.',
            bullets: [
              'Data: declared or functional unit, BOM, manufacturing and energy data, transport, packaging, waste, scenario assumptions, and the standards or category rules used for the report.',
              'Flow: confirm the target declaration logic first, collect plant and supplier data by life-cycle stage, build the study with the required module structure, then prepare the report package for review and publication.',
              'Watch out: keep the declared unit, system boundary and module logic consistent from model to final report; this is where teams often create rework.',
              'Watch out: data vintage, site specificity and evidence quality must match what the verifier or downstream customer will expect.',
            ],
          },
          {
            title: 'PEF and report',
            description:
              'Use this path when comparability is central and the study has to follow a more prescriptive environmental-footprint logic.',
            bullets: [
              'Data: category-specific method rules, high-quality foreground data, aligned secondary datasets, allocation choices, end-of-life assumptions and documented data-quality limitations.',
              'Flow: identify the relevant method or category rules, model with those comparability constraints from the start, review methodological choices explicitly, then generate the report with assumptions and limitations disclosed.',
              'Watch out: avoid mixing a generic screening LCA with PEF-style comparative claims; the method discipline needs to be visible from the first modelling decision.',
              'Watch out: allocation, secondary data and end-of-life choices require explicit rationale because they materially affect comparability.',
            ],
          },
          {
            title: 'LCA to CSRD scope 3',
            description:
              'Use this path when product and supplier evidence must support company-level scope 3 calculations and CSRD-ready disclosures.',
            bullets: [
              'Data: purchase or activity mapping, supplier and plant geography, volumes, product LCAs or emission factors, reporting-year coverage and uncertainty notes.',
              'Flow: map products and purchases to the relevant scope 3 categories, collect primary data for hotspots, fill the remaining gaps with documented proxies, then aggregate and reconcile with reporting needs.',
              'Watch out: product studies and company disclosures use different boundaries, so prevent double counting and reconcile carefully with finance or procurement data.',
              'Watch out: year coverage, consolidation logic and proxy quality should be transparent, because CSRD scrutiny will focus on traceability and consistency.',
            ],
          },
        ],
      },
    ],
    guideTitle: 'Field-by-field guidance',
    heroDescription:
      'Use this knowledge hub as the operational layer of the platform: understand what each form does, how to collect product data internally, how GS1 and ecoinvent fit in, and how LCA, DPP and regulations connect.',
    heroTitle:
      'Turn product data into a usable LCA and product passport workflow',
    knowledgeCards: [
      {
        bullets: [
          'Create the product record before trying to build reports.',
          'Keep identifiers, producer context and intended use clean from day one.',
          'Treat the platform as a controlled product evidence system, not a document dump.',
        ],
        status: 'Platform',
        summary:
          'A reliable implementation starts with master data discipline and clear ownership, not with the final PDF output.',
        tab: 'platform',
        title: 'How to approach the platform as a system of record',
      },
      {
        bullets: [
          'Functional unit, boundaries and evidence determine whether the study is defensible.',
          'Primary data is strongest where the product is specific and material.',
          'Secondary datasets should be visible and reviewable, not hidden.',
        ],
        status: 'LCA',
        summary:
          'The platform mirrors real LCA practice: scope first, then inventory, then interpretation.',
        tab: 'lca',
        title: 'What good LCA work looks like inside the platform',
      },
      {
        bullets: [
          'Open the LCA tab in Help Centre and scroll to Field-by-field guidance.',
          'Look for the section called How to use the analytical features.',
          'Use Scenario Explorer for practical what-if checks and Monte Carlo as a supporting uncertainty-style view.',
        ],
        status: 'LCA Analytics',
        summary:
          'The Help Centre includes a dedicated guide for the Analytics tab, including Scenario Explorer and Monte Carlo.',
        tab: 'lca',
        title: 'Where to find the Analytics guide',
      },
      {
        bullets: [
          'This area is for generating governed external outputs from one product record.',
          'Passports support DPP, while reports support EPD, Product Environmental Footprint and scope 3 work.',
          'Output quality depends on complete source data, evidence and version control, not only on the final layout.',
        ],
        status: 'DPP',
        summary:
          'The output layer should generate both passports and reports from the same controlled product-data backbone.',
        tab: 'dpp',
        title: 'How one product record supports passports and reports',
      },
      {
        bullets: [
          'Build one shared product-data backbone, then split it into four delivery paths instead of four separate projects.',
          'Each path needs different outputs, but the same discipline around identifiers, evidence, LCA logic and versioning.',
          'The highest leverage comes from deciding early which data is common and which review rules are path-specific.',
        ],
        status: 'Regulation',
        summary:
          'The most robust operating model is one product record serving ESPR/DPP, EPD with CPR, PEF and CSRD scope 3.',
        tab: 'regulations',
        title: 'How to turn regulation work into four governed paths',
      },
      ...ECOINVENT_KNOWLEDGE_CARDS_EN,
    ],
    videoInstructionsLabel: 'Video instructions',
    noMatchingCardsDescription:
      'Try another keyword or move to a different workstream.',
    noMatchingCardsTitle: 'No matching overview cards',
    noMatchingQuestionsLabel:
      'No matching questions for the selected workstream and keyword.',
    officialResourceButtonLabel: 'Open source',
    officialResources: [
      {
        label: 'ecoinvent',
        summary:
          'Official entry point for the ecoinvent database and its methodological context.',
        url: 'https://ecoinvent.org/',
      },
      {
        label: 'GS1 standards',
        summary:
          'Overview of GS1 standards and identifiers relevant for product master data and interoperability.',
        url: 'https://www.gs1.org/standards',
      },
      {
        label: 'PEF method',
        summary:
          'European Commission overview of the Product Environmental Footprint method and category rules.',
        url: 'https://green-forum.ec.europa.eu/green-business/environmental-footprint-methods/pef-method_en',
      },
      {
        label: 'ESPR and DPP',
        summary:
          'European Commission page describing the Ecodesign for Sustainable Products Regulation and Digital Product Passport context.',
        url: 'https://commission.europa.eu/energy-climate-change-environment/standards-tools-and-labels/products-labelling-rules-and-requirements/ecodesign-sustainable-products-regulation_en',
      },
      {
        label: 'CSRD and ESRS',
        summary:
          'European Commission explanation of sustainability reporting obligations and standards.',
        url: 'https://finance.ec.europa.eu/financial-markets/company-reporting-and-auditing/company-reporting/corporate-sustainability-reporting_en',
      },
      {
        label: 'CBAM',
        summary:
          'Official European Commission portal for Carbon Border Adjustment Mechanism guidance.',
        url: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en',
      },
      ...ECOINVENT_OFFICIAL_RESOURCES_EN,
    ],
    officialResourcesTitle: 'Primary sources worth sharing with the team',
    quickActions: [
      {
        description:
          'How to fill the platform, what each field group means and how to keep evidence attached.',
        id: 'platform',
        title: 'Platform guide',
      },
      {
        description:
          'What LCA is, how to define scope and how to work with product and process data.',
        id: 'lca',
        title: 'LCA essentials',
      },
      {
        description:
          'How to access ecoinvent from the Envirly LCA platform, validate dataset choices and compare them against ecoQuery.',
        id: 'ecoinvent',
        title: 'Ecoinvent expert guide',
      },
      {
        description:
          'How the platform generates passports for DPP and reports for EPD, PEF and scope 3.',
        id: 'dpp',
        title: 'Passports and reports',
      },
      {
        description:
          'How one product data backbone supports four paths: ESPR/DPP, EPD with CPR, PEF reporting and LCA for CSRD scope 3.',
        id: 'regulations',
        title: 'Regulation radar',
      },
    ],
    quickActionsTitle: 'Choose the workstream you are working on',
    searchPlaceholder:
      'Search fields, GTIN, GPC, ecoinvent, functional unit, suppliers...',
    searchNoExactMatchDescription:
      'We could not find an exact match for this phrase in the current knowledge base. Try one of the closest topics below.',
    searchNoExactMatchTitle: 'No exact match found',
    searchResultsLabel: 'matching guidance items',
    searchSuggestionOpenLabel: 'Open topic',
    searchSuggestionsLabel: 'Closest similar topics',
    stats: [
      'Field-level instructions',
      'GS1 and ecoinvent explained',
      'Workflow for cross-team data collection',
    ],
    tabs: {
      platform: 'Platform',
      lca: 'LCA',
      ecoinvent: 'Ecoinvent',
      dpp: 'Passports & reports',
      regulations: 'Regulations',
    },
    workflowDescription:
      'Use this as the recommended sequence for onboarding a product into the platform and collecting data across the organization.',
    workflowEmptyDescription:
      'No workflow steps matched the current keyword. Try a broader term like materials, supplier or passport.',
    workflowEmptyTitle: 'No matching workflow steps',
    workflowOutputLabel: 'Expected outputs',
    workflowOwnerLabel: 'Typical owner',
    workflowSteps: [
      {
        owner: 'Product owner / sustainability lead',
        outputs: [
          'Product owner assigned',
          'Study purpose agreed',
          'Priority frameworks identified',
        ],
        summary:
          'Start by agreeing why the record exists, who owns it and whether the target is internal design, customer request, DPP preparation or external reporting.',
        tab: 'platform',
        title: '1. Kick off the product record',
      },
      {
        owner: 'Product owner / master data',
        outputs: [
          'Name and description completed',
          'CPC and NACE selected',
          'GTIN and producer information reviewed',
        ],
        summary:
          'Fill the core product record first so every later request points to one stable master-data object.',
        tab: 'platform',
        title: '2. Lock the basic identifiers and classifications',
      },
      {
        owner: 'Procurement / engineering',
        outputs: [
          'Material list drafted',
          'Key suppliers identified',
          'Critical certificates requested',
        ],
        summary:
          'Collect the initial bill of materials, supplier map and documentation that will anchor the model and the evidence pack.',
        tab: 'platform',
        title: '3. Collect upstream product evidence',
      },
      {
        owner: 'Plant / operations / LCA analyst',
        outputs: [
          'Process description captured',
          'Production geography confirmed',
          'Primary data coverage assessed',
        ],
        summary:
          'Gather manufacturing, energy, yield, logistics and location information in the same structure that the LCA will use later.',
        tab: 'lca',
        title: '4. Build the life-cycle inventory backbone',
      },
      {
        owner: 'LCA analyst',
        outputs: [
          'Functional unit defined',
          'Assumptions documented',
          'Secondary datasets justified',
        ],
        summary:
          'Turn raw operational data into a defensible model by documenting assumptions, boundaries and the logic behind ecoinvent or proxy dataset choices.',
        tab: 'lca',
        title: '5. Model, validate and interpret',
      },
      ...ECOINVENT_WORKFLOW_STEPS_EN,
      {
        owner: 'Digital product / compliance lead',
        outputs: [
          'Passport or report output reviewed',
          'Shared view or exported deliverable validated',
          'Update owner assigned',
        ],
        summary:
          'After the source data is governed, configure the passport or report output and make sure the published or exported result is complete, reviewable and appropriate for the audience.',
        tab: 'dpp',
        title: '6. Publish passports and reports',
      },
      {
        owner: 'Compliance / sustainability team',
        outputs: [
          'Evidence links retained',
          'Applicable frameworks mapped',
          'Improvement backlog prioritised',
        ],
        summary:
          'Maintain one evidence layer for regulations, customer requests and future updates instead of rebuilding data from scratch every time.',
        tab: 'regulations',
        title: '7. Reuse the record across frameworks',
      },
    ],
    workflowTitle: 'Recommended implementation sequence',
  },
  pl: {
    badge: 'Centrum pomocy',
    breadcrumbs: [
      { name: 'Lista produktów', url: '/lca/products' },
      { name: 'Centrum pomocy', url: '/lca/help-centre' },
    ],
    checklistItems: [
      {
        id: 'owner',
        label: 'Przypisz jednego właściciela danych produktu i weryfikujących',
      },
      {
        id: 'scope',
        label: 'Ustal cel LCA, zakres, jednostkę funkcjonalną i metodę',
      },
      {
        id: 'primary',
        label: 'Zbierz dane pierwotne z zakładów i od dostawców',
      },
      {
        id: 'secondary',
        label: 'Uzupełnij luki danymi wtórnymi i założeniami',
      },
      {
        id: 'evidence',
        label: 'Podepnij dowody, certyfikaty i pliki źródłowe',
      },
      {
        id: 'passport',
        label: 'Przygotuj dane pod DPP i udostępnianie produktu',
      },
    ],
    checklistLabel: 'Checklista operacyjna',
    checklistProgressLabel: 'ukończono',
    checklistTitle: 'Co powinien zawierać rekord produktu gotowy do wdrożenia',
    disclaimer:
      'To centrum wiedzy jest stworzone do codziennej pracy, wdrażania i porządkowania danych. Wspiera zgodność, ale nie zastępuje formalnej analizy prawnej.',
    faqTitle: 'Najczęstsze pytania',
    faqs: [
      {
        question: 'Co użytkownik powinien uzupełnić najpierw?',
        answer:
          'Najpierw rekord produktu, identyfikatory, kontekst producenta i przewidziane zastosowanie. Dopiero potem zakres LCA, dane materiałowe i procesowe, a na końcu paszport i raportowanie.',
        tab: 'platform',
      },
      {
        question:
          'Dlaczego wersję ecoinvent wybiera się przy tworzeniu produktu?',
        answer:
          'Bo wersja bazy jest częścią logiki modelu. Zachowanie jednej wersji dla produktu ogranicza niekontrolowane zmiany wyników między iteracjami.',
        tab: 'lca',
      },
      {
        question: 'Czy GTIN jest obowiązkowy dla każdego produktu?',
        answer:
          'Nie zawsze, ale jest bardzo mocnym identyfikatorem, jeśli organizacja korzysta z GS1. Jest szczególnie przydatny dla DPP, danych podstawowych produktu, etykiet i integracji z dalszymi systemami.',
        tab: 'platform',
      },
      {
        question: 'Jak podejść do zbierania danych między zespołami?',
        answer:
          'Przypisz jednego właściciela produktu, przygotuj jeden szablon prośby o dane według etapów cyklu życia, zbieraj dane pierwotne od zakładów i działu zakupów, a założenia zapisuj jawnie tam, gdzie danych brakuje.',
        tab: 'platform',
      },
      {
        question: 'Jaka jest różnica między CPC, NACE i GPC?',
        answer:
          'CPC klasyfikuje produkt, NACE klasyfikuje działalność gospodarczą, a GPC jest klasyfikacją GS1 używaną w wymianie danych handlowych. Każdy z tych kodów rozwiązuje inny problem i mogą współistnieć na jednym produkcie.',
        tab: 'platform',
      },
      {
        question: 'Po czym poznać, że LCA nadaje się do użycia na zewnątrz?',
        answer:
          'Po jasno zdefiniowanym celu i zakresie, jednostce funkcjonalnej, przejrzystych granicach systemu, udokumentowanych źródłach danych, jawnych założeniach, spójnych zbiorach danych i ścieżce dowodowej możliwej do przeglądu.',
        tab: 'lca',
      },
      {
        question: 'Czy kreator paszportu zastępuje przepływ pracy LCA?',
        answer:
          'Nie. Kreator jest warstwą prezentacji i udostępniania. Dane produktu i LCA nadal muszą być poprawnie uporządkowane w rekordzie produktu.',
        tab: 'dpp',
      },
      {
        question: 'Czy jeden rekord produktu może obsłużyć wiele regulacji?',
        answer:
          'Tak, jeśli dane podstawowe, dowody, dane środowiskowe i identyfikatory są utrzymywane w jednym spójnym modelu pracy. Dlatego to Centrum pomocy łączy platformę, LCA, DPP i regulacje.',
        tab: 'regulations',
      },
      ...ECOINVENT_FAQS_PL,
    ],
    flowchartAreaLabel: 'Obszar wpływu',
    flowchartBoundaryLabel: 'przykład od kołyski do grobu',
    flowchartCanvasHint:
      'Przykładowy ekran: czytaj etapy od lewej do prawej i umieszczaj obiekty pod tą fazą cyklu życia, w której faktycznie zachodzą.',
    flowchartCanvasTitle: 'Przykładowy ekran schematu przepływu',
    flowchartDescription:
      'Schemat przepływu to miejsce, w którym LCA przestaje być abstrakcyjne. Pomaga zdecydować, które fazy cyklu życia wchodzą do zakresu, jakie procesy należą do każdej fazy oraz gdzie umieścić materiały, transport, opakowanie albo scenariusze końca życia.',
    flowchartExampleStages: [
      {
        title: 'Pozyskanie i przetwarzanie surowców',
        items: ['Blacha aluminiowa', 'Granulat z recyklatu'],
      },
      {
        title: 'Transport surowców',
        items: ['Transport ciężarówką od dostawcy', 'Transport palet inbound'],
      },
      {
        title: 'Produkcja i pakowanie',
        items: ['Cięcie i formowanie', 'Montaż i opakowanie detaliczne'],
      },
      {
        title: 'Dystrybucja i magazynowanie',
        items: ['Magazyn centrum dystrybucji', 'Transport outbound'],
      },
      {
        title: 'Instalacja',
        items: ['Instalacja u klienta', 'Dojazd instalatora'],
      },
      {
        title: 'Faza użytkowania',
        items: ['Zużycie energii w użyciu', 'Utrzymanie / część zamienna'],
      },
      {
        title: 'Koniec życia',
        items: ['Demontaż', 'Recykling i odpad resztkowy'],
      },
    ],
    flowchartExampleTitle: 'Jak myśleć o budowaniu schematu przepływu',
    flowchartFilterByStageLabel: 'Filtruj po etapie',
    flowchartGroupCradleLabel: 'CRADLE',
    flowchartGroupGraveLabel: 'GRAVE',
    flowchartLegendLabel: 'Co umieszczać na diagramie',
    flowchartLegendItems: [
      'Twórz osobny box dla każdego materiału, odcinka transportu, procesu albo scenariusza końca życia, który materialnie wpływa na wynik.',
      'Umieszczaj box w tej fazie, w której aktywność fizycznie zachodzi, a nie tam, gdzie siedzi dokument zakupowy.',
      'Nazwy procesów utrzymuj krótkie i konkretne, żeby inny analityk rozumiał model bez otwierania dodatkowego arkusza kalkulacyjnego.',
    ],
    flowchartNoObjectsLabel: 'Nie dodano jeszcze obiektów',
    flowchartStepLabel: 'Logika budowy',
    flowchartSteps: [
      {
        title: '1. Najpierw wybierz granicę systemu',
        description:
          'Zanim dodasz szczegółowe obiekty, zdecyduj, czy produkt modelujesz od kołyski do bramy, od kołyski do grobu albo w innym zakresie.',
        bullets: [
          'Granica systemu decyduje o tym, które kolumny pojawią się na schemacie przepływu.',
          'Wybór uzasadniaj celem studium i wymaganiem raportowym.',
          'Wyłączenia zapisuj od początku, żeby nie zniknęły później w założeniach.',
        ],
      },
      {
        title: '2. Dodaj główne procesy zanim wejdziesz w detale',
        description:
          'Zacznij od wysokopoziomowej logiki łańcucha wartości i najpierw zbuduj główną strukturę procesu. Dopiero potem dopinaj materiały, transport i aktywności energochłonne.',
        bullets: [
          'Myśl procesami typu odlew, montaż, magazynowanie albo recykling.',
          'Nie zaczynaj od dziesiątek drobnych węzłów, zanim nie będzie jasna logika etapów.',
          'Jedna główna ramka powinna odpowiadać jednej znaczącej transformacji albo trasie.',
        ],
      },
      {
        title: '3. Mapuj dane i zbiory danych do właściwego etapu',
        description:
          'Każdy obiekt powinien być pokryty albo danymi pierwotnymi, albo deklaracją dostawcy, albo danymi wtórnymi, np. z ecoinventu.',
        bullets: [
          'Materiały zwykle trafiają do pozyskania surowców albo produkcji w zależności od tego, co reprezentujesz.',
          'Odcinki transportowe warto rozdzielać, gdy znaczenie ma geografia albo środek transportu.',
          'Komentarzy i załączonych schematów przepływu używaj wtedy, gdy model wizualny wymaga dodatkowego wyjaśnienia.',
        ],
      },
      {
        title: '4. Utrzymuj diagram w stanie możliwym do weryfikacji',
        description:
          'Dobry schemat przepływu nie jest najbardziej szczegółowy, tylko taki, który inna osoba może zweryfikować i połączyć z założeniami, polami produktu oraz plikami źródłowymi.',
        bullets: [
          'Łącz trywialne węzły, jeśli nie zmieniają interpretacji wyniku.',
          'Nazwy boxów dopasuj do pakietu dowodowego i wrzuconej dokumentacji.',
          'Sprawdź, czy diagram opowiada spójną historię od materiałów do końca życia.',
        ],
      },
    ],
    flowchartSummaryLabel: 'Podsumowanie',
    flowchartSummaryValue: '42,3',
    flowchartSummaryUnit: 'kg CO2e',
    flowchartTabs: {
      analytics: 'Analityka',
      flowchart: 'Schemat przepływu',
      impact: 'Wpływ',
    },
    flowchartTitle: 'Jak budować schemat przepływu do LCA',
    guideDescription:
      'Te sekcje tłumaczą, do czego służy każda grupa pól, jak ją uzupełnić i jak powinien wyglądać dobry materiał dowodowy w praktyce.',
    guideEmptyDescription:
      'Spróbuj innego słowa kluczowego albo przełącz się na inny obszar, żeby zobaczyć więcej szczegółowych instrukcji.',
    guideEmptyTitle: 'Brak pasujących sekcji instruktażowych',
    guideSections: [
      {
        badge: 'Pola',
        description:
          'Początkowy rekord produktu decyduje o jakości danych. Te pola wpływają na kategoryzację, wybór zbiorów danych i późniejszą spójność paszportu.',
        tab: 'platform',
        title: 'Rekord produktu i kontekst biznesowy',
        entries: [
          {
            title: 'Nazwa produktu i opis produktu',
            description:
              'Wpisz handlową albo wewnętrzną nazwę produktu oraz opis, który pozwala osobie weryfikującej zrozumieć funkcję, skład i przeznaczenie rynkowe.',
            bullets: [
              'Jeśli to możliwe, używaj tej samej logiki nazewniczej co w ERP albo PLM.',
              'Opisuj to, co produkt robi, a nie tylko jak nazywa go marketing.',
              'Unikaj pustych sformułowań typu „produkt wysokiej jakości”, które nie niosą znaczenia operacyjnego.',
            ],
          },
          {
            title: 'Wersja ecoinvent',
            description:
              'Wybierz wersję ecoinvent podczas tworzenia produktu i utrzymuj ją bez zmian między iteracjami, chyba że w projekcie celowo zmienia się metodologia.',
            bullets: [
              'Zmiana wersji może zmienić zbiór danych tła i wynik.',
              'Stosuj najnowszą wersję tylko wtedy, gdy jest zgodna z zasadami projektu.',
              'Jeśli przełączasz wersję między studiami, opisz powód tej zmiany.',
            ],
          },
          {
            title: 'Kategoria produktu, branża i ramy regulacyjne',
            description:
              'CPC pomaga sklasyfikować produkt, NACE mapuje główny kontekst branżowy, a tagi ram regulacyjnych pokazują, jakie logiki raportowe albo wymogi zgodności powinny być uwzględnione od startu.',
            bullets: [
              'CPC wybieraj dla samego produktu, a nie dla całej firmy.',
              'NACE wybieraj zgodnie z działalnością, która tworzy kontekst łańcucha wartości.',
              'Tagów ram regulacyjnych używaj do oznaczenia, czy celem jest np. EPD, DPP albo Ślad środowiskowy produktu.',
            ],
          },
          {
            title: 'GTIN / kod produktu i dane producenta',
            description:
              'Pola identyfikacyjne kotwiczą rekord w informacji o produkcie. Nazwa producenta, adres i strona www powinny identyfikować podmiot prawny albo operacyjny stojący za produktem.',
            bullets: [
              'Jeśli produkt ma GTIN, trzymaj tylko kod liczbowy i zweryfikuj go przed importem.',
              'Dane producenta wpisuj tak, aby mogły później pojawić się w paszporcie albo dokumencie zgodności.',
              'Pilnuj spójności z etykietami, dokumentacją techniczną i innymi systemami źródłowymi.',
            ],
          },
        ],
      },
      {
        badge: 'Pola',
        description:
          'Ta sekcja umożliwia dołączenie dowodów, opisanie zamierzonego zastosowania oraz uzupełnienie informacji biznesowych, które później pojawiają się w paszporcie lub w kontekście audytu.',
        tab: 'platform',
        title: 'Dodatkowe informacje i pola dowodowe',
        entries: [
          {
            title: 'Schematy rozwoju produktu i dokumentacja techniczna',
            description:
              'Używaj tych uploadów do diagramów procesów, map montażu, plików technicznych, rysunków lub notatek inżynierskich wyjaśniających sposób powstawania produktu.',
            bullets: [
              'Wrzuć dowody, które potwierdzają logikę produkcji albo interpretację listy materiałowej BOM.',
              'Preferuj pliki z jasnym wersjonowaniem i właścicielem źródła.',
              'W polu opisowym dopisz, co dokładnie dany plik potwierdza.',
            ],
          },
          {
            title:
              'Przewidziane zastosowanie, typ produktu i dodatkowe informacje',
            description:
              'Wyjaśnij, jak produkt jest faktycznie używany, czy jest to produkt fizyczny czy usługa, oraz jaki kontekst powinien znać modelujący albo weryfikujący przed budową LCA.',
            bullets: [
              'Przewidziane zastosowanie wykorzystuj do budowania realistycznych założeń dla fazy użytkowania i trwałości.',
              'Typ produktu wybieraj uważnie, bo dla usług zmieniają się etykiety i oczekiwania wobec pól.',
              'Jeśli ważny kontekst nie mieści się w polach strukturalnych, zapisz go tutaj.',
            ],
          },
          {
            title: 'Data produkcji, gwarancja, znak CE i import danych GS1',
            description:
              'Te pola wspierają możliwość śledzenia i późniejszą komunikację cyfrową o produkcie. Gdy działa import GTIN, najpierw zweryfikuj kod, a potem sprawdź zaimportowane wartości.',
            bullets: [
              'Daty produkcji używaj tam, gdzie ważna jest identyfikacja partii albo okresu.',
              'Warunki gwarancji opisuj w sposób czytelny dla klienta i możliwy do przeglądu.',
              'CE zaznaczaj tylko wtedy, gdy produkt realnie posiada oznakowanie CE na właściwej podstawie prawnej.',
            ],
          },
          {
            title: 'GPC i powiązane identyfikatory',
            description:
              'Jeśli import GTIN uzupełnia GPC lub inne pola, sprawdź, czy klasyfikacja rzeczywiście odpowiada produktowi wprowadzonemu na rynek.',
            bullets: [
              'Dane GS1 mogą przyspieszyć start, ale nadal wymagają przeglądu przez właściciela produktu.',
              'Nie zakładaj, że import jest automatycznie poprawny dla każdego wariantu rynkowego.',
              'Rozbieżności między GTIN, etykietą i dokumentacją techniczną eskaluj od razu.',
            ],
          },
        ],
      },
      {
        badge: 'Pola',
        description:
          'Pola geograficzne i dostawców są krytyczne dla zbierania danych pierwotnych, możliwości śledzenia i późniejszego tłumaczenia założeń dla wcześniejszych etapów łańcucha.',
        tab: 'platform',
        title: 'Producent, geografia, dostawcy i dokumenty wspierające',
        entries: [
          {
            title: 'Producent, zakład i pola dystrybucji',
            description:
              'Oddzielaj to, kto odpowiada za produkt, od tego, gdzie odbywa się produkcja i gdzie produkt jest dystrybuowany.',
            bullets: [
              'Pól producenta używaj dla podmiotu stojącego za produktem.',
              'Kraj produkcji i adres produkcji służą do geografii wytworzenia.',
              'Regiony dystrybucji pokazują, gdzie produkt jest sprzedawany albo dostarczany.',
            ],
          },
          {
            title: 'Krótki opis działalności producenta',
            description:
              'To pole powinno wyjaśniać główną działalność producenta w jednym operacyjnym akapicie, żeby weryfikujący rozumiał kontekst przemysłowy.',
            bullets: [
              'Wspomnij, jaka rola produkcyjna albo usługowa jest realizowana.',
              'Pisz tak, aby dział zakupów albo audytor mógł szybko rozpoznać operację.',
              'Trzymaj się faktów i krótkiej formy zamiast języka marketingowego.',
            ],
          },
          {
            title: 'Kluczowi dostawcy',
            description:
              'Wpisuj dostawców, którzy istotnie wpływają na ślad, skład produktu albo pakiet dowodowy.',
            bullets: [
              'Zapisuj nazwę dostawcy, adres, dostarczany element i osobę kontaktową, jeśli jest dostępna.',
              'Priorytetyzuj dostawców powiązanych z głównymi materiałami, procesami krytycznymi albo wrażliwymi danymi wejściowymi do zgodności.',
              'Używaj tej samej nazwy dostawcy co w systemach zakupowych, żeby unikać duplikatów.',
            ],
          },
          {
            title: 'Specyfikacja techniczna i certyfikaty',
            description:
              'Specyfikacje techniczne powinny łapać mierzalne parametry produktu, a certyfikaty kontrolowane dokumenty z typem, statusem i okresem ważności.',
            bullets: [
              'Jedna linia specyfikacji powinna opisywać jeden parametr, np. pojemność, napięcie albo zakres obciążenia.',
              'Przy certyfikatach wpisuj typ dokumentu, zakres, daty ważności i to, czy dokument ma charakter środowiskowy.',
              'Jeśli istnieje PDF, podepnij go do rekordu, żeby dowód nie żył osobno od danych.',
            ],
          },
        ],
      },
      {
        badge: 'Przepływ pracy',
        description:
          'Cele i zasady ujawniania określają, po co istnieje dane LCA, kto będzie z niego korzystać i jaki poziom poufności lub komunikacji zewnętrznej jest oczekiwany.',
        tab: 'platform',
        title: 'Cele, ujęcie zakresu i logika ujawniania',
        entries: [
          {
            title: 'Przewidziane zastosowania, powody i grupa docelowa',
            description:
              'Te pola tłumaczą, dlaczego LCA jest wykonywane i kto potrzebuje wyniku. To od nich zaczynają się późniejsze decyzje metodologiczne.',
            bullets: [
              'Przewidziane zastosowania wpisuj pod kątem decyzji lub rezultatów, np. ecodesign, wymaganie klienta albo przygotowanie do regulacji.',
              'Powody realizacji studium powinny opisywać impuls biznesowy albo ryzyko.',
              'Grupa docelowa może obejmować zespoły wewnętrzne, klientów, certyfikatorów albo regulatorów.',
            ],
          },
          {
            title: 'Zlecający i eksperci zewnętrzni',
            description:
              'Zapisuj, kto zamówił analizę i czy w modelowaniu, przeglądzie albo weryfikacji brali udział zewnętrzni eksperci.',
            bullets: [
              'Pole z nazwą zlecającego wypełniaj szczególnie wtedy, gdy projekt uruchomił konkretny klient albo jednostka biznesowa.',
              'Ekspertów zewnętrznych uwzględniaj tylko wtedy, gdy realnie uczestniczyli w projekcie.',
              'Nazwij organizację ekspercką albo rolę, aby późniejszy weryfikujący wiedział, kto wspierał pracę.',
            ],
          },
          {
            title: 'Ujawnianie, poufność i twierdzenia',
            description:
              'Te przełączniki pomagają oddzielić dane, które mogą trafić na zewnątrz, od danych wewnętrznych albo objętych ograniczeniami.',
            bullets: [
              'Jawnie określ, czy wyniki albo twierdzenia będą komunikowane zewnętrznie.',
              'W polu opisu dopisz, co wolno udostępniać i pod jakimi warunkami.',
              'Notatki o poufności prowadź czytelnie, żeby przyszły paszport albo raport nie ujawnił danych przez pomyłkę.',
            ],
          },
          {
            title: 'Dodatkowe notatki',
            description:
              'Komentarzy używaj do zapisania kontekstu projektu, uwag dotyczących zarządzania, kwestii z weryfikacji albo zastrzeżeń, które są ważne interpretacyjnie, ale nie pasują do pytań strukturalnych.',
            bullets: [
              'Lepiej zapisać nierozwiązany problem niż zostawić go domyślnym założeniem.',
              'Notuj planowane aktualizacje, jeśli dane mają dopiero przyjść od dostawców.',
              'Używaj komentarzy jako miejsca przekazania kontekstu między zespołami.',
            ],
          },
        ],
      },
      {
        badge: 'LCA',
        description:
          'Te sekcje wyjaśniają logikę samego LCA i pokazują, jak platforma odwzorowuje standardowy przepływ pracy od celu i zakresu do interpretacji.',
        tab: 'lca',
        title: 'Podstawy LCA i architektura studium',
        entries: [
          {
            title: 'Czym jest LCA',
            description:
              'Ocena cyklu życia to uporządkowana metoda ilościowego badania wpływów środowiskowych w całym cyklu życia produktu, a nie tylko obliczenie emisji CO2.',
            bullets: [
              'Zazwyczaj obejmuje surowce, produkcję, transport, użytkowanie i koniec życia.',
              'Wynik ma sens tylko wtedy, gdy cel, zakres i granice systemu są przejrzyste.',
              'Platforma została zaprojektowana tak, by łapać zarówno dane strukturalne, jak i założenia stojące za nimi.',
            ],
          },
          {
            title: 'Cztery fazy LCA',
            description:
              'Pełne studium najczęściej obejmuje definicję celu i zakresu, analizę inwentarza, ocenę wpływu i interpretację.',
            bullets: [
              'Cel i zakres tłumaczą, po co istnieje studium i co obejmuje.',
              'Analiza inwentarza zbiera przepływy materiałów, energii, transportu i odpadów.',
              'Ocena wpływu przekłada analizę inwentarza na wskaźniki środowiskowe.',
            ],
          },
          {
            title: 'Dlaczego jednostka funkcjonalna jest tak ważna',
            description:
              'Jednostka funkcjonalna opisuje, jaką usługę dostarcza produkt i tworzy bazę dla uczciwego modelu oraz uczciwych porównań.',
            bullets: [
              'Używaj opisu opartego na funkcji, a nie tylko na liczbie sztuk.',
              'Dla usług opisz dostarczaną usługę, czas trwania albo przepustowość.',
              'Sprawdź, czy dostawcy i zespoły wewnętrzne potrafią odnieść swoje dane do tej samej referencji.',
            ],
          },
          {
            title: 'Co sprawia, że studium da się obronić',
            description:
              'Studium można zweryfikować i obronić tylko wtedy, gdy zbiór danych, założenia, źródła i wyłączenia są czytelne dla innej osoby.',
            bullets: [
              'Dokumentuj, dlaczego użyto danych pierwotnych albo konkretnego zastępczego zbioru danych.',
              'Wyjaśniaj główne założenia i logikę cut-off.',
              'Pakiet dowodowy trzymaj przy modelu, a nie w osobnych mailach i folderach.',
            ],
          },
        ],
      },
      {
        badge: 'Pola',
        description:
          'Product characteristics tworzą punkt odniesienia dla modelowania deklarowanego produktu albo usługi i wspierają logikę use phase oraz end-of-life.',
        tab: 'lca',
        title: 'Charakterystyka produktu i dane materiałowe',
        entries: [
          {
            title: 'Jednostka funkcjonalna i okres zbierania danych',
            description:
              'Te pola definiują, co jest oceniane i jaki przedział czasu reprezentują dane pierwotne.',
            bullets: [
              'Jednostkę funkcjonalną zapisuj tak, aby dało się jej konsekwentnie użyć w obliczeniach.',
              'Daty zbierania danych powinny odpowiadać rzeczywistemu okresowi działania, z którego pochodzą dane.',
              'Nie mieszaj średnich wieloletnich z migawką z jednego miesiąca bez wyjaśnienia.',
            ],
          },
          {
            title: 'Masa, wymiary i objętość',
            description:
              'Te cechy fizyczne wspierają założenia logistyczne, kontrolę materiałów, logikę opakowań i późniejszą zawartość paszportu.',
            bullets: [
              'Jeśli to możliwe, używaj wartości zmierzonych.',
              'Pilnuj spójnych i realistycznych jednostek dla typu produktu.',
              'Jeżeli wymiary są placeholderem, zaznacz to wyraźnie w założeniach albo komentarzach.',
            ],
          },
          {
            title: 'Skład materiałowy',
            description:
              'Wpisuj materiały podstawowe i dodatkowe wraz z udziałem procentowym oraz udziałem recyklatu, aby skład produktu był zrozumiały i możliwy do wykorzystania w modelu środowiskowym.',
            bullets: [
              'Zaczynaj od materiałów dominujących masowo albo środowiskowo.',
              'Udział recyklatu wpisuj tylko wtedy, gdy masz dowód albo uzasadnione założenie.',
              'Przed udostępnieniem wyników na zewnątrz sprawdź, czy procenty dają kierunkowo pełny obraz produktu.',
            ],
          },
          {
            title: 'Dyscyplina mass balance',
            description:
              'Wiersze materiałowe powinny razem dawać spójny obraz produktu. Nawet jeśli suma nie jest jeszcze idealna, model powinien pokazywać, gdzie są luki.',
            bullets: [
              'Sprawdź, czy główne materiały tłumaczą większość masy produktu.',
              'Dodatkowych wierszy używaj dla powłok, dodatków i mniejszych komponentów, jeśli mają znaczenie.',
              'Duże niewyjaśnione luki masowe eskaluj przed publikacją paszportu albo raportu.',
            ],
          },
        ],
      },
      {
        badge: 'Metoda',
        description:
          'Pola założeń dokumentują sposób produkcji, przyjętą referencyjną trwałość oraz miejsca, w których studium celowo pomija przepływy o niskiej istotności.',
        tab: 'lca',
        title: 'Procesy, założenia i trwałość referencyjna',
        entries: [
          {
            title: 'Opis procesu produkcji',
            description:
              'Opisz główne etapy procesu, które realnie wpływają na ślad, np. formowanie, montaż, wykończenie, testy albo pakowanie.',
            bullets: [
              'Skup się na operacjach istotnych środowiskowo, a nie na każdym mikro-kroku.',
              'Wspomnij technologię albo zakład, jeśli zmieniają założenia energetyczne albo wydajnościowe.',
              'Opis powinien być zrozumiały także dla osoby spoza zespołu zakładowego.',
            ],
          },
          {
            title: 'Założenia i reguły cut-off',
            description:
              'Te pola tłumaczą, jak potraktowano braki danych, przepływy o niskiej istotności oraz logikę estymacji w modelu.',
            bullets: [
              'Dokumentuj, gdzie brakowało danych pierwotnych i jakie przybliżenia zastosowano.',
              'Cut-off stosuj konsekwentnie, żeby wyłączenia dało się później zweryfikować.',
              'Wyjaśniaj każde założenie, które materialnie wpływa na interpretację lub porównanie.',
            ],
          },
          {
            title: 'Referencyjna trwałość użytkowa',
            description:
              'Referencyjna trwałość łączy fazę użytkowania, trwałość i koniec życia. Powinna wynikać z obronionego oczekiwania, a nie z deklaracji marketingowej.',
            bullets: [
              'Odnoś trwałość do testów, logiki gwarancyjnej, norm albo oceny eksperckiej.',
              'Pilnuj spójności z założeniami o utrzymaniu i częściach zamiennych.',
              'Aktualizuj ją wtedy, gdy nowy dowód zmienia oczekiwania dotyczące trwałości.',
            ],
          },
          {
            title: 'Dodatkowe uwagi interpretacyjne',
            description:
              'Dodatkowe notatki wykorzystuj do zapisania niepewności, zastrzeżeń scenariuszowych albo decyzji metodologicznych, które nie mieszczą się gdzie indziej, ale wpływają na odczyt wyniku.',
            bullets: [
              'Zaznacz, gdzie może być potrzebna analiza wrażliwości.',
              'Notuj, czy wariant klientowski może istotnie zmienić ślad.',
              'Używaj tego obszaru do zachowania kontekstu weryfikacji przy kolejnych aktualizacjach.',
            ],
          },
        ],
      },
      {
        badge: 'Baza danych',
        description:
          'Ecoinvent to baza tła stojąca za wieloma modelami LCA. Użytkownik powinien rozumieć, do czego służy, jak dobierać zbiory danych i jak dokumentować użycie przybliżeń.',
        tab: 'lca',
        title: 'Ecoinvent i praca z danymi wtórnymi',
        entries: [
          {
            title: 'Czym jest ecoinvent',
            description:
              'Ecoinvent to baza danych LCI zawierająca zbiory danych tła dla materiałów, energii, transportu, zagospodarowania odpadów i wielu procesów przemysłowych.',
            bullets: [
              'Jest używana tam, gdzie brakuje pełnych danych pierwotnych specyficznych dla produktu.',
              'Powinna uzupełniać dane pierwotne, a nie bezrefleksyjnie je zastępować.',
              'Wersję i wybór zbioru danych trzeba dokumentować jako część logiki studium.',
            ],
          },
          {
            title: 'Jak odpowiedzialnie korzystać z ecoinventu',
            description:
              'Dobieraj zbiory danych tak, aby jak najlepiej pasowały geograficznie, technologicznie i produktowo do rzeczywistego procesu, a potem opisz to dopasowanie oraz znane niedopasowania.',
            bullets: [
              'Najpierw opisz realny proces, a dopiero potem szukaj najbliższego obronionego przybliżenia.',
              'Przed ogólnym globalnym przybliżeniem preferuj zbiory danych zgodne z regionem i technologią.',
              'Zapisuj, dlaczego wybrano dany zbiór danych, żeby inny modelujący mógł powtórzyć tę logikę.',
            ],
          },
          {
            title: 'Jak łączyć dane pierwotne i wtórne',
            description:
              'Dane pierwotne powinny opisywać aktywność specyficzną dla organizacji, a dane wtórne wypełniać luki w procesach tła albo modułach ogólnych.',
            bullets: [
              'Dane pierwotne stosuj tam, gdzie to możliwe dla głównych materiałów, energii zakładu, wydajności i transportu.',
              'Dane wtórne stosuj dla wcześniejszych etapów łańcucha i procesów tła, których nie da się praktycznie zmierzyć bezpośrednio.',
              'Jasno zaznaczaj, od którego miejsca model przestaje być oparty o dane pierwotne.',
            ],
          },
          {
            title: 'Jak mówić użytkownikom o jakości danych',
            description:
              'Użytkownicy muszą wiedzieć, które wyniki stoją na danych mierzonych, a które na przybliżeniach i założeniach. Ta transparentność jest ważniejsza niż udawanie perfekcyjnego modelu.',
            bullets: [
              'Oznaczaj zbiory danych ze słabym dopasowaniem geograficznym albo technologicznym.',
              'Prowadź listę najistotniejszych zastępczych zbiorów danych do poprawy w kolejnych iteracjach.',
              'Używaj dalszych działań, aby zastępować przybliżenia lepszymi danymi od dostawców lub zakładów.',
            ],
          },
        ],
      },
      {
        badge: 'Analityka',
        description:
          'Zakładka Analityka pomaga zinterpretować bieżący model, szybko przetestować scenariusze wariantowe i zrozumieć zachowanie wybranego wskaźnika wpływu jeszcze przed zmianą danych produktu.',
        tab: 'lca',
        title: 'Jak korzystać z funkcji analitycznych',
        visualId: 'analytics-guide',
        entries: [
          {
            title: 'Zacznij od wybranego obszaru wpływu',
            description:
              'Analityka zawsze działa dla aktualnie wybranej kategorii wpływu i jej jednostki, np. kg CO2-Eq. Zanim zaczniesz interpretować wykresy, upewnij się, że wybrany obszar wpływu odpowiada na pytanie biznesowe albo metodologiczne, na które chcesz odpowiedzieć.',
            bullets: [
              'Najpierw zmień obszar wpływu w filtrach diagramu, a dopiero potem otwórz Analitykę.',
              'Każdy wynik czytaj razem z jednostką, bo różnych kategorii wpływu nie da się porównywać wprost.',
              'W rozmowie o wyniku bazowym i scenariuszach trzymaj ten sam obszar wpływu dla wszystkich uczestników.',
              'Jeżeli rozmowa dotyczy wpływu klimatycznego, trzymaj się kg CO2-Eq przez całą analizę zamiast zmieniać kategorię w połowie przeglądu.',
            ],
          },
          {
            title:
              'Eksplorator scenariuszy służy do szybkich decyzji wariantowych',
            description:
              'Eksplorator scenariuszy jest praktycznym narzędziem decyzyjnym. Szacuje, jak zmieni się obecny wynik, jeśli wzrośnie udział recyklatu, spadnie masa produktu albo zmniejszy się obciążenie transportowe. To narzędzie do priorytetyzacji działań jeszcze przed zmianą właściwego modelu produktu.',
            bullets: [
              'Udział recyklatu wykorzystuj przy rozmowach o zmianie dostawcy albo substytucji materiału.',
              'Odchudzanie produktu wykorzystuj wtedy, gdy inżynieria chce przetestować niższą masę lub niższą materiałochłonność.',
              'Optymalizację transportu wykorzystuj przy zmianach logistycznych, zakupowych albo opakowaniowych.',
              'Na początku testuj realistyczne zakresy na suwakach, a nie idealne scenariusze, których organizacja i tak nie wdroży.',
            ],
          },
          {
            title: 'Jak czytać karty scenariuszy',
            description:
              'Każda karta pokazuje ślad bazowy, oszacowany wynik scenariusza oraz deltę. Ujemna delta oznacza, że scenariusz obniża wpływ względem bieżącego modelu, a bardzo mała delta może oznaczać, że dany parametr nie jest istotną dźwignią dla tego produktu.',
            bullets: [
              'Ślad bazowy traktuj jako aktualny model produktu, a nie punkt odniesienia rynkowego.',
              'Patrz jednocześnie na deltę i procentową zmianę, żeby rozumieć efekt absolutny i względny.',
              'Scenariuszy używaj do priorytetyzacji działań, a po decyzji aktualizuj właściwy model źródłowy.',
              'Jeśli któryś scenariusz prawie nie zmienia wyniku, to też jest cenna informacja, bo pokazuje gdzie nie warto zaczynać pracy.',
            ],
          },
          {
            title: 'Do czego przydaje się Monte Carlo',
            description:
              'Monte Carlo na tym ekranie pomaga zwizualizować zmienność wokół obecnego łącznego wyniku dla wybranego obszaru wpływu. To przydatny widok niepewności, kiedy trzeba pokazać, że wynik nie jest jedną idealnie precyzyjną liczbą, ale nie zastępuje weryfikacji założeń, zbiorów danych i logiki etapów.',
            bullets: [
              'Używaj go do komunikowania, że wynik jest estymacją, a nie idealnie dokładną liczbą.',
              'Nie traktuj go jako dowodu poprawności modelu, bo nadal zależy od jakości danych wejściowych.',
              'Jeśli decyzja zależy od jednego parametru, Eksplorator scenariuszy zwykle jest bardziej użyteczny niż samo Monte Carlo.',
              'Monte Carlo traktuj głównie jako wsparcie rozmowy o niepewności i zmienności, a nie jako samodzielne narzędzie wyboru wariantu operacyjnego.',
            ],
          },
          {
            title: 'Czego nie zastępuje Analityka',
            description:
              'Analityka wspiera interpretację i priorytetyzację, ale nie zastępuje zarządzania modelem. Nawet atrakcyjny wynik scenariusza nie oznacza jeszcze, że rekord produktu, założenia albo mapowanie etapów są poprawne.',
            bullets: [
              'Nie używaj Analityki zamiast sprawdzenia materiałów, transportów, ścieżki końca życia albo danych aktywności.',
              'Nie traktuj wyniku ze slidera jako zatwierdzonej zmiany produktowej, dopóki nie zaktualizujesz danych źródłowych.',
              'Nie porównuj scenariuszy liczonych na różnych kategoriach wpływu tak, jakby odpowiadały na to samo pytanie.',
              'Jeśli model pomija ważny etap albo opiera się na słabym zastępczym zbiorze danych, najpierw popraw model, a dopiero potem wyciągaj mocne wnioski.',
            ],
          },
          {
            title: 'Rekomendowany przepływ pracy dla analityka',
            description:
              'Praktyczny przepływ pracy wygląda tak: najpierw przejrzyj schemat przepływu i widok wpływu, potem otwórz Analitykę do przetestowania alternatyw, a dopiero na końcu zdecyduj, czy aktualizować rekord produktu albo schemat przepływu. Dzięki temu zespół nie zmienia danych źródłowych, zanim zrozumie, co naprawdę steruje wynikiem.',
            bullets: [
              'Najpierw potwierdź, które etapy dominują ślad w aktualnym modelu.',
              'Następnie przetestuj 2-3 realistyczne scenariusze zamiast wielu skrajnych ustawień suwaków.',
              'Na końcu zamień wybrany scenariusz na realną zmianę danych lub modelu, żeby ślad bazowy pozostał wiarygodny.',
              'Po aktualizacji modelu wróć do Analityki i sprawdź, czy oczekiwana redukcja nadal jest widoczna w nowym śladzie bazowym.',
            ],
          },
        ],
      },
      {
        badge: 'Paszport',
        description:
          'Tej sekcji używaj wtedy, gdy rezultatem ma być paszport produktowy pod DPP. Ekran po prawej pokazuje, jak platforma układa bloki, podgląd i kontrolę udostępniania.',
        tab: 'dpp',
        title: 'Jak działa rezultat paszportu',
        visualId: 'passport-guide',
        entries: [
          {
            title: 'Do czego służy kreator paszportu',
            description:
              'Kreator paszportu zamienia uporządkowane dane produktowe i LCA w widok DPP dla odbiorców. To warstwa publikacji, a nie miejsce do dopisywania brakujących treści.',
            bullets: [
              'Buduj sekcje paszportu z danych identyfikacyjnych, środowiskowych, materiałowych, trwałościowych, dotyczących zgodności i serwisowych zapisanych już w platformie.',
              'Przed publikacją sprawdź, które bloki powinny być widoczne dla danej grupy produktowej i odbiorcy zewnętrznego.',
              'Sygnały o brakujących polach traktuj jako problem danych źródłowych do poprawy w rekordzie, a nie coś do ręcznego łatania w rezultacie.',
            ],
          },
          {
            title: 'Podgląd, QR i udostępnianie',
            description:
              'Rezultat paszportu trzeba sprawdzić dokładnie w taki sposób, w jaki później będzie oglądany przez odbiorcę zewnętrznego.',
            bullets: [
              'Zweryfikuj widok paszportu na komputerze i urządzeniu mobilnym przed udostępnieniem na zewnątrz.',
              'QR i link udostępniania traktuj jako kontrolowany rezultat publikacyjny, a nie przestrzeń roboczą dla wersji roboczych.',
              'Upewnij się, że publiczny widok nie pokazuje notatek wewnętrznych ani dowodów z ograniczonym dostępem.',
            ],
          },
          {
            title: 'Zarządzanie aktualizacjami po publikacji',
            description:
              'Paszport powinien dać się aktualizować po publikacji, więc rekord produktu musi zachować czytelną ścieżkę weryfikacji i odpowiedzialności za zmiany.',
            bullets: [
              'Trzymaj źródłowe pliki PDF, okresy ważności i komentarze z weryfikacji w rekordzie produktu.',
              'Przeglądaj paszport po zmianie danych dostawcy, certyfikatów, wariantów produktu albo założeń o trwałości.',
              'Przypisz jednego właściciela do aktualizacji po publikacji i jakości publicznego rezultatu.',
            ],
          },
        ],
      },
      {
        badge: 'Raporty',
        description:
          'Tej sekcji używaj wtedy, gdy ten sam kręgosłup produktowy ma wygenerować formalny raport. Ekran po prawej pokazuje przebieg pracy kreatora raportów w platformie.',
        tab: 'dpp',
        title: 'Jak działa rezultat raportu',
        visualId: 'report-guide',
        entries: [
          {
            title: 'Do czego służy kreator raportów',
            description:
              'Kreator raportów składa studium produktowe w uporządkowany dokument wynikowy. Powinien wykorzystywać ten sam kontrolowany rekord produktu, zamiast duplikować treść poza platformą.',
            bullets: [
              'Traktuj generowanie raportu jako końcowy etap po weryfikacji danych produktowych, dowodów i logiki LCA.',
              'Wykorzystuj ten sam rekord produktu do zasilania różnych formatów raportowych zamiast odbudowywać tę samą narrację obok platformy.',
              'Pilnuj, żeby sekcje raportu były zgodne z rzeczywistym modelem, założeniami i ścieżką dowodową.',
            ],
          },
          {
            title: 'Raporty dla EPD, PEF i zakresu 3',
            description:
              'Jedna warstwa raportowa może obsługiwać kilka ścieżek raportowych, ale logika dokumentu wynikowego nadal musi być zgodna z docelowymi wymaganiami.',
            bullets: [
              'Pilnuj, żeby logika raportu była spójna z declared unit, zakresem, założeniami i metodą używaną w studium.',
              'Dowody gotowe pod raport, założenia tła i historia wersji powinny pozostać w tym samym rekordzie produktu.',
              'Pracuj na jednym kontrolowanym źródle referencyjnym, żeby zmiany w modelu mogły zasilać raporty bez podwójnej edycji.',
            ],
          },
          {
            title: 'Weryfikacja, eksport i wersjonowanie',
            description:
              'Raport ma wartość tylko wtedy, gdy wyeksportowany plik da się powiązać z konkretnym, przejrzanym stanem produktu i modelu.',
            bullets: [
              'Sprawdź kompletność sekcji przed eksportem, aby dokument odpowiadał zamierzonemu zakresowi.',
              'Eksportuj tylko z przejrzanej konfiguracji, szczególnie jeśli raport wychodzi poza organizację.',
              'Zachowaj jasno określoną odpowiedzialność za przyszłe aktualizacje raportu po zmianie danych, zakresu albo metodologii.',
            ],
          },
        ],
      },
      {
        badge: 'Operacje',
        description:
          'Większość organizacji nie przegrywa na metodologii, tylko na rozproszonym zbieraniu danych. Te kroki zamieniają platformę w model pracy.',
        tab: 'dpp',
        title: 'Jak zorganizować zbieranie danych produktowych w firmie',
        entries: [
          {
            title: 'Zacznij od jednego właściciela i jednego szablonu',
            description:
              'Przypisz jednego właściciela na produkt i zbuduj jeden powtarzalny schemat próśb dla zakładów, działu zakupów, inżynierii i jakości.',
            bullets: [
              'Nie wysyłaj różnych arkuszy kalkulacyjnych z różnych działów.',
              'Strukturę pól platformy wykorzystaj jako szablon zbierania danych.',
              'Najpierw pokaż, kto odpowiada za produkt, a dopiero potem pytaj dostawców o dane.',
            ],
          },
          {
            title: 'Zbieraj dane według etapów cyklu życia',
            description:
              'Proś o dane w kolejności: materiały, produkcja, logistyka, użytkowanie i koniec życia. To dokładnie odpowiada późniejszej logice modelu i weryfikacji.',
            bullets: [
              'Dział zakupów zwykle dostarcza najpierw dane o materiałach i kluczowych dostawcach.',
              'Zakłady dostarczają informacje o procesach, energii, odpadzie produkcyjnym i wydajności.',
              'Zespoły serwisowe albo jakościowe często najlepiej znają utrzymanie i założenia dotyczące trwałości.',
            ],
          },
          {
            title: 'Oddzielaj dowody od założeń',
            description:
              'Każda liczba powinna mieć status: zmierzona, zadeklarowana przez dostawcę albo oszacowana przez zespół projektowy.',
            bullets: [
              'Do plików i deklaracji używaj uploadów dowodowych.',
              'Jeśli liczba jest estymacją, zapisz kto ją oszacował i dlaczego.',
              'Taki podział bardzo przyspiesza późniejszą weryfikację i aktualizacje.',
            ],
          },
          {
            title: 'Buduj listę ulepszeń',
            description:
              'Nie czekaj na perfekcyjne dane, żeby zacząć. Lepiej utrzymaj dyscyplinę modelu i widoczną listę najważniejszych luk do domknięcia.',
            bullets: [
              'Najpierw zastępuj największe zastępcze zbiory danych.',
              'Priorytetyzuj dostawców lub zakłady dominujące masą, kosztem albo wpływem.',
              'Tę samą listę ulepszeń wykorzystuj do poprawy zarówno LCA, jak i gotowości do paszportu.',
            ],
          },
        ],
      },
      {
        badge: 'GS1',
        description:
          'Identyfikatory GS1 wspierają dane podstawowe produktu w platformie i są przydatne tam, gdzie rekord produktu musi dobrze łączyć się z innymi systemami, opakowaniem, etykietą albo dalszą wymianą danych.',
        tab: 'platform',
        title: 'Kody GS1, identyfikatory produktu i możliwość śledzenia',
        entries: [
          {
            title: 'GTIN: Global Trade Item Number',
            description:
              'GTIN to podstawowy identyfikator GS1 używany do identyfikacji produktów handlowych w łańcuchach dostaw, na etykietach, w katalogach i systemach cyfrowych.',
            bullets: [
              'Używaj GTIN wtedy, gdy produkt ma już nadany numer handlowy GS1.',
              'Przed importem sprawdzaj długość i cyfrę kontrolną.',
              'Po imporcie zawsze przeglądaj dane, bo GTIN opakowania i wariantu rynkowego może różnić się od oczekiwanego produktu.',
            ],
          },
          {
            title: 'GPC: Global Product Classification',
            description:
              'GPC to klasyfikacja GS1 grupująca produkty dla wymiany danych i kategoryzacji. Nie jest tym samym co CPC ani NACE.',
            bullets: [
              'GPC wspiera zastosowania handlowe i interoperacyjność.',
              'CPC zachowuj tam, gdzie potrzebna jest logika kategorii produktu do LCA lub raportowania.',
              'Nie próbuj jednym systemem kodów rozwiązać wszystkich problemów klasyfikacyjnych.',
            ],
          },
          {
            title: 'Model, numer partii i numer seryjny',
            description:
              'Te identyfikatory wspierają możliwość śledzenia poniżej poziomu rodziny produktu i są przydatne, gdy treść paszportu albo informacja serwisowa zmienia się po wariancie lub partii.',
            bullets: [
              'Model identyfikuje wersję albo konfigurację produktu.',
              'Numer partii wspiera identyfikację okresu lub partii produkcyjnej.',
              'Numer seryjny ma znaczenie, gdy potrzebna jest identyfikowalność na poziomie pojedynczej sztuki.',
            ],
          },
          {
            title: 'Gdzie GS1 pomaga najbardziej',
            description:
              'GS1 jest najbardziej przydatne tam, gdzie rezultaty muszą płynnie łączyć się z ERP, PLM, e-commerce, opakowaniem, etykietą albo ścieżką QR.',
            bullets: [
              'GS1 poprawia interoperacyjność i ogranicza ręczne mapowanie.',
              'Pomaga powiązać treść paszportu z etykietą fizyczną albo ścieżką QR.',
              'Buduje mocniejszy most między danymi zrównoważonego rozwoju a danymi podstawowymi produktu.',
            ],
          },
        ],
      },
      {
        badge: 'Zgodność',
        description:
          'Potraktuj ten obszar jako jeden wspólny kręgosłup danych produktowych i cztery ścieżki rezultatów: paszport produktowy pod ESPR, raport EPD z CPR, raport PEF oraz LCA zasilające zakres 3 w CSRD.',
        tab: 'regulations',
        title: 'Jak ułożyć 4 ścieżki regulacyjne i raportowe',
        entries: [
          {
            title: 'Jak to powinno wyglądać',
            description:
              'Najpierw budujesz jeden wspólny rekord produktu, a dopiero później rozdzielasz przepływ pracy tam, gdzie różnią się metodologia, weryfikacja albo zasady publikacji.',
            bullets: [
              'Wspólny kręgosłup: identyfikatory, hierarchia produktu, BOM, dowody od dostawców, dane produkcyjne, logistyka, geografia, założenia i historia wersji.',
              'Ścieżka 1: ESPR do paszportu produktowego dla identyfikacji, możliwości śledzenia, cyrkularności i kontrolowanego udostępniania danych.',
              'Ścieżka 2: EPD i CPR do raportu dla deklaracji produktowych opartych na uporządkowanym LCA i formalnym pakiecie dowodowym.',
              'Ścieżka 3: PEF do raportu tam, gdzie najważniejsza jest porównywalność i bardziej preskrypcyjna metodologia.',
              'Ścieżka 4: LCA do zakresu 3 CSRD tam, gdzie dane produktowe i dostawcze mają zasilać ujawnienia na poziomie spółki.',
            ],
          },
          {
            title: 'ESPR do paszportu produktowego',
            description:
              'Tej ścieżki używaj, gdy celem jest gotowość do DPP pod ESPR i utrzymanie zarządzalnego, aktualizowalnego zestawu danych paszportowych.',
            bullets: [
              'Dane: stabilne identyfikatory, atrybuty produktu i wariantu, dane producenta lub podmiotu gospodarczego, skład materiałowy, informacje serwisowe lub naprawcze oraz podpięte certyfikaty i dowody.',
              'Przebieg: zbuduj bazowy rekord produktu, powiąż warianty i identyfikatory, dołącz materiał dowodowy, a dopiero potem skonfiguruj bloki paszportu i reguły dostępu.',
              'Na co uważać: akty delegowane mogą zmieniać wymagany zakres danych w zależności od grupy produktowej, więc nie projektuj jednego sztywnego szablonu paszportu.',
              'Na co uważać: oddziel treści publiczne od poufnych danych dostawcy i dowodów zgodności przed publikacją.',
            ],
          },
          {
            title: 'EPD, CPR i raport',
            description:
              'Tej ścieżki używaj, gdy obronialne studium produktowe ma zakończyć się deklaracją typu EPD albo raportem wspierającym dokumentację w logice CPR.',
            bullets: [
              'Dane: declared unit lub jednostka funkcjonalna, BOM, dane o produkcji i energii, transport, opakowanie, odpady, założenia scenariuszowe oraz standardy albo reguły kategorii użyte w raporcie.',
              'Przebieg: najpierw potwierdź logikę deklaracji i zakres raportu, potem zbieraj dane zakładowe i dostawcze według etapów cyklu życia, zbuduj model w wymaganej strukturze modułów i przygotuj pakiet do weryfikacji oraz publikacji.',
              'Na co uważać: declared unit, granice systemu i logika modułów muszą być spójne od modelu do finalnego raportu, bo tu najczęściej powstaje rework.',
              'Na co uważać: okres danych, specyfika zakładu i jakość dowodów muszą odpowiadać temu, czego będzie oczekiwał weryfikator albo klient.',
            ],
          },
          {
            title: 'PEF i raport',
            description:
              'Tej ścieżki używaj, gdy kluczowa jest porównywalność, a studium ma podlegać bardziej preskrypcyjnej logice śladu środowiskowego.',
            bullets: [
              'Dane: reguły metody lub kategorii, wysokiej jakości dane pierwszoplanowe, dopasowane wtórne zbiory danych, reguły alokacji, założenia końca życia i opis ograniczeń jakości danych.',
              'Przebieg: zidentyfikuj właściwą logikę metody lub reguły kategorii, modeluj od początku pod te zasady porównywalności, jawnie przeglądaj wybory metodologiczne i dopiero na końcu generuj raport z ujawnionymi założeniami oraz ograniczeniami.',
              'Na co uważać: nie mieszaj wstępnego LCA z roszczeniami o porównywalności w logice PEF, bo dyscyplina metody musi być widoczna od pierwszej decyzji modelowej.',
              'Na co uważać: alokacja, dane wtórne i end-of-life wymagają jawnego uzasadnienia, bo silnie wpływają na porównywalność.',
            ],
          },
          {
            title: 'LCA do zakresu 3 CSRD',
            description:
              'Tej ścieżki używaj, gdy dane produktowe i dostawcze mają zasilać kalkulacje zakresu 3 oraz ujawnienia CSRD na poziomie całej spółki.',
            bullets: [
              'Dane: mapowanie zakupów albo aktywności, geografia dostawcy i zakładu, wolumeny, produktowe LCA lub współczynniki emisji, pokrycie roku raportowego i notatki o niepewności.',
              'Przebieg: zmapuj produkty i zakupy do właściwych kategorii zakresu 3, zbierz dane pierwotne dla hotspotów, pozostałe luki domknij udokumentowanymi przybliżeniami, a potem agreguj i uzgadniaj dane pod raportowanie.',
              'Na co uważać: studia produktowe i ujawnienia spółkowe mają inne granice, więc trzeba pilnować podwójnego liczenia i uzgodnienia z danymi finansowymi albo zakupowymi.',
              'Na co uważać: pokrycie roku, logika konsolidacji i jakość przybliżeń muszą być transparentne, bo w CSRD możliwość śledzenia i spójność będą szczególnie widoczne.',
            ],
          },
        ],
      },
      ...ECOINVENT_GUIDE_SECTIONS_PL,
    ],
    guideTitle: 'Przewodnik po polach i sekcjach',
    heroDescription:
      'Traktuj to centrum wiedzy jako operacyjną warstwę platformy: zrozum, do czego służy każdy formularz, jak zbierać dane produktowe w organizacji, jaką rolę pełnią GS1 i ecoinvent oraz jak łączą się LCA, DPP i regulacje.',
    heroTitle:
      'Zamień dane produktowe w użyteczny przepływ pracy LCA i paszport produktowy',
    knowledgeCards: [
      {
        bullets: [
          'Najpierw zbuduj rekord produktu, a dopiero potem myśl o raporcie.',
          'Od początku pilnuj identyfikatorów, kontekstu producenta i zamierzonego zastosowania.',
          'Traktuj platformę jako kontrolowany system danych i dowodów o produkcie, a nie magazyn plików.',
        ],
        status: 'Platforma',
        summary:
          'Dobre wdrożenie zaczyna się od uporządkowanych danych podstawowych i jasno określonej odpowiedzialności, a nie od końcowego PDF-a.',
        tab: 'platform',
        title: 'Jak podejść do platformy jako systemu pracy',
      },
      {
        bullets: [
          'Jednostka funkcjonalna, granice i dowody decydują o tym, czy LCA da się obronić.',
          'Dane pierwotne są najmocniejsze tam, gdzie produkt jest specyficzny i materialny środowiskowo.',
          'Dane wtórne powinny być widoczne i możliwe do zweryfikowania, a nie ukryte w modelu.',
        ],
        status: 'LCA',
        summary:
          'Platforma odwzorowuje realną pracę LCA: najpierw zakres, potem analiza inwentarza, a na końcu interpretacja.',
        tab: 'lca',
        title: 'Jak wygląda dobre LCA wewnątrz platformy',
      },
      {
        bullets: [
          'Wejdź w zakładkę LCA w Centrum pomocy i przewiń do sekcji Przewodnik po polach i sekcjach.',
          'Szukaj akordeonu Jak korzystać z funkcji analitycznych.',
          'Eksplorator scenariuszy traktuj jako narzędzie do praktycznych symulacji wariantowych, a Monte Carlo jako widok wspierający interpretację niepewności.',
        ],
        status: 'Analityka LCA',
        summary:
          'W Centrum pomocy jest osobny przewodnik do zakładki Analityka, obejmujący Eksplorator scenariuszy i Monte Carlo.',
        tab: 'lca',
        title: 'Gdzie znaleźć przewodnik do Analityki',
      },
      {
        bullets: [
          'Ten obszar służy do generowania kontrolowanych rezultatów z jednego rekordu produktu.',
          'Paszporty wspierają DPP, a raporty wspierają EPD, ślad środowiskowy produktu i pracę nad zakresem 3.',
          'Jakość rezultatu zależy od kompletności danych źródłowych, dowodów i wersjonowania, a nie tylko od końcowego układu.',
        ],
        status: 'DPP',
        summary:
          'Warstwa rezultatów powinna generować zarówno paszporty, jak i raporty z tego samego kontrolowanego kręgosłupa danych produktowych.',
        tab: 'dpp',
        title: 'Jak jeden rekord produktu zasila paszporty i raporty',
      },
      {
        bullets: [
          'Zbuduj jeden wspólny kręgosłup danych produktowych, a potem rozdziel go na 4 ścieżki zamiast prowadzić 4 osobne projekty.',
          'Każda ścieżka wymaga innego rezultatu, ale tej samej dyscypliny wokół identyfikatorów, dowodów, logiki LCA i wersjonowania.',
          'Największa oszczędność pracy pojawia się wtedy, gdy od początku rozdzielisz dane wspólne od wymagań specyficznych dla danej ścieżki.',
        ],
        status: 'Regulacje',
        summary:
          'Najmocniejszy model operacyjny to jeden rekord produktu obsługujący ESPR/DPP, EPD z CPR, PEF i zakres 3 w CSRD.',
        tab: 'regulations',
        title: 'Jak zamienić wymagania zgodności na 4 zarządzalne ścieżki',
      },
      ...ECOINVENT_KNOWLEDGE_CARDS_PL,
    ],
    videoInstructionsLabel: 'Instrukcja wideo',
    noMatchingCardsDescription:
      'Spróbuj innego słowa kluczowego albo przejdź do innego obszaru.',
    noMatchingCardsTitle: 'Brak pasujących kart przeglądowych',
    noMatchingQuestionsLabel:
      'Brak pasujących pytań dla wybranego obszaru i słowa kluczowego.',
    officialResourceButtonLabel: 'Otwórz źródło',
    officialResources: [
      {
        label: 'ecoinvent',
        summary:
          'Oficjalny punkt wejścia do bazy ecoinvent i jej kontekstu metodologicznego.',
        url: 'https://ecoinvent.org/',
      },
      {
        label: 'Standardy GS1',
        summary:
          'Przegląd standardów GS1 i identyfikatorów istotnych dla danych podstawowych produktu i interoperacyjności.',
        url: 'https://www.gs1.org/standards',
      },
      {
        label: 'Metoda PEF',
        summary:
          'Oficjalny przegląd śladu środowiskowego produktu oraz reguł kategorii produktowych od Komisji Europejskiej.',
        url: 'https://green-forum.ec.europa.eu/green-business/environmental-footprint-methods/pef-method_en',
      },
      {
        label: 'ESPR i DPP',
        summary:
          'Strona Komisji Europejskiej opisująca rozporządzenie w sprawie ekoprojektu dla zrównoważonych produktów i kontekst cyfrowego paszportu produktu.',
        url: 'https://commission.europa.eu/energy-climate-change-environment/standards-tools-and-labels/products-labelling-rules-and-requirements/ecodesign-sustainable-products-regulation_en',
      },
      {
        label: 'CSRD i ESRS',
        summary:
          'Wyjaśnienie obowiązków raportowych zrównoważonego rozwoju i standardów raportowania od Komisji Europejskiej.',
        url: 'https://finance.ec.europa.eu/financial-markets/company-reporting-and-auditing/company-reporting/corporate-sustainability-reporting_en',
      },
      {
        label: 'CBAM',
        summary:
          'Oficjalny portal Komisji Europejskiej dotyczący Carbon Border Adjustment Mechanism.',
        url: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en',
      },
      ...ECOINVENT_OFFICIAL_RESOURCES_PL,
    ],
    officialResourcesTitle: 'Źródła pierwotne, które warto podpiąć zespołowi',
    quickActions: [
      {
        description:
          'Jak uzupełniać platformę, co oznaczają grupy pól i jak nie zgubić ścieżki dowodowej.',
        id: 'platform',
        title: 'Przewodnik po platformie',
      },
      {
        description:
          'Czym jest LCA, jak definiować zakres i jak pracować z danymi produktu oraz procesu.',
        id: 'lca',
        title: 'Podstawy LCA',
      },
      {
        description:
          'Jak wejść do ecoinvent z poziomu platformy Envirly LCA, weryfikować wybór zbiorów danych i porównywać je z ecoQuery.',
        id: 'ecoinvent',
        title: 'Przewodnik po bazie Ecoinvent',
      },
      {
        description:
          'Jak platforma generuje paszporty dla DPP oraz raporty dla EPD, PEF i zakresu 3.',
        id: 'dpp',
        title: 'Paszporty i raporty',
      },
      {
        description:
          'Jak jeden kręgosłup danych produktowych obsługuje 4 ścieżki: ESPR/DPP, EPD z CPR, raport PEF i LCA do zakresu 3 CSRD.',
        id: 'regulations',
        title: 'Radar regulacyjny',
      },
    ],
    quickActionsTitle: 'Wybierz obszar, nad którym teraz pracujesz',
    searchPlaceholder:
      'Szukaj po polach, GTIN, GPC, ecoinvent, jednostce funkcjonalnej, dostawcach...',
    searchNoExactMatchDescription:
      'Nie znaleźliśmy dokładnego dopasowania dla tej frazy w bazie wiedzy. Skorzystaj z najbliższych podobnych tematów poniżej.',
    searchNoExactMatchTitle: 'Nie znaleziono dokładnego dopasowania',
    searchResultsLabel: 'pasujących elementów instruktażowych',
    searchSuggestionOpenLabel: 'Otwórz temat',
    searchSuggestionsLabel: 'Najbliższe podobne tematy',
    stats: [
      'Instrukcje na poziomie pól',
      'GS1 i ecoinvent wyjaśnione',
      'Przepływ pracy zbierania danych między zespołami',
    ],
    tabs: {
      platform: 'Platforma',
      lca: 'LCA',
      ecoinvent: 'Ecoinvent',
      dpp: 'Paszporty i raporty',
      regulations: 'Regulacje',
    },
    workflowDescription:
      'Użyj tego jako rekomendowanej sekwencji wdrożenia produktu do platformy i zorganizowania zbierania danych w organizacji.',
    workflowEmptyDescription:
      'Żaden krok przepływu pracy nie pasuje do obecnego słowa kluczowego. Spróbuj szerszego hasła, np. materiał, dostawca albo paszport.',
    workflowEmptyTitle: 'Brak pasujących kroków przepływu pracy',
    workflowOutputLabel: 'Oczekiwane rezultaty',
    workflowOwnerLabel: 'Typowy właściciel',
    workflowSteps: [
      {
        owner: 'właściciel produktu / lider ds. zrównoważonego rozwoju',
        outputs: [
          'Przypisany właściciel produktu',
          'Uzgodniony cel studium',
          'Rekomendowane priorytetowe ramy działania',
        ],
        summary:
          'Zacznij od ustalenia, po co istnieje rekord produktu, kto za niego odpowiada oraz czy jego celem jest ecodesign, wymaganie klienta, przygotowanie do DPP czy raportowanie zewnętrzne.',
        tab: 'platform',
        title: '1. Uruchom rekord produktu',
      },
      {
        owner: 'właściciel produktu / zespół danych podstawowych',
        outputs: [
          'Uzupełniona nazwa i opis',
          'Wybrane CPC i NACE',
          'Zweryfikowany GTIN i dane producenta',
        ],
        summary:
          'Najpierw domknij podstawowy rekord produktu, aby wszystkie późniejsze prośby odnosiły się do jednego stabilnego obiektu danych podstawowych.',
        tab: 'platform',
        title: '2. Zablokuj podstawowe identyfikatory i klasyfikacje',
      },
      {
        owner: 'zakupy / inżynieria',
        outputs: [
          'Wstępna lista materiałów',
          'Zidentyfikowani kluczowi dostawcy',
          'Zebrane krytyczne certyfikaty',
        ],
        summary:
          'Zbierz pierwszy BOM, mapę dostawców i dokumentację, która zakotwiczy model oraz pakiet dowodowy.',
        tab: 'platform',
        title: '3. Zbierz materiał dowodowy dla wcześniejszych etapów łańcucha',
      },
      {
        owner: 'zakład / operacje / analityk LCA',
        outputs: [
          'Opisany proces produkcji',
          'Potwierdzona geografia produkcji',
          'Ocenione pokrycie danych pierwotnych',
        ],
        summary:
          'Zbierz informacje o procesach, energii, wydajnościach, logistyce i lokalizacji w tej samej strukturze, w której później zbudujesz LCA.',
        tab: 'lca',
        title: '4. Zbuduj kręgosłup analizy inwentarza',
      },
      {
        owner: 'analityk LCA',
        outputs: [
          'Zdefiniowana jednostka funkcjonalna',
          'Udokumentowane założenia',
          'Uzasadnione wtórne zbiory danych',
        ],
        summary:
          'Zamień surowe dane operacyjne w obroniony model poprzez zapisanie założeń, granic oraz logiki wyboru ecoinventu albo innych zastępczych zbiorów danych.',
        tab: 'lca',
        title: '5. Modeluj, waliduj i interpretuj',
      },
      ...ECOINVENT_WORKFLOW_STEPS_PL,
      {
        owner: 'zespół produktu cyfrowego / lider ds. zgodności',
        outputs: [
          'Przejrzany rezultat paszportu albo raportu',
          'Zweryfikowany widok udostępniania albo eksportowany dokument wynikowy',
          'Przypisany właściciel aktualizacji',
        ],
        summary:
          'Dopiero po uporządkowaniu danych źródłowych skonfiguruj rezultat paszportu albo raportu i sprawdź, czy publikowany lub eksportowany materiał jest kompletny, możliwy do zweryfikowania i adekwatny dla odbiorcy.',
        tab: 'dpp',
        title: '6. Opublikuj paszporty i raporty',
      },
      {
        owner: 'zespół ds. zgodności / zespół ds. zrównoważonego rozwoju',
        outputs: [
          'Zachowane linki do dowodów',
          'Zmapowane ramy regulacyjne',
          'Uporządkowana lista ulepszeń',
        ],
        summary:
          'Utrzymuj jedną warstwę dowodową dla regulacji, próśb klientów i kolejnych aktualizacji zamiast odtwarzać dane od zera przy każdym projekcie.',
        tab: 'regulations',
        title: '7. Wykorzystuj rekord produktu między ramami regulacyjnymi',
      },
    ],
    workflowTitle: 'Rekomendowana sekwencja wdrożenia',
  },
  sv: {
    badge: 'Hjälpcenter',
    breadcrumbs: [
      {
        name: 'Produktlista',
        url: '/lca/products',
      },
      {
        name: 'Hjälpcenter',
        url: '/lca/help-centre',
      },
    ],
    checklistItems: [
      {
        id: 'owner',
        label: 'Tilldela en produktdataägare och granskare',
      },
      {
        id: 'scope',
        label: 'Kom överens om LCA:s omfattning, funktionella enhet och metod',
      },
      {
        id: 'primary',
        label: 'Samla in primärdata från anläggningar och leverantörer',
      },
      {
        id: 'secondary',
        label: 'Kartlägg luckor till sekundära datamängder och antaganden',
      },
      {
        id: 'evidence',
        label: 'Bifoga bevis, certifikat och källfiler',
      },
      {
        id: 'passport',
        label: 'Förbered produktdata för DPP och dela flöden',
      },
    ],
    checklistLabel: 'Operationell checklista',
    checklistProgressLabel: 'komplett',
    checklistTitle: 'Vad en distributionsfärdig produktpost ska innehålla',
    disclaimer:
      'Denna hub är avsedd för dagligt utförande och onboarding. Det stöder efterlevnadsarbete men ersätter inte formell juridisk rådgivning.',
    faqTitle: 'Vanliga frågor',
    faqs: [
      {
        question: 'Vad ska användarna göra först i plattformen?',
        answer:
          'Börja med produktposten, identifierare, producentsammanhang och avsedd användning. Definiera sedan LCA-omfattning, samla in material och processdata, och gå först därefter till pass eller rapportering.',
        tab: 'platform',
      },
      {
        question: 'Varför väljs ecoinvent vid produktskapandet?',
        answer:
          'Eftersom databasversionen är en del av modelleringslogiken. Genom att hålla versionen stabil för en produkt undviks tysta ändringar i bakgrundsdatauppsättningar mellan iterationerna.',
        tab: 'lca',
      },
      {
        question: 'Är GTIN obligatoriskt för varje produkt?',
        answer:
          'Inte alltid, men det är en stark identifierare närhelst organisationen använder GS1. Det blir särskilt värdefullt för digitala pass, produktbasdata, förpackning och nedströms interoperabilitet.',
        tab: 'platform',
      },
      {
        question: 'Hur ska vi samla in data mellan teamen?',
        answer:
          'Använd en ägare per produkt, definiera en mall för databegäran efter livscykelstadium, fråga anläggningar och inköp om primärdata och håll antaganden explicita när exakta värden inte är tillgängliga.',
        tab: 'platform',
      },
      {
        question: 'Vad är skillnaden mellan CPC, NACE och GPC?',
        answer:
          'CPC klassificerar produkter, NACE klassificerar ekonomisk aktivitet och GPC är en GS1-produktklassificering som används vid utbyte av handelsdata. De löser olika kartläggningsproblem och kan samexistera på samma produkt.',
        tab: 'platform',
      },
      {
        question:
          'Vad gör en LCA tillräckligt trovärdig för extern användning?',
        answer:
          'Ett tydligt mål och omfattning, funktionell enhet, transparenta gränser, dokumenterade datakällor, explicita antaganden, sammanhängande datauppsättningsval och bevis som kan granskas senare.',
        tab: 'lca',
      },
      {
        question: 'Ersätter passbyggaren LCA-arbetsflödet?',
        answer:
          'Nej. Byggaren är ett utdata- och kommunikationslager. De underliggande produkt- och LCA-data måste fortfarande struktureras korrekt i produktposten först.',
        tab: 'dpp',
      },
      {
        question: 'Kan en produktpost stödja flera regler?',
        answer:
          'Ja, om basdata, bevis, miljödata och identifierare upprätthålls i en disciplinerad verksamhetsmodell. Det är anledningen till att detta hjälpcenter länkar samman plattformsarbete, LCA, DPP och regleringsämnen.',
        tab: 'regulations',
      },
      {
        question:
          'När ska en analytiker välja en marknadsdatauppsättning istället för en produktionsdatauppsättning?',
        answer:
          'Välj en datauppsättning av produktionstyp när du modellerar hur en produkt eller intermediär tillverkas av en specifik process. Välj en marknadsdatauppsättning när du behöver den genomsnittliga utbudsmixen som är tillgänglig för en köpare i en geografi, inklusive inhemsk produktion och import som modellerats av ecoinvent. En marknadsdatauppsättning är vanligtvis det bättre valet för inköpta insatser när du inte vet exakt producentvägen. En produktionsdatauppsättning är vanligtvis det bättre valet när du känner till tillverkningsvägen, teknologin och geografin och vill modellera den rutten explicit.',
        tab: 'ecoinvent',
      },
      {
        question: 'Hur ska geografi väljas mellan land, region, RoW och GLO?',
        answer:
          'Börja med den geografi som bäst matchar där aktiviteten fysiskt sker. Föredra landsspecifika datauppsättningar när leveranskedjan är landsspecifik och väsentlig för resultaten. Använd regionala datauppsättningar som RER när utbudsbasen verkligen är regional eller när det inte finns någon robust landsdatauppsättning. Använd RoW endast när aktiviteten tydligt representerar en icke-specificerad övriga världens mix, och använd endast GLO när en global marknad eller globalt genomsnittligt utbud är det avsedda modellvalet. Globalt är inte automatiskt säkrare; det kan späda på en annars relevant nationell eller regional signal.',
        tab: 'ecoinvent',
      },
      {
        question:
          'Varför kan Envirly LCA-plattformen och ecoQuery visa olika resultat för det som ser ut som samma aktivitet?',
        answer:
          'De första kontrollerna är konsistensen av ecoinvent-version, systemmodell och vald geografi. I Envirly LCA-plattformen är produktversionen fixerad vid produktskapandet och ecoinvent nås från plattformen, med en länk ut till ecoQuery i cut-off systemmodellen. Om en användare jämför det med en annan version, APOS, följd eller en aktivitet i en annan geografi, förväntas skillnader. Resultaten kan också skilja sig åt när plattformen använder en grupperad aktivitet och användaren senare väljer en annan geografivariant än den som inspekteras i ecoQuery.',
        tab: 'ecoinvent',
      },
      {
        question:
          'Vad ska göras när det inte finns någon exakt ecoinvent-matchning?',
        answer:
          'Använd en dokumenterad proxy snarare än en ytligt liknande men metodologiskt svag matchning. Fullmakten bör bevara huvudfunktionen, fysisk form, enhetslogik, tekniknivå och geografi så långt det är rimligt möjligt. Anteckna proxyrationalen i antaganden eller kommentarer så att en granskare kan förstå varför den valdes, vad den står för och var primärdata skulle behövas för att förbättra modellen senare.',
        tab: 'ecoinvent',
      },
      {
        question:
          'Hur ska en expert tolka RoW, RER, marknadsgrupp och behandlingsetiketter?',
        answer:
          'Dessa etiketter är modelleringssignaler, inte bara namngivningsbrus. RoW betyder resten av världen och bör läsas som en restgeografi, inte en generisk standard. RER representerar Europa i ecoinvent geografilogik och kan skilja sig väsentligt från en datauppsättning för ett land. Marknadsgruppsdatauppsättningar aggregerar utbud bortom en marknadsgräns och kan skilja sig från en direkt marknadsdatauppsättning i sammansättning och avsedd tolkning. Behandlingsdatauppsättningar modellerar avfallsbehandling eller uttjänta verksamheter och bör inte användas som vanliga produktionsfullmakter för material eller komponenter.',
        tab: 'ecoinvent',
      },
      {
        question:
          'När är det bättre att använda en anpassad faktor i Envirly LCA-plattformen istället för en ecoinvent-datauppsättning?',
        answer:
          'Använd en anpassad faktor när den relevanta processen är företagsspecifik och den tillgängliga ecoinvent-proxyn skulle dölja en väsentlig teknologiskillnad, när en leverantör tillhandahåller granskningsbara primära effektdata som är metodologiskt anpassade till studien, eller när det inte finns någon trovärdig ecoinvent-proxy för det avsedda modelleringsobjektet. En anpassad faktor bör fortfarande dokumenteras med begränsningar för källa, omfattning, enhet och kvalitet eftersom ett anpassat värde utan spårbarhet är svagare än en välmotiverad sekundär datauppsättning.',
        tab: 'ecoinvent',
      },
    ],
    flowchartAreaLabel: 'Inverkansområde',
    flowchartBoundaryLabel: 'exempel från vagga till grav',
    flowchartCanvasHint:
      'Exempelskärm: läs stadier från vänster till höger och placera objekt under livscykelfasen där de faktiskt inträffar.',
    flowchartCanvasTitle: 'Exempel på flödesschemaskärm',
    flowchartDescription:
      'Flödesschemat är där LCA slutar vara abstrakt. Det hjälper användare att bestämma vilka livscykelfaser som omfattas, vilka processer som hör till varje fas och var material, transport, förpackning eller uttjänta vägar ska placeras.',
    flowchartExampleStages: [
      {
        title: 'Utvinning och bearbetning av råvaror',
        items: ['Aluminiumplåt', 'Återvunnet polymergranulat'],
      },
      {
        title: 'Transport av råvaror',
        items: ['Lastbil från leverantör', 'Inkommande palltransport'],
      },
      {
        title: 'Produktion & förpackning',
        items: ['Skärning och formning', 'Montering och butikslåda'],
      },
      {
        title: 'Distribution & lagring',
        items: ['Distributionscentralförråd', 'Utgående transport'],
      },
      {
        title: 'Installation',
        items: ['Installation på plats', 'Installatörens resa'],
      },
      {
        title: 'Använd scenen',
        items: ['El i bruk', 'Underhåll / reservdel'],
      },
      {
        title: 'Slutet på livet',
        items: ['Demontering', 'Återvinning och restavfall'],
      },
    ],
    flowchartExampleTitle: 'Hur man tänker kring att bygga flödesschemat',
    flowchartFilterByStageLabel: 'Filtrera efter steg',
    flowchartGroupCradleLabel: 'VAGGA',
    flowchartGroupGraveLabel: 'GRAV',
    flowchartLegendLabel: 'Vad ska man placera på diagrammet',
    flowchartLegendItems: [
      'Skapa en låda för varje material, transportsträcka, process eller uttjänt väg som väsentligt påverkar resultatet.',
      'Placera rutan i det skede där aktiviteten fysiskt sker, inte där fakturan sitter.',
      'Håll processnamn korta och specifika så att en annan analytiker kan förstå modellen utan att öppna ett kalkylblad.',
    ],
    flowchartNoObjectsLabel: 'Inga objekt har lagts till ännu',
    flowchartStepLabel: 'Bygg logik',
    flowchartSteps: [
      {
        title: '1. Välj först systemgränsen',
        description:
          'Bestäm om produkten ska modelleras från vagga till grind, vagga till grav eller med annan gräns innan du lägger till detaljerade objekt.',
        bullets: [
          'Boundary bestämmer vilka kolumner som ska visas i flödesschemat.',
          'Använd målet med studien och rapporteringskravet för att motivera valet.',
          'Skriv ner uteslutningar tidigt så att de inte försvinner i senare antaganden.',
        ],
      },
      {
        title: '2. Lägg till huvudprocesserna före detaljerade inmatningar',
        description:
          'Utgå från värdekedjan på hög nivå och skapa den huvudsakliga processstrukturen först. Efter det, bifoga material, transporter och energikrävande aktiviteter.',
        bullets: [
          'Tänk i processer som gjutning, montering, lagerhållning eller återvinning.',
          'Börja inte med dussintals små noder innan scenens logik är klar.',
          'Använd en huvudbox per meningsfull transformation eller rutt.',
        ],
      },
      {
        title: '3. Kartlägg data och datauppsättningar till rätt stadium',
        description:
          'Varje objekt bör backas upp antingen av primärdata, en leverantörsdeklaration eller en sekundär datauppsättning som ecoinvent.',
        bullets: [
          'Material sitter oftast i råvaror eller produktion beroende på vad som representeras.',
          'Transportbenen bör vara åtskilda när geografi eller läge är viktigt.',
          'Använd kommentarer eller bifogade flödesschemafiler när den visuella modellen behöver extra sammanhang.',
        ],
      },
      {
        title: '4. Se till att diagrammet kan granskas',
        description:
          'Ett användbart flödesschema är inte det mest detaljerade. Det är den en annan person kan granska och koppla tillbaka till antaganden, produktfält och källfiler.',
        bullets: [
          'Slå samman triviala noder när de inte ändrar tolkning.',
          'Namnrutor så att de matchar bevispaketet och uppladdad dokumentation.',
          'Granska om diagrammet berättar en konsekvent historia från material till livets slut.',
        ],
      },
    ],
    flowchartSummaryLabel: 'Sammanfattning',
    flowchartSummaryValue: '42,3',
    flowchartSummaryUnit: 'kg CO2e',
    flowchartTabs: {
      analytics: 'Analys',
      flowchart: 'Flödesschema',
      impact: 'Inverkan',
    },
    flowchartTitle: 'Hur man bygger LCA-flödesschemat',
    guideDescription:
      'Dessa avsnitt förklarar vad varje fältgrupp är till för, hur man fyller i den och hur bra bevis ser ut i praktiken.',
    guideEmptyDescription:
      'Prova ett annat sökord eller byt till en annan arbetsström för att se mer detaljerad vägledning.',
    guideEmptyTitle: 'Inga matchande vägledningsavsnitt',
    guideSections: [
      {
        badge: 'Fält',
        description:
          'Den inledande produktposten är där masterdatakvaliteten avgörs. Dessa fält driver kategorisering, urval av datauppsättningar och passkonsistens nedströms.',
        tab: 'platform',
        title: 'Produktrekord och affärssammanhang',
        entries: [
          {
            title: 'Produktnamn och beskrivning',
            description:
              'Skriv det kommersiella eller interna produktnamnet och en beskrivning som hjälper en granskare att förstå funktion, sammansättning och avsedd marknadsanvändning.',
            bullets: [
              'Använd samma namnlogik som ERP eller PLM där det är möjligt.',
              'Beskriv vad produkten gör, inte bara vad marknadsföringen kallar den.',
              'Undvik vag text som "högkvalitativ produkt" utan operativ betydelse.',
            ],
          },
          {
            title: 'Ecoinvent version',
            description:
              'Välj bakgrundsdatabasversionen vid produktskapandet och håll den stabil över modelleringsuppdateringar om det inte sker en avsiktlig metodisk förändring.',
            bullets: [
              'En versionsändring kan ändra bakgrundsdatauppsättningar och resultat.',
              'Använd den senaste godkända versionen endast när projektreglerna tillåter det.',
              'Dokumentera orsaken när du byter version mellan studier.',
            ],
          },
          {
            title: 'Produktkategori, bransch och regelverk',
            description:
              'CPC hjälper till att klassificera produkten, NACE kartlägger det huvudsakliga branschens sammanhang och regelverkstaggar visar vilken rapporterings- eller efterlevnadslogik som bör beaktas från början.',
            bullets: [
              'Välj CPC för själva produkten, inte för hela företaget.',
              'Välj NACE enligt den aktivitet som skapar värdekedjans sammanhang.',
              'Använd ramtaggar för att signalera om EPD, DPP eller produkters koldioxidavtryck förväntas.',
            ],
          },
          {
            title: 'GTIN / produktkod och producentuppgifter',
            description:
              'Produktkodfält förankrar posten till produktbasdata. Producentnamn, adress och webbplats bör identifiera den juridiska eller operativa enheten bakom produkten.',
            bullets: [
              'Om GTIN finns, behåll endast den numeriska koden och validera den före import.',
              'Använd producentuppgifter som senare kan visas på ett pass eller dokument som ställer krav på efterlevnad.',
              'Håll namngivningen i linje med etiketter, tekniska filer eller andra källsystem.',
            ],
          },
        ],
      },
      {
        badge: 'Fält',
        description:
          'Det här avsnittet hjälper användare att ladda upp bevis, förklara avsedd användning och komplettera affärsinformation som senare dyker upp i pass- eller revisionssammanhang.',
        tab: 'platform',
        title: 'Ytterligare information och bevisfält',
        entries: [
          {
            title: 'Utvecklingsflödesscheman och teknisk dokumentation',
            description:
              'Använd dessa uppladdningar för processdiagram, monteringskartor, tekniska filer, designritningar eller tekniska anteckningar som hjälper till att förklara hur produkten är tillverkad.',
            bullets: [
              'Ladda upp bevis som stöder tillverkningslogik eller tolkning av stycklistor.',
              'Föredrar filer med tydlig versionshantering och källägande.',
              'Beskriv vad filen bevisar i det medföljande textfältet.',
            ],
          },
          {
            title: 'Avsedd användning, produkttyp och ytterligare information',
            description:
              'Förklara hur produkten faktiskt används, om det är en fysisk produkt eller tjänst, och alla sammanhang som en modellerare eller granskare bör känna till innan LCA bygger.',
            bullets: [
              'Använd avsedd användning för att definiera realistiska antaganden för användningsfas och livslängd.',
              'Välj produkttyp noggrant eftersom etiketter och fältförväntningar ändras för tjänster.',
              'Lagra sammanhang här när det inte passar i strukturerade fält men ändå har betydelse för tolkning.',
            ],
          },
          {
            title:
              'Tillverkningsdatum, garanti, CE-märkning och importerade GS1-data',
            description:
              'Dessa områden stödjer spårbarhet och digital produktkommunikation nedströms. När GTIN-import är tillgänglig, validera koden först och granska sedan importerade värden innan du sparar.',
            bullets: [
              'Använd produktionsdatum när batch- eller periodspårbarhet är viktig.',
              'Beskriv garanti och villkor på ett sätt som är kundinriktat och kan granskas.',
              'Märk CE endast när produkten faktiskt är CE-märkt enligt gällande regler.',
            ],
          },
          {
            title: 'GPC och relaterade identifierare',
            description:
              'Om GTIN-import fyller i GPC eller relaterade fält, kontrollera att klassificeringen matchar den faktiska produkten som släpps ut på marknaden.',
            bullets: [
              'GS1-data kan påskynda installationen men bör fortfarande granskas av en produktägare.',
              'Behandla inte importerade fält som automatiskt korrekta för varje marknadsvariant.',
              'Eskalera oöverensstämmelse mellan GTIN, etikettdata och teknisk dokumentation.',
            ],
          },
        ],
      },
      {
        badge: 'Fält',
        description:
          'Geografi och leverantörsfält är väsentliga för primär datainsamling, spårbarhet och senare förklaringar av uppströmsantaganden.',
        tab: 'platform',
        title: 'Producent, geografi, leverantörer och stödjande register',
        entries: [
          {
            title: 'Producent, plats och distributionsfält',
            description:
              'Avgränsa vem som ansvarar för produkten varifrån produktionen sker och var produkten distribueras.',
            bullets: [
              'Använd producentfält för enheten bakom produkten.',
              'Använd produktionsland och produktionsadress för tillverkningsgeografi.',
              'Använd distributionsregioner för att visa var produkten säljs eller levereras.',
            ],
          },
          {
            title: 'Kort beskrivning av producentverksamheten',
            description:
              'Detta fält bör förklara producentens kärnverksamhet i ett operativt stycke så att granskarna förstår det industriella sammanhanget.',
            bullets: [
              'Nämn tillverkningsrollen eller levererad tjänst.',
              'Använd formuleringar som hjälper upphandling eller revisorer att snabbt identifiera verksamheten.',
              'Håll det sakligt och kortfattat snarare än reklam.',
            ],
          },
          {
            title: 'Nyckelleverantörer',
            description:
              'Lista de leverantörer som väsentligt påverkar produktens fotavtryck, sammansättning eller bevispaket.',
            bullets: [
              'Fånga leverantörens namn, adress, levererad vara och en kontaktperson när det är möjligt.',
              'Prioritera leverantörer kopplade till kärnmaterial, kritiska processer eller efterlevnadskänsliga insatser.',
              'Använd samma leverantörsnamn som i upphandlingssystem för att undvika dubbletter.',
            ],
          },
          {
            title: 'Tekniska specifikationer och certifikat',
            description:
              'Tekniska specifikationer ska fånga mätbara produktparametrar, medan certifikat ska fånga kontrollerade dokument med status och giltighetsperioder.',
            bullets: [
              'Använd en specifikationsrad per måttenhet, till exempel kapacitet, spänning eller belastningsområde.',
              'För certifikat, inkludera dokumenttyp, omfattning, giltighetsdatum och om dokumentet är miljömässigt.',
              'Bifoga PDF-filen närhelst filen finns så att bevis inte separeras från dokumentet.',
            ],
          },
        ],
      },
      {
        badge: 'Arbetsflöde',
        description:
          'Mål och inställningar för avslöjande definierar varför studien finns, vem som ska använda den och vilken nivå av konfidentialitet eller extern kommunikation som förväntas.',
        tab: 'platform',
        title: 'Mål, scope framing och avslöjande logik',
        entries: [
          {
            title: 'Avsedda tillämpningar, skäl och målgrupp',
            description:
              'Dessa fält förklarar varför LCA görs och vem som behöver resultatet. De förankrar senare metodval.',
            bullets: [
              'Använd avsedda applikationer för beslut eller leveranser såsom intern ekodesign, kundförfrågan eller regelförberedelser.',
              'Studieskäl bör förklara affärsutlösaren eller risken.',
              'Målgrupp kan inkludera interna team, kunder, certifierare eller tillsynsmyndigheter.',
            ],
          },
          {
            title: 'kommissionsledamot och externa experter',
            description:
              'Registrera vem som beställt studien och om externa specialister bidrog till modellering, granskning eller verifiering.',
            bullets: [
              'Använd uppdragsgivarens namn när projektet initierades av en specifik kund eller affärsenhet.',
              'Slå på externa experter först när de faktiskt var inblandade.',
              'Namnge expertorganisationen eller rollen så att senare granskare vet vem som stödde arbetet.',
            ],
          },
          {
            title: 'Avslöjande, sekretess och påståenden',
            description:
              'Dessa switchar hjälper till att separera data som kan delas offentligt från data som förblir intern eller föremål för restriktioner.',
            bullets: [
              'Var tydlig om huruvida resultat eller påståenden kommer att avslöjas externt.',
              'Använd beskrivningsfältet för att förklara vad som kan delas och under vilka förutsättningar.',
              'Håll konfidentiella anteckningar tydliga så att ett framtida pass eller rapport inte avslöjar begränsad data av misstag.',
            ],
          },
          {
            title: 'Ytterligare anmärkningar',
            description:
              'Använd kommentarer för studiesammanhang, styrningsanteckningar, granskningsfrågor eller varningar som har betydelse för tolkning men som inte hör hemma i strukturerade frågor.',
            bullets: [
              'Dokumentera olösta problem istället för att lämna dem underförstådda.',
              'Notera planerade uppdateringar när data förväntas från leverantörer senare.',
              'Använd kommentarer för att överlämna sammanhang mellan interna team.',
            ],
          },
        ],
      },
      {
        badge: 'LCA',
        description:
          'Dessa avsnitt förklarar logiken i en LCA och hur plattformen speglar standardarbetsflödet från definition av omfattning till tolkning.',
        tab: 'lca',
        title: 'LCA grunder och studiearkitektur',
        entries: [
          {
            title: 'Vad LCA är',
            description:
              'Livscykelanalys är en strukturerad metod för att kvantifiera miljöpåverkan över produktens livscykel, inte bara koldioxidutsläpp.',
            bullets: [
              'Det omfattar vanligtvis råvaror, tillverkning, transport, användning och livslängd.',
              'Resultaten är bara meningsfulla när målet, omfattningen och gränserna är transparenta.',
              'Plattformen är designad för att fånga både strukturerad data och antagandena bakom den.',
            ],
          },
          {
            title: 'De fyra LCA-faserna',
            description:
              'En komplett studie innefattar normalt mål- och omfattningsdefinition, inventeringsanalys, konsekvensanalys och tolkning.',
            bullets: [
              'Mål och omfattning förklarar varför studien finns och vad som ingår.',
              'Inventering samlar flöden av material, energi, transporter och avfall.',
              'Konsekvensanalys översätter inventeringsflöden till effektindikatorer.',
            ],
          },
          {
            title: 'Varför funktionell enhet är viktig',
            description:
              'Den funktionella enheten beskriver vilken tjänst produkten levererar och skapar grunden för en rättvis modell och rättvisa jämförelser.',
            bullets: [
              'Använd ett funktionsbaserat uttalande, inte bara en enkel produkträkning.',
              'För tjänster, beskriv den levererade tjänsten, varaktighet eller genomströmning.',
              'Kontrollera att leverantörer och interna team kan mappa sina data till samma referens.',
            ],
          },
          {
            title: 'Vad gör en studie granskbar',
            description:
              'En studie kan bara försvaras när datauppsättningar, antaganden, källor och uteslutningar är synliga för en annan expert.',
            bullets: [
              'Dokumentera varför primärdata användes eller varför en proxydatauppsättning valdes.',
              'Förklara viktiga antaganden och cut-off logik.',
              'Håll bevispaketet nära modellen, inte i separata e-posttrådar.',
            ],
          },
        ],
      },
      {
        badge: 'Fält',
        description:
          'Produktegenskaper tillhandahåller modelleringsreferensen för den deklarerade produkten eller tjänsten och stöder logik för användningsfas och uttjänt livslängd.',
        tab: 'lca',
        title: 'Produktegenskaper och materialdata',
        entries: [
          {
            title: 'Funktionell enhet och datainsamlingsperiod',
            description:
              'Dessa fält definierar vad som bedöms och vilket tidsfönster primärdatan representerar.',
            bullets: [
              'Skriv funktions- eller tjänsteenheten på ett sätt som kan användas konsekvent över beräkningar.',
              'Använd datainsamlingsdatum som återspeglar den verkliga driftsperioden för insamlad data.',
              'Blanda inte fleråriga medelvärden och en månads ögonblicksbilder utan förklaring.',
            ],
          },
          {
            title: 'Vikt, mått och volym',
            description:
              'Dessa fysiska egenskaper stödjer logistikantaganden, materialkontroller, förpackningslogik och nedströms passinnehåll.',
            bullets: [
              'Använd uppmätta värden när det är möjligt.',
              'Håll enheterna konsekventa och realistiska för produkttypen.',
              'Om dimensioner är platshållare, markera det tydligt i antaganden eller kommentarer.',
            ],
          },
          {
            title: 'Materialsammansättning',
            description:
              'Lista bas- och tilläggsmaterial med procentandel och återvunnen andel så att sammansättningen kan förstås och återanvändas i miljömodellering.',
            bullets: [
              'Börja med de material som dominerar massa eller slag.',
              'Använd återvunnen andel endast när det finns bevis eller ett motiverat antagande.',
              'Kontrollera om procentsatserna är riktade fullständiga innan du delar resultat externt.',
            ],
          },
          {
            title: 'Massbalans disciplin',
            description:
              'Materialrader ska tillsammans bilda en sammanhängande bild av produkten. Även när summan ännu inte är perfekt bör modellen visa var luckorna finns.',
            bullets: [
              'Granska om huvudmaterial förklarar det mesta av produktmassan.',
              'Använd ytterligare rader för beläggningar, tillsatser eller mindre komponenter när det är relevant.',
              'Eskalera stora oförklarade massluckor innan du publicerar ett pass eller en rapport.',
            ],
          },
        ],
      },
      {
        badge: 'Metod',
        description:
          'Antagandefält dokumenterar hur produkten är tillverkad, vilken referenslivslängd som används och var studien medvetet utesluter lågsignifikanta flöden.',
        tab: 'lca',
        title: 'Processer, antaganden och livslängd',
        entries: [
          {
            title: 'Produktionsprocessbeskrivning',
            description:
              'Beskriv de centrala processtegen som väsentligt påverkar fotavtrycket, såsom formning, montering, efterbehandling, testning eller förpackning.',
            bullets: [
              'Fokusera på de operationer som är viktiga för effekten, inte varje mikrosteg.',
              'Nämn platsen eller tekniken när den ändrar energi- eller avkastningsantaganden.',
              'Håll beskrivningen begriplig för någon utanför anläggningsteamet.',
            ],
          },
          {
            title: 'Antaganden och cut-off regler',
            description:
              'Dessa fält förklarar hur saknade data, flöden med låg signifikans och uppskattningslogik behandlades i modellen.',
            bullets: [
              'Dokument var primärdata inte var tillgänglig och vilken proxy som användes istället.',
              'Använd avgränsningsregler konsekvent så att uteslutningar kan granskas senare.',
              'Förklara alla antaganden som väsentligt påverkar tolkning eller jämförelse.',
            ],
          },
          {
            title: 'Referens livslängd',
            description:
              'Referenslivslängd kopplar samman användningsfas, hållbarhet och livslängdstänkande. Det bör återspegla en försvarbar förväntan, inte ett marknadsföringspåstående.',
            bullets: [
              'Koppla livslängden till testdata, garantilogik, standarder eller expertbedömning.',
              'Håll det i överensstämmelse med antaganden om underhåll eller reservdelar.',
              'Uppdatera det när nya bevis ändrar hållbarhetsförväntningarna.',
            ],
          },
          {
            title: 'Ytterligare tolkningsanmärkningar',
            description:
              'Använd valfria anteckningar för osäkerheter, scenarier eller metodval som inte passar någon annanstans men påverkar hur resultaten ska läsas.',
            bullets: [
              'Ring ut där känslighetstestning kan behövas.',
              'Observera om kundspecifika varianter kan förändra fotavtrycket väsentligt.',
              'Använd det här området för att bevara granskarens sammanhang för framtida uppdateringar.',
            ],
          },
        ],
      },
      {
        badge: 'Databas',
        description:
          'Ecoinvent är bakgrundsdatabasen bakom många LCA-modeller. Användare bör förstå vad det är bra för, hur man väljer datauppsättningar och hur man dokumenterar proxyanvändning.',
        tab: 'lca',
        title: 'Ecoinvent och sekundär dataanvändning',
        entries: [
          {
            title: 'Vad ecoinvent är',
            description:
              'Ecoinvent är en livscykelinventeringsdatabas med bakgrundsdata för material, energi, transport, avfallsbehandling och många industriella processer.',
            bullets: [
              'Den används när primär produktspecifik data är otillgänglig eller ofullständig.',
              'Den ska komplettera primärdata, inte ersätta den utan eftertanke.',
              'Val av version och datauppsättning bör registreras som en del av studielogiken.',
            ],
          },
          {
            title: 'Hur man använder ecoinvent på ett ansvarsfullt sätt',
            description:
              'Välj datauppsättningar som matchar geografi, teknologi och produkttyp så nära som möjligt, förklara sedan passformen och eventuella kända missmatchningar.',
            bullets: [
              'Börja från den verkliga processen och hitta den närmaste försvarbara proxyn.',
              'Föredra datamängder som matchar region och teknik före generiska globala proxyservrar.',
              'Fånga varför en datauppsättning valdes så att en annan modell kan upprepa logiken.',
            ],
          },
          {
            title: 'Hur man kombinerar primära och sekundära data',
            description:
              'Primärdata ska beskriva den organisationsspecifika aktiviteten, medan sekundärdata fyller luckor i bakgrundsprocesser eller generiska uppströmsmoduler.',
            bullets: [
              'Använd primärdata för kärnmaterial, växtenergi, avkastning och transport där det är möjligt.',
              'Använd sekundära datauppsättningar för uppströms eller generiska bakgrundsprocesser som inte är praktiska att mäta direkt.',
              'Var tydlig om var modellen slutar vara primärdatadriven.',
            ],
          },
          {
            title: 'Vad ska man berätta för användarna om datakvalitet',
            description:
              'Användare bör veta när ett resultat är baserat på uppmätta data kontra proxy-antaganden. Att transparens är viktigare än att låtsas som att modellen är perfekt.',
            bullets: [
              'Flagga datamängder med svag geografisk eller teknisk passform.',
              'Håll en lista över de viktigaste proxydatauppsättningarna för att förbättra senare.',
              'Använd uppföljningsuppgifter för att ersätta fullmakter när bättre leverantörs- eller anläggningsdata blir tillgängliga.',
            ],
          },
        ],
      },
      {
        badge: 'Analys',
        description:
          'Fliken Analys hjälper användare att tolka den aktuella modellen, testa snabba vad-om-scenarier och förstå hur det valda påverkansområdet beter sig innan de ändrar själva produktdatan.',
        tab: 'lca',
        title: 'Hur man använder de analytiska funktionerna',
        visualId: 'analytics-guide',
        entries: [
          {
            title: 'Börja från det valda nedslagsområdet',
            description:
              'Analys fungerar alltid för den aktuella effektkategorin och enheten, till exempel kg CO2-Eq. Innan du läser diagram, se till att det valda inverkansområdet stämmer överens med den affärs- eller metodfråga du faktiskt vill besvara.',
            bullets: [
              'Ändra påverkansområdet i diagramfiltren först och öppna sedan Analys.',
              'Läs varje resultat tillsammans med dess enhet eftersom olika effektkategorier inte är direkt jämförbara.',
              'Använd samma påverkansområde när du diskuterar baslinje och scenarier med andra team.',
              'Om diskussionen handlar om klimatpåverkan, håll dig i kg CO2-Eq under hela jämförelsen istället för att byta kategori mitt i granskningen.',
            ],
          },
          {
            title: 'Använd Scenarioutforskaren för snabba vad-om-beslut',
            description:
              'Scenarioutforskaren är det praktiska beslutsverktyget. Den uppskattar hur det nuvarande resultatet skulle utvecklas om återvunnet innehåll ökade, produktmassan minskade eller transportbördan minskade. Det är tänkt att stödja prioritering innan någon redigerar den verkliga produktmodellen.',
            bullets: [
              'Använd återvunnet material när du diskuterar leverantörsförbättringar eller materialbyten.',
              'Använd lättviktning när ingenjören vill testa lägre massa eller lägre materialintensitet.',
              'Använd transportoptimering när logistik, inköp eller förpackningsförändringar övervägs.',
              'Använd reglagen för att testa realistiska intervall först, inte idealiserade bästa fallvärden som företaget inte kan implementera.',
            ],
          },
          {
            title: 'Hur man läser scenariokorten',
            description:
              'Varje kort visar baslinjens fotavtryck, det uppskattade scenariets fotavtryck och delta. Negativt delta betyder att scenariot sänker påverkan i förhållande till den aktuella modellen, medan ett litet delta kan indikera att parametern inte är en stark spak för denna produkt.',
            bullets: [
              'Behandla baslinjen som den nuvarande produktmodellen, inte som ett marknadsriktmärke.',
              'Titta på delta och deltaprocent tillsammans för att förstå både absolut och relativ effekt.',
              'Använd scenarier för att prioritera åtgärder, uppdatera sedan den underliggande modellen om ändringen accepteras.',
              'Om ett scenario förändras lite är det fortfarande ett användbart resultat eftersom det talar om för teamet var de inte ska lägga kraft först.',
            ],
          },
          {
            title: 'Vad Monte Carlo är användbart för här',
            description:
              'Monte Carlo på den här skärmen hjälper till att visualisera variationen kring det aktuella totala resultatet för det valda nedslagsområdet. Det är användbart som en osäkerhetsliknande vy när användare behöver förklara att resultatet inte är ett enda helt exakt tal, men det bör inte ersätta granskning av antaganden, datamängder och scenlogik.',
            bullets: [
              'Använd den för att kommunicera att resultaten är uppskattningar snarare än helt exakta siffror.',
              'Behandla det inte som ett bevis på att en modell är korrekt; det beror fortfarande på kvaliteten på de underliggande indata.',
              'När ett beslut beror på en parameter är Scenarioutforskaren vanligtvis mer handlingskraftig än Monte Carlo ensam.',
              'Använd Monte Carlo främst för att skapa förtroende och variation, inte för att välja mellan operativa alternativ i sig.',
            ],
          },
          {
            title: 'Vad Analys inte ersätter',
            description:
              'Analys hjälper till med tolkning och prioritering, men det ersätter inte modellstyrning. Ett snyggt scenarioresultat betyder inte att produktposten, antagandena eller etappkartläggningen redan är korrekta.',
            bullets: [
              'Använd inte Analys som ett substitut för att kontrollera material, transportsträckor, uttjänta rutter eller aktivitetsdata.',
              'Behandla inte skjutreglage som godkända produktändringar förrän underliggande källdata har uppdaterats.',
              'Jämför inte två scenarier skapade för olika effektkategorier som om de svarade på samma fråga.',
              'Om modellen saknar ett väsentligt stadium eller använder en svag proxydatauppsättning, fixa det först innan du drar starka slutsatser.',
            ],
          },
          {
            title: 'Rekommenderat arbetsflöde för analytiker',
            description:
              'Ett praktiskt arbetsflöde är: granska flödes- och effektvyn först, öppna sedan Analys för att testa alternativ, och bestäm först efteråt om produktposten eller flödesschemat ska uppdateras. Detta hindrar teamet från att ändra källdata innan de förstår vad som verkligen driver resultatet.',
            bullets: [
              'Bekräfta först vilka stadier som dominerar fotavtrycket i den aktuella modellen.',
              'Testa nästa 2-3 realistiska scenarier istället för många extrema skjutreglagevärden.',
              'Konvertera slutligen det valda scenariot till en riktig data- eller modelländring så att baslinjen förblir pålitlig.',
              'Efter modelluppdateringen öppnar du Analys igen och bekräftar om den förväntade minskningen fortfarande är synlig i den nya baslinjen.',
            ],
          },
        ],
      },
      {
        badge: 'Ecoinvent arbetsflöde',
        description:
          'Det här avsnittet förklarar hur Envirly LCA-plattformen strukturerar ecoinvent-åtkomst och -sökning, varför det grupperade aktivitetssteget är viktigt och hur man går från en produktidé till ett försvarbart datauppsättningsval.',
        tab: 'ecoinvent',
        title: 'Hur ecoinvent-val fungerar i Envirly LCA-plattformen',
        entries: [
          {
            title:
              'Börja med modelleringsobjektet, inte med det första nyckelordet',
            description:
              'Innan du söker, definiera vad objektet faktiskt representerar i modellen: köpt insatsmaterial, tillverkningsprocess, transportsträcka, behandlingsväg eller marknadsgenomsnittligt utbud.',
            bullets: [
              'Skriv ner objektfunktionen i en mening innan du öppnar sökningen.',
              'Ange om föremålet är en material-, process-, tjänst-, transport- eller avfallsverksamhet.',
              'Identifiera det förväntade referensflödet och enheten så att sökresultat snabbt kan avvisas när de inte matchar.',
              'Om objektet är en del av en produktspecifik rutt, notera vilka primära data som redan är kända innan du förlitar dig på sekundära datamängder.',
            ],
          },
          {
            title: 'Använd det grupperade aktivitetssteget medvetet',
            description:
              'I Envirly LCA-plattformen väljer användarna först en grupperad aktivitet, som kombinerar ett aktivitetsnamn och en referensprodukt över flera geografier. Först efter det väljer användaren den exakta geografivarianten.',
            bullets: [
              'Detta flöde förhindrar användare från att behandla geografi som en eftertanke och speglar hur datasetfamiljer är strukturerade i ecoinvent.',
              'Det första valet bör svara på vilken process eller marknad som är relevant; det andra valet bör svara på var den aktiviteten sker.',
              'Granskningsgeografier räknas som en signal om att flera regionala varianter finns och kräver ett medvetet beslut.',
              'Anta inte att det första resultatet är rätt geografivariant även om aktivitetsformuleringen ser exakt ut.',
            ],
          },
          {
            title: 'Använd organisationsminne, men verifiera det',
            description:
              'Envirly LCA-plattformen visar nya eller tidigare använda aktiviteter eftersom upprepade kategorier ofta konvergerar på en liten uppsättning acceptabla proxyservrar. Den bekvämligheten bör påskynda granskningen, inte ersätta den.',
            bullets: [
              'Tidigare använda aktiviteter är användbara när samma underkategori upprepade gånger modelleras över produkter.',
              'Återanvändning av en tidigare datauppsättning är endast motiverad om funktion, teknik, enhet och geografi förblir jämförbara.',
              'Om produktkontexten ändrades, behandla snabbvalet som en kortlistaskandidat och kontrollera datasetfamiljen igen.',
              'Håll kommentarer tillräckligt tydliga så att nästa analytiker förstår om en återanvänd datauppsättning är exakt, ungefärlig eller en tillfällig proxy.',
            ],
          },
          {
            title:
              'Använd ecoQuery som ett verifieringsverktyg, inte som ett separat modelleringsuniversum',
            description:
              'Envirly LCA-plattformen ger tillgång till ecoinvent från produktarbetsflödet och länkar användare till ecoQuery i cut-off systemmodellen. Syftet är att inspektera metadata, jämföra syskongeografier och förstå datamängden, inte att tyst byta systemmodell mitt i studien.',
            bullets: [
              'Kontrollera att ecoQuery-sidan använder samma ecoinvent-version som produkten i Envirly LCA-plattformen.',
              'Använd ecoQuery-vyn för att inspektera aktivitetsfamiljen, metadata och närliggande geografivarianter.',
              'När du jämför resultat, håll dig inom samma systemmodell och version annars blir jämförelsen metodologiskt ogiltig.',
              'Dokumentera eventuella slutsatser från ecoQuery tillbaka till Envirly LCA-plattformens antaganden eller kommentarer så att resonemanget förblir med produktposten.',
            ],
          },
        ],
      },
      {
        badge: 'Val av datauppsättning',
        description:
          'De flesta misstag sker för att en datauppsättning låter rätt språkligt men är fel funktionellt. Det här avsnittet fokuserar på de kontroller som experter bör tillämpa innan de accepterar en datauppsättning.',
        tab: 'ecoinvent',
        title: 'Hur man väljer rätt datasetfamilj',
        entries: [
          {
            title:
              'Särskilja marknad, marknadsgrupp, produktion och behandling',
            description:
              'Aktivitetstyp är ett metodiskt val. Liknande etiketter kan representera olika systemlogik och därför olika inventeringar och effekter.',
            bullets: [
              'Använd datauppsättningar av produktionstyp när du modellerar en specifik tillverkningsrutt eller transformationsprocess.',
              'Använd marknadsdatauppsättningar när objektet representerar köpt utbud från en geografispecifik mix snarare än en känd producent.',
              'Behandla marknadsgruppsdatauppsättningar som bredare utbudsaggregationer som kan skilja sig från en direkt marknadsdatauppsättning i sammansättning och avsedd tolkning.',
              'Använd behandlingsdatauppsättningar endast för avfallshantering eller uttjänta verksamheter och aldrig som en genväg för vanlig materialproduktion.',
            ],
          },
          {
            title:
              'Verifiera referensprodukten innan du läser effektresultatet',
            description:
              'Referensprodukten definierar vad en enhet av datamängden levererar. Om den logiken inte stämmer överens med det modellerade objektet är datauppsättningen fel även om effektnumret ser rimligt ut.',
            bullets: [
              'Kontrollera referensproduktens namn och se till att det återspeglar samma utdata som objektet i Envirly LCA-plattformen.',
              'Kontrollera referensproduktenheten och se till att kvantitetsomvandlingen är tydlig och kan granskas.',
              'Kontrollera referensproduktmängden där det är relevant, särskilt för tjänster eller processresultat som inte uttrycks som en snygg fysisk enhet.',
              'Avvisa ett dataset tidigt om det representerar en annan fysisk form, koncentration, behandlingstillstånd eller tjänstegräns än objektet i omfattningen.',
            ],
          },
          {
            title: 'Lös geografi hierarkiskt',
            description:
              'Välj geografi genom att följa den fysiska tillförselvägen från mest specifik till minst specifik, inte genom att välja den största tillgängliga regionen.',
            bullets: [
              'Börja med produktionslandet eller den kända leveransplatsen närhelst det är väsentligt relevant.',
              'Om det inte finns någon landsspecifik datauppsättning, flytta till närmaste försvarbara regionala datauppsättning och notera den begränsningen uttryckligen.',
              'Använd endast RoW eller GLO när de verkligen återspeglar den avsedda leveranslogiken eller när ingen starkare geografi är tillgänglig.',
              'Var särskilt försiktig med el, transporter och avfallshantering eftersom geografi ofta förändrar resultat väsentligt i dessa kategorier.',
            ],
          },
          {
            title: 'Välj proxyer transparent när ingen exakt matchning finns',
            description:
              'Val av proxy är normalt expertarbete inom LCA, men det måste vara tydligt. En dold proxy är ett styrningsproblem även när det tekniska valet är rimligt.',
            bullets: [
              'Prioritera funktion, processteknik, materialsammansättning och geografi i den ordningen när du bygger en proxy-rationale.',
              'Undvik att välja en datauppsättning endast eftersom den kommersiella formuleringen liknar produktnamnet.',
              'Ange vad proxyn utesluter eller överdriver så att granskarna kan bedöma den sannolika biasriktningen.',
              'Registrera var leverantörs- eller anläggningsspecifika data skulle ersätta proxyn i nästa iteration av modellen.',
            ],
          },
        ],
      },
      {
        badge: 'Konsekvens och granskning',
        description:
          'Val av datauppsättning är bara en del av bra användning av ecoinvent. Experter behöver också versionskontroll, systemmodellkonsistens och kvalitetsgranskning.',
        tab: 'ecoinvent',
        title: 'Versionskontroll, systemmodelldisciplin och datakvalitet',
        entries: [
          {
            title: 'Håll versionen stabil på produktnivå',
            description:
              'Envirly LCA-plattformen binder en produkt till en ecoinvent-version eftersom byte av bakgrundsdata mitt i projektet gör det svårt att försvara trendanalys och granskning.',
            bullets: [
              'Jämför inte två produktiterationer som om de vore likvärdiga om bakgrundsdatabasversionen ändrades däremellan.',
              'När en ny ecoinvent-version blir tillgänglig, behandla migration som en kontrollerad metoduppdatering snarare än en tyst uppdatering.',
              'Kontrollera aktivitetens tillgänglighet efter duplicering eller kopiering mellan produkter eftersom en tidigare länkad aktivitet kanske inte har datapunkter i den nya versionen.',
              'Vid benchmarking mot externa studier, verifiera både versionen och systemmodellen innan du drar slutsatser av skillnader.',
            ],
          },
          {
            title: 'Blanda inte systemmodeller när du validerar resultat',
            description:
              'Experter jämför ofta utdata mellan olika verktyg, men en jämförelse är meningsfull endast när samma systemmodell används. Envirly LCA-plattformen länkar för närvarande till ecoQuery i cut-off.',
            bullets: [
              'Cut-off, APOS, följd- och EN15804-orienterade vyer bör inte behandlas som utbytbara tolkningar av samma resultat.',
              'Om ett resultat skiljer sig från ett annat verktyg, kontrollera först om det andra resultatet genererades under en annan systemmodell.',
              'Dokumentera den valda systemmodellen i studiens antaganden och håll jämförelser inom den metodiska ramen.',
              'Var försiktig med återvinning och uttjänta påståenden eftersom val av systemmodell väsentligt kan förändra bördans fördelningslogik.',
            ],
          },
          {
            title: 'Bedöm datakvaliteten bortom effektnumret',
            description:
              'ecoinvent-datauppsättningar kommer med metodologiska sammanhang, representativitetsantaganden och osäkerhetslogik. Experter bör granska lämpligheten, inte bara rangordningen.',
            bullets: [
              'Bedöma teknisk, geografisk och tidsmässig representativitet i förhållande till det modellerade objektet.',
              'Behandla härstamnings- och osäkerhetsinformation som signaler för granskningsdjup, inte som en ersättning för tekniskt omdöme.',
              'Fokusera först kvalitetsgranskning på hotspots och objekt med högt bidrag eftersom det är där valet av datauppsättning är viktigast.',
              'Använd känslighets- eller scenariotester när flera rimliga proxyservrar finns och valet kan väsentligt förändra resultatet.',
            ],
          },
          {
            title: 'Vet när du ska eskalera till anpassad data',
            description:
              'Sekundära datauppsättningar är effektiva, men vissa modelleringsfrågor kräver primära eller anpassade data för att förbli trovärdiga.',
            bullets: [
              'Eskalera när företagets process skiljer sig väsentligt från den generiska proxyn i energimix, avkastning, skrothastighet, återvunnet innehåll eller teknik.',
              'Eskalera när en leverantör tillhandahåller granskningsbar produktspecifik data som är mer representativ än den generiska bakgrundsrutten.',
              'Eskalera när ett regulatoriskt eller kundanvändningsfall kräver ett starkare bevisspår än en generisk proxy kan tillhandahålla.',
              'Om en anpassad faktor används, håll källan, enheten, omfattningen och gränsförklaringen lika rigorös som för alla datauppsättningsval.',
            ],
          },
        ],
      },
      {
        badge: 'Pass',
        description:
          'Använd det här avsnittet när utgången är ett produktpass för DPP. Skärmen till höger visar hur plattformen arrangerar block, förhandsgranskning och delningskontroller.',
        tab: 'dpp',
        title: 'Hur passutmatning fungerar',
        visualId: 'passport-guide',
        entries: [
          {
            title: 'Vad passbyggaren gör',
            description:
              'Passbyggaren förvandlar styrd produkt- och LCA-data till en DPP-vy som vänder sig till intressenterna. Det är ett publiceringslager, inte en plats att hitta på saknat innehåll.',
            bullets: [
              'Bygg passsektioner från produktidentifiering, miljö, material, hållbarhet, efterlevnad och servicedata som redan finns lagrade i plattformen.',
              'Se över vilka block som ska synas för produktgruppen och extern publik innan publicering.',
              'Behandla saknade fältvarningar som källdataproblem att fixa i posten, inte som något att korrigera direkt i utdata.',
            ],
          },
          {
            title: 'Förhandsgranska, QR och delning',
            description:
              'Passutdata bör kontrolleras på samma sätt som det senare kommer att delas offentligt eller med intressenter.',
            bullets: [
              'Validera den synliga passvyn på dator och mobil innan du delar den externt.',
              'Behandla QR och dela länkar som kontrollerade publikationer, inte som utkast till samarbetsutrymmen.',
              'Se till att interna anteckningar eller leverantörsbegränsade bevis inte är synliga för allmänheten.',
            ],
          },
          {
            title: 'Uppdatera styrning efter publicering',
            description:
              'Ett pass bör förbli uppdateringsbart efter utgivningen, så produktposten behöver en tydlig granskningsspår och ägarmodell.',
            bullets: [
              'Håll käll-PDF-filer, giltighetsdatum och granska kommentarer i produktposten.',
              'Kontrollera passet igen närhelst leverantörsdata, certifikat, produktvarianter eller antaganden om livslängd ändras.',
              'Tilldela en ägare för uppdateringar efter publicering och offentlig utskriftskvalitet.',
            ],
          },
        ],
      },
      {
        badge: 'Rapporter',
        description:
          'Använd det här avsnittet när samma produktstomme måste generera formella rapporter. Skärmen till höger visar rapportbyggarflödet inuti plattformen.',
        tab: 'dpp',
        title: 'Hur rapportutdata fungerar',
        visualId: 'report-guide',
        entries: [
          {
            title: 'Vad rapportbyggaren gör',
            description:
              'Rapportbyggaren sätter samman produktstudien till en strukturerad leverans. Den bör återanvända samma styrda produktryggrad istället för att duplicera innehåll utanför plattformen.',
            bullets: [
              'Behandla rapportgenerering som det sista steget efter att produktdata, bevis och LCA-logik redan har granskats.',
              'Använd samma produktpost för att mata olika rapportformat istället för att bygga om samma berättelse parallellt.',
              'Håll rapportavsnitten i linje med den faktiska modellen, antaganden och bevisspår.',
            ],
          },
          {
            title: 'Rapporter för EPD, PEF och scope 3',
            description:
              'Ett rapportlager kan stödja flera rapporteringsvägar, men logiken i leveransen måste fortfarande matcha målramverket.',
            bullets: [
              'Se till att rapportlogiken matchar den deklarerade enheten, omfattningen, antagandena och metoden som används i studien.',
              'Håll rapportklara bevis, bakgrundsantaganden och versionshistorik i samma produktregister.',
              'Använd en styrd källa till sanning så att ändringar i modellen kan flöda in i rapporter utan dubbletter av redigering.',
            ],
          },
          {
            title: 'Granskning, export och versionskontroll',
            description:
              'En rapport är bara användbar när den exporterade filen kan spåras tillbaka till ett specifikt granskat tillstånd för produkten och modellen.',
            bullets: [
              'Granska avsnittets fullständighet före export så att det genererade dokumentet återspeglar den avsedda omfattningen.',
              'Spåra versioner och exportera endast från en granskad konfiguration, särskilt för externa leveranser.',
              'Håll ägarskapet tydligt för framtida rapportuppdateringar när data, omfattning eller metod ändras.',
            ],
          },
        ],
      },
      {
        badge: 'Operationer',
        description:
          'De flesta organisationer misslyckas inte för att metoden är okänd, utan för att datainsamlingen är fragmenterad. Dessa steg gör plattformen till en driftsmodell.',
        tab: 'dpp',
        title: 'Hur man organiserar produktdatainsamling inom företaget',
        entries: [
          {
            title: 'Börja med en ägare och en mall',
            description:
              'Tilldela en intern ägare per produkt och bygg en återkommande förfrågningsstruktur för anläggningar, inköp, ingenjörs- och kvalitetsteam.',
            bullets: [
              'Undvik att skicka olika kalkylblad från olika avdelningar.',
              'Återanvänd plattformens fältstruktur som insamlingsmall.',
              'Gör ägarskapet synligt innan du frågar leverantörer om något.',
            ],
          },
          {
            title: 'Samla in efter livscykelstadium',
            description:
              'Be om data i beställningsmaterial, tillverkning, logistik, användning och livslängd. Det speglar hur modellen senare kommer att byggas och ses över.',
            bullets: [
              'Inköp kan vanligtvis tillhandahålla material och nyckelleverantörer först.',
              'Växter kan tillhandahålla process-, energi-, skrot- och avkastningslogik.',
              'Service- eller kvalitetsteam känner ofta till underhålls- och livstidsantaganden.',
            ],
          },
          {
            title: 'Separera bevis från antaganden',
            description:
              'Varje inkommande nummer ska markeras som uppmätt, deklareras av leverantören eller uppskattas av projektgruppen.',
            bullets: [
              'Använd bevisuppladdningar för filer och deklarationer.',
              'När ett tal uppskattas, säg vem som uppskattade det och varför.',
              'Denna separation gör senare granskning och uppdateringar mycket snabbare.',
            ],
          },
          {
            title: 'Bygg en förbättringseftersläpning',
            description:
              'Vänta inte på perfekt data innan du börjar. Publicera istället den nuvarande modelldisciplinen och behåll en synlig eftersläpning av de luckor som har störst effekt.',
            bullets: [
              'Byt ut de största proxydatauppsättningarna först.',
              'Prioritera leverantörer eller anläggningar som dominerar massa, spenderar eller påverkar.',
              'Använd samma eftersläpning för att förbättra både LCA- och passberedskapen över tid.',
            ],
          },
        ],
      },
      {
        badge: 'GS1',
        description:
          'GS1-identifierare stöder produktmasterdata inuti plattformen och blir användbara när poster behöver starkare interoperabilitet mellan system, förpackningar, etiketter eller nedströmsutbyte.',
        tab: 'platform',
        title: 'GS1-koder, produktidentifierare och spårbarhet',
        entries: [
          {
            title: 'GTIN: Global Trade Artikelnummer',
            description:
              'GTIN är den centrala GS1-identifieraren som används för att identifiera handelsartiklar i leveranskedjor, etiketter, kataloger och digitala system.',
            bullets: [
              'Använd GTIN när produkten redan har ett tilldelat GS1-artikelnummer.',
              'Validera längd och kontrollsiffra före import.',
              'Granska importerat innehåll eftersom GTIN-nummer på förpackningsnivå och marknadsvarianter kan skilja sig åt.',
            ],
          },
          {
            title: 'GPC: Global produktklassificering',
            description:
              'GPC är en GS1-klassificering som grupperar produkter för datautbyte och kategorisering. Det är inte samma sak som CPC eller NACE.',
            bullets: [
              'Använd GPC för att stödja kommersiella och driftskompatibla användningsfall.',
              'Behåll CPC för LCA eller rapporteringskategorilogik vid behov.',
              'Tvinga inte ett kodsystem för att lösa varje klassificeringsbehov.',
            ],
          },
          {
            title: 'Modell-, batch- och serielogik',
            description:
              'Dessa identifierare stödjer spårbarhet under produktfamiljenivå och är användbara när passinnehåll eller tjänstinformation ändras per variant eller batch.',
            bullets: [
              'Modellen identifierar produktversionen eller konfigurationen.',
              'Batch stöder spårbarhet under tillverkningsperioden.',
              'Serienummer är relevant när spårbarhet på enhetsnivå krävs.',
            ],
          },
          {
            title: 'Där GS1 hjälper mest',
            description:
              'GS1 blir mest användbar när utgångar måste anslutas rent till ERP, PLM, e-handel, förpackningar, etiketter eller QR-drivna resor.',
            bullets: [
              'GS1 förbättrar interoperabiliteten och minskar manuell ommappning.',
              'Det hjälper till att länka passinnehåll till fysiska etiketter eller QR-drivna resor.',
              'Det skapar en starkare brygga mellan hållbarhetsdata och produktbasdata.',
            ],
          },
        ],
      },
      {
        badge: 'Efterlevnad',
        description:
          'Behandla detta område som en delad produktdataryggrad med fyra utmatningsvägar: ESPR-produktpass, EPD med HLR-rapport, PEF-rapport och LCA-matande CSRD scope 3.',
        tab: 'regulations',
        title: 'Hur man strukturerar fyra reglerings- och rapporteringsvägar',
        entries: [
          {
            title: 'Rekommenderad struktur för detta avsnitt',
            description:
              'Börja med en delad produktpost och förgrena dig till leveransspecifika arbetsflöden endast där metoder, granskning eller publiceringsregler skiljer sig åt.',
            bullets: [
              'Delad ryggrad: identifierare, produkthierarki, BOM, leverantörsbevis, tillverkningsdata, logistik, geografi, antaganden och versionshistorik.',
              'Branch 1: ESPR till produktpass för identitet, spårbarhet, cirkularitet och kontrollerad datadelning.',
              'Gren 2: EPD och HLR för att rapportera för bygginriktade deklarationer som behöver strukturerad LCA-bevis och granskning.',
              'Gren 3: PEF att rapportera för metodtung jämförbarhet och striktare modelleringsdisciplin.',
              'Branch 4: LCA till CSRD scope 3 för värdekedjeutsläpp på företagsnivå som fortfarande beror på produkt- och leverantörsbevis.',
            ],
          },
          {
            title: 'ESPR till produktpass',
            description:
              'Använd den här sökvägen när målet är DPP-beredskap enligt ESPR och en styrd, uppdateringsbar produkt-passdatauppsättning.',
            bullets: [
              'Data: stabila identifierare, produkt- och variantattribut, detaljer om tillverkare eller ekonomisk operatör, materialsammansättning, reparations- eller serviceinformation och länkade certifikat eller bevis.',
              'Flöde: bygg kärnproduktposten, kartlägg varianter och identifierare, bifoga bevis, konfigurera sedan passblocken och åtkomstregler.',
              'Se upp: delegerade akter kan ändra den exakta nyttolasten per produktgrupp, så håll datamodellen flexibel istället för att hårdkoda en passstruktur.',
              'Se upp: separera offentligt passinnehåll från konfidentiell leverantör eller överensstämmelsebevis före publicering.',
            ],
          },
          {
            title: 'EPD, HLR och rapport',
            description:
              'Använd den här sökvägen när en försvarbar produktstudie måste sluta i en EPD-liknande deklaration eller en rapport som stödjer HLR-relaterad dokumentation.',
            bullets: [
              'Data: deklarerad eller funktionell enhet, stycklista, tillverknings- och energidata, transport, förpackning, avfall, scenarioantaganden och de standarder eller kategoriregler som används för rapporten.',
              'Flöde: bekräfta först måldeklarationslogiken, samla in anläggnings- och leverantörsdata per livscykelstadium, bygg studien med den nödvändiga modulstrukturen, förbered sedan rapportpaketet för granskning och publicering.',
              'Se upp: håll den deklarerade enheten, systemgränsen och modullogiken konsekvent från modell till slutrapport; det är här team ofta skapar omarbetning.',
              'Se upp: dataårgång, webbplatsspecificitet och beviskvalitet måste matcha vad verifieraren eller nedströmskunden förväntar sig.',
            ],
          },
          {
            title: 'PEF och rapport',
            description:
              'Använd den här vägen när jämförbarhet är centralt och studien måste följa en mer föreskrivande miljö-fotavtryckslogik.',
            bullets: [
              'Data: kategorispecifika metodregler, högkvalitativa förgrundsdata, anpassade sekundära datamängder, allokeringsval, antaganden om livslängden och dokumenterade begränsningar av datakvalitet.',
              'Flöde: identifiera den relevanta metoden eller kategorireglerna, modellera med dessa jämförbarhetsbegränsningar från början, granska metodiska val explicit, generera sedan rapporten med antaganden och begränsningar avslöjade.',
              'Se upp: undvik att blanda en generisk screening-LCA med jämförande påståenden i PEF-stil; metoddisciplinen måste vara synlig från det första modelleringsbeslutet.',
              'Se upp: allokering, sekundära data och val vid livets slut kräver tydliga skäl eftersom de väsentligt påverkar jämförbarheten.',
            ],
          },
          {
            title: 'LCA till CSRD scope 3',
            description:
              'Använd den här sökvägen när produkt- och leverantörsbevis måste stödja scope 3-beräkningar på företagsnivå och CSRD-klara upplysningar.',
            bullets: [
              'Data: inköps- eller aktivitetskartläggning, leverantörs- och anläggningsgeografi, volymer, produkt-LCA eller emissionsfaktorer, täckning för rapporteringsåret och osäkerhetsanteckningar.',
              'Flöde: kartlägg produkter och inköp till relevanta scope 3-kategorier, samla in primärdata för hotspots, fyll de återstående luckorna med dokumenterade proxyservrar, aggregera sedan och stämma av med rapporteringsbehov.',
              'Se upp: produktstudier och företagsupplysningar använder olika gränser, så förhindra dubbelräkning och avstäm noggrant med ekonomi- eller upphandlingsdata.',
              'Se upp: årstäckning, konsolideringslogik och proxykvalitet bör vara transparenta, eftersom CSRD-granskning kommer att fokusera på spårbarhet och konsekvens.',
            ],
          },
        ],
      },
    ],
    guideTitle: 'Fält-för-fält-guidning',
    heroDescription:
      'Använd denna kunskapshub som plattformens operativa lager: förstå vad varje form gör, hur man samlar in produktdata internt, hur GS1 och ecoinvent passar in och hur LCA, DPP och regelverk ansluter.',
    heroTitle:
      'Förvandla produktdata till ett användbart arbetsflöde för LCA och produktpass',
    knowledgeCards: [
      {
        bullets: [
          'Skapa produktposten innan du försöker skapa rapporter.',
          'Håll identifierare, producentsammanhang och avsedd användning ren från dag ett.',
          'Behandla plattformen som ett kontrollerat produktbevissystem, inte en dokumentdump.',
        ],
        status: 'Plattform',
        summary:
          'En tillförlitlig implementering börjar med masterdatadisciplin och tydligt ägande, inte med den slutliga PDF-utmatningen.',
        tab: 'platform',
        title: 'Hur man närmar sig plattformen som ett registersystem',
      },
      {
        bullets: [
          'Funktionell enhet, gränser och bevis avgör om studien är försvarbar.',
          'Primärdata är starkast där produkten är specifik och materiell.',
          'Sekundära datauppsättningar ska vara synliga och granskas, inte dolda.',
        ],
        status: 'LCA',
        summary:
          'Plattformen speglar verklig LCA-praxis: först omfattning, sedan inventering och sedan tolkning.',
        tab: 'lca',
        title: 'Hur bra LCA-arbete ser ut inuti plattformen',
      },
      {
        bullets: [
          'Öppna fliken LCA i hjälpcentret och bläddra till Fält-för-fält-vägledning.',
          'Leta efter avsnittet som heter Hur man använder de analytiska funktionerna.',
          'Använd Scenarioutforskaren för praktiska vad-om-kontroller och Monte Carlo som en stödjande vy i osäkerhetsstil.',
        ],
        status: 'LCA-analys',
        summary:
          'Hjälpcentret innehåller en dedikerad guide för fliken Analys, inklusive Scenarioutforskaren och Monte Carlo.',
        tab: 'lca',
        title: 'Var du hittar analysguiden',
      },
      {
        bullets: [
          'Detta område är till för att generera styrda externa utdata från en produktpost.',
          'Pass stöder DPP, medan rapporter stöder EPD, Product Environmental Footprint och scope 3-arbete.',
          'Utdatakvalitet beror på fullständiga källdata, bevis och versionskontroll, inte bara på den slutliga layouten.',
        ],
        status: 'DPP',
        summary:
          'Utdatalagret bör generera både pass och rapporter från samma kontrollerade produktdatabas.',
        tab: 'dpp',
        title: 'Hur en produktpost stöder pass och rapporter',
      },
      {
        bullets: [
          'Bygg en delad produktdataryggrad och dela upp den i fyra leveransvägar istället för fyra separata projekt.',
          'Varje väg behöver olika utdata, men samma disciplin kring identifierare, bevis, LCA-logik och versionshantering.',
          'Den största hävstångseffekten kommer från att tidigt bestämma vilka data som är vanliga och vilka granskningsregler som är vägspecifika.',
        ],
        status: 'förordning',
        summary:
          'Den mest robusta driftmodellen är en produktpost som betjänar ESPR/DPP, EPD med CPR, PEF och CSRD scope 3.',
        tab: 'regulations',
        title: 'Hur man gör om regleringsarbetet till fyra styrda vägar',
      },
      {
        bullets: [
          'Envirly LCA-plattformen skiljer grupperat aktivitetsurval från geografival med avsikt.',
          'Den första frågan är vilken processfamilj som är relevant; den andra är där den aktiviteten sker.',
          'Detta minskar falsk precision som orsakas av att välja en geografi innan man förstår datasetfamiljen.',
        ],
        status: 'Ecoinvent i Envirly LCA-plattform',
        summary:
          'En bra sökprocess i Envirly LCA-plattformen speglar ecoinvent-strukturen: definiera aktivitetsfamiljen först och välj sedan den exakta geografivarianten.',
        tab: 'ecoinvent',
        title:
          'Varför Envirly LCA-plattformen ecoinvent flow har två beslut istället för ett',
      },
      {
        bullets: [
          'En övertygande titel räcker inte; referensprodukt, enhet och aktivitetstyp måste också passa.',
          'Marknads-, marknadsgrupps-, produktions- och behandlingsnamn kan se likadana ut men representerar olika modelleringslogik.',
          'Rätt dataset är den som bevarar funktionell mening, inte den med snyggast formulering.',
        ],
        status: 'Datauppsättning granskning',
        summary:
          'Det vanligaste expertfelet är att acceptera en datauppsättning eftersom dess ordalydelse ser rätt ut innan man kontrollerar vad datauppsättningen faktiskt levererar.',
        tab: 'ecoinvent',
        title:
          'Varför datauppsättningsnamn är användbara ledtrådar men svaga bevis',
      },
      {
        bullets: [
          'Kontrollera ecoinvent-version, systemmodell och geografi innan du jämför siffror mellan olika verktyg.',
          'Envirly LCA-plattformen länkar för närvarande till ecoQuery i cut-off, så APOS eller följdjämförelser kommer inte att vara like-for-like.',
          'Använd ecoQuery för att inspektera datauppsättningar, inte för att tyst byta metod under granskning.',
        ],
        status: 'Godkännande',
        summary:
          'De flesta uppenbara avvikelserna mellan Envirly LCA-plattformen och externa kontroller är metodologiska felmatchningar, inte programvarufel.',
        tab: 'ecoinvent',
        title:
          'Hur man validerar Envirly LCA-plattformsresultat mot ecoQuery utan falska avvikelser',
      },
    ],
    videoInstructionsLabel: 'Videoinstruktioner',
    noMatchingCardsDescription:
      'Prova ett annat sökord eller flytta till en annan arbetsström.',
    noMatchingCardsTitle: 'Inga matchande översiktskort',
    noMatchingQuestionsLabel:
      'Inga matchande frågor för den valda arbetsströmmen och sökordet.',
    officialResourceButtonLabel: 'Öppen källkod',
    officialResources: [
      {
        label: 'ekoinvent',
        summary:
          'Officiell ingångspunkt för ecoinvent-databasen och dess metodologiska sammanhang.',
        url: 'https://ecoinvent.org/',
      },
      {
        label: 'GS1 standarder',
        summary:
          'Översikt över GS1-standarder och identifierare som är relevanta för produktbasdata och interoperabilitet.',
        url: 'https://www.gs1.org/standards',
      },
      {
        label: 'PEF-metoden',
        summary:
          'Europeiska kommissionens översikt över produktens miljöavtrycksmetod och kategoriregler.',
        url: 'https://green-forum.ec.europa.eu/green-business/environmental-footprint-methods/pef-method_en',
      },
      {
        label: 'ESPR och DPP',
        summary:
          'Europeiska kommissionens sida som beskriver Ecodesign for Sustainable Products Regulation och Digital Product Passport-sammanhang.',
        url: 'https://commission.europa.eu/energy-climate-change-environment/standards-tools-and-labels/products-labelling-rules-and-requirements/ecodesign-sustainable-products-regulation_en',
      },
      {
        label: 'CSRD och ESRS',
        summary:
          'Europeiska kommissionens förklaring av skyldigheter och standarder för hållbarhetsrapportering.',
        url: 'https://finance.ec.europa.eu/financial-markets/company-reporting-and-auditing/company-reporting/corporate-sustainability-reporting_en',
      },
      {
        label: 'CBAM',
        summary:
          'Europeiska kommissionens officiella portal för vägledning för Carbon Border Adjustment Mechanism.',
        url: 'https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en',
      },
      {
        label: 'ecoinvent-kunskapsbas',
        summary:
          'Officiell dokumentationshub för metodik, releaser, ecoQuery-guider, vanliga frågor och utbildningsmaterial.',
        url: 'https://support.ecoinvent.org/',
      },
      {
        label: 'Söker efter datauppsättningar i ecoQuery',
        summary:
          'Officiell ecoQuery-vägledning om hur man söker, tolkar datasetfamiljer och navigerar i databasen effektivt.',
        url: 'https://support.ecoinvent.org/searching-for-datasets',
      },
      {
        label: 'Visa datauppsättningar i ecoQuery',
        summary:
          'Officiell guide som förklarar hur man inspekterar datasetmetadata, utbyten och sammanhang när en kandidatdatauppsättning har hittats.',
        url: 'https://support.ecoinvent.org/viewing-datasets-in-ecoquery',
      },
      {
        label: 'Exempel på dataset i ecoQuery',
        summary:
          'Officiella exempel på datauppsättningsstrukturer som hjälper analytiker att förstå hur aktiviteter, produkter och marknader representeras.',
        url: 'https://support.ecoinvent.org/example-datasets',
      },
      {
        label: 'Grunderna i ecoinvent-databasen',
        summary:
          'Officiell utbildningsstartpunkt som täcker databaslogik, modelleringsprinciper och bakgrundsantaganden som är relevanta för expertanvändning.',
        url: 'https://support.ecoinvent.org/migration/e-learning-fundamentals-database',
      },
      {
        label: 'ecoinvent-version 3.12',
        summary:
          'Officiell releasesida som bekräftar att ecoinvent v3.12 släpptes den 5 november 2025 och sammanfattar viktiga sektoruppdateringar.',
        url: 'https://support.ecoinvent.org/ecoinvent-version-3.12',
      },
      {
        label: 'ecoinvent v3.12 webbseminarium Frågor och Svar',
        summary:
          'Officiella svar från ecoinvent om version 3.12-ämnen, inklusive cut-off logik, återvinningsbehandling och vanliga tolkningsfrågor.',
        url: 'https://support.ecoinvent.org/hubfs/Comms/2025%20v3.12%20Release/ecoinvent%20Introduction%20to%20version%203.12%20Webinar%20Q%26A.pdf',
      },
      {
        label: 'Licensiering och tillgång',
        summary:
          'Officiell licensvägledning som förklarar åtkomstmodeller och vad som krävs när ecoinvent görs tillgängligt via mjukvaruverktyg.',
        url: 'https://support.ecoinvent.org/licensing',
      },
    ],
    officialResourcesTitle: 'Primära källor värda att dela med teamet',
    quickActions: [
      {
        description:
          'Hur man fyller plattformen, vad varje fältgrupp betyder och hur man håller bevis bifogade.',
        id: 'platform',
        title: 'Plattformsguide',
      },
      {
        description:
          'Vad LCA är, hur man definierar omfattning och hur man arbetar med produkt- och processdata.',
        id: 'lca',
        title: 'LCA väsentligheter',
      },
      {
        description:
          'Hur man kommer åt ecoinvent från Envirly LCA-plattformen, validerar datauppsättningsval och jämför dem med ecoQuery.',
        id: 'ecoinvent',
        title: 'Ecoinvent expertguide',
      },
      {
        description:
          'Hur plattformen genererar pass för DPP och rapporter för EPD, PEF och scope 3.',
        id: 'dpp',
        title: 'Pass och rapporter',
      },
      {
        description:
          'Hur en produktdataryggrad stöder fyra vägar: ESPR/DPP, EPD med HLR, PEF-rapportering och LCA för CSRD scope 3.',
        id: 'regulations',
        title: 'Regleradar',
      },
    ],
    quickActionsTitle: 'Välj den arbetsström du arbetar med',
    searchPlaceholder:
      'Sökfält, GTIN, GPC, ecoinvent, funktionell enhet, leverantörer...',
    searchNoExactMatchDescription:
      'Vi kunde inte hitta en exakt matchning för denna fras i den aktuella kunskapsbasen. Prova ett av de närmaste ämnena nedan.',
    searchNoExactMatchTitle: 'Ingen exakt matchning hittades',
    searchResultsLabel: 'matchande vägledningsobjekt',
    searchSuggestionOpenLabel: 'Öppet ämne',
    searchSuggestionsLabel: 'Närmast liknande ämnen',
    stats: [
      'Instruktioner på fältnivå',
      'GS1 och ecoinvent förklaras',
      'Arbetsflöde för datainsamling över team',
    ],
    tabs: {
      platform: 'Plattform',
      lca: 'LCA',
      ecoinvent: 'Ecoinvent',
      dpp: 'Pass & rapporter',
      regulations: 'förordningar',
    },
    workflowDescription:
      'Använd detta som den rekommenderade sekvensen för att införa en produkt i plattformen och samla in data i hela organisationen.',
    workflowEmptyDescription:
      'Inga arbetsflödessteg matchade det aktuella sökordet. Prova en bredare term som material, leverantör eller pass.',
    workflowEmptyTitle: 'Inga matchande arbetsflödessteg',
    workflowOutputLabel: 'Förväntade utgångar',
    workflowOwnerLabel: 'Typisk ägare',
    workflowSteps: [
      {
        owner: 'Produktägare / hållbarhetsledare',
        outputs: [
          'Produktägare tilldelas',
          'Studiesyfte överenskommet',
          'Prioriterade ramar identifierade',
        ],
        summary:
          'Börja med att komma överens om varför posten finns, vem som äger den och om målet är intern design, kundförfrågan, DPP-förberedelse eller extern rapportering.',
        tab: 'platform',
        title: '1. Kick off produktrekordet',
      },
      {
        owner: 'Produktägare / stamdata',
        outputs: [
          'Namn och beskrivning ifyllda',
          'CPC och NACE har valts',
          'GTIN och producentinformation granskas',
        ],
        summary:
          'Fyll i kärnproduktposten först så att varje senare begäran pekar på ett stabilt masterdataobjekt.',
        tab: 'platform',
        title: '2. Lås de grundläggande identifierarna och klassificeringarna',
      },
      {
        owner: 'Upphandling/teknik',
        outputs: [
          'Materiallista upprättad',
          'Nyckelleverantörer identifierade',
          'Kritiska certifikat begärs',
        ],
        summary:
          'Samla in den första materiallistan, leverantörskartan och dokumentationen som kommer att förankra modellen och bevispaketet.',
        tab: 'platform',
        title: '3. Samla in uppströms produktbevis',
      },
      {
        owner: 'Anläggning / drift / LCA analytiker',
        outputs: [
          'Processbeskrivning fångad',
          'Produktionsgeografi bekräftad',
          'Primär datatäckning bedömd',
        ],
        summary:
          'Samla information om tillverkning, energi, avkastning, logistik och plats i samma struktur som LCA kommer att använda senare.',
        tab: 'lca',
        title: '4. Bygg ryggraden i livscykelinventeringen',
      },
      {
        owner: 'LCA analytiker',
        outputs: [
          'Funktionell enhet definierad',
          'Antaganden dokumenterade',
          'Sekundära datauppsättningar motiverade',
        ],
        summary:
          'Förvandla rå operativ data till en försvarbar modell genom att dokumentera antaganden, gränser och logiken bakom val av ekoinvent eller proxydatauppsättning.',
        tab: 'lca',
        title: '5. Modellera, validera och tolka',
      },
      {
        owner: 'LCA-utövare / produktägare',
        outputs: [
          'Objektfunktion definierad',
          'Förväntad enhet identifierad',
          'Kända geografiska begränsningar listade',
        ],
        summary:
          'Rama in modelleringsobjektet innan du söker så att datauppsättningsvalet styrs av funktion, enhet och försörjningslogik snarare än av enbart formulering.',
        tab: 'ecoinvent',
        title: '1. Definiera vad objektet representerar',
      },
      {
        owner: 'LCA-utövare',
        outputs: [
          'Kortlista över grupperade aktiviteter',
          'Avvisade falska positiva dokumenterade mentalt eller i anteckningar',
          'Relevant aktivitetstyp identifierad',
        ],
        summary:
          'Få tillgång till ecoinvent från Envirly LCA-plattformen, sök grupperade aktiviteter där och avvisa alternativ som misslyckas på aktivitetstyp, referensprodukt eller enhet innan geografi övervägs.',
        tab: 'ecoinvent',
        title: '2. Bygg en försvarbar kortlista',
      },
      {
        owner: 'LCA utövare / ingenjör / upphandling',
        outputs: [
          'Vald geografivariant',
          'Proxy eller exakt matchning klassificeras',
          'Nyckelbegränsning identifierad',
        ],
        summary:
          'Bestäm den exakta geografin baserat på var aktiviteten sker och avgör om den slutliga datamängden är exakt eller en transparent proxy.',
        tab: 'ecoinvent',
        title: '3. Slutför geografi och proxylogik',
      },
      {
        owner: 'LCA granskare / hållbarhetsledare',
        outputs: [
          'Antagande spår uppdaterat',
          'Version och systemmodell bekräftad',
          'Hotspot granskning slutförd',
        ],
        summary:
          'Granska den valda datamängden mot version, systemmodell och bidrag från hotspot så att den slutliga länken är tekniskt och metodologiskt sammanhängande.',
        tab: 'ecoinvent',
        title: '4. Granska och lås valet',
      },
      {
        owner: 'Ledare för digital produkt/efterlevnad',
        outputs: [
          'Pass eller rapportutdata granskas',
          'Delad vy eller exporterad leverans har validerats',
          'Uppdatera ägaren tilldelad',
        ],
        summary:
          'Efter att källdata har styrts, konfigurera passet eller rapportens utdata och se till att det publicerade eller exporterade resultatet är komplett, går att granska och lämpligt för publiken.',
        tab: 'dpp',
        title: '6. Publicera pass och rapporter',
      },
      {
        owner: 'Efterlevnads-/hållbarhetsteam',
        outputs: [
          'Bevislänkar behålls',
          'Tillämpliga ramar kartlagda',
          'Förbättring eftersläpning prioriteras',
        ],
        summary:
          'Behåll ett bevislager för regleringar, kundförfrågningar och framtida uppdateringar istället för att bygga om data från grunden varje gång.',
        tab: 'regulations',
        title: '7. Återanvänd posten över ramar',
      },
    ],
    workflowTitle: 'Rekommenderad implementeringssekvens',
  },
};

export const getHelpCentreLanguage = (language: string) => {
  const normalizedLanguage = language.toLowerCase();

  if (normalizedLanguage.startsWith('pl')) {
    return 'pl';
  }

  if (normalizedLanguage.startsWith('sv')) {
    return 'sv';
  }

  return 'en';
};

export const getLcaVideoInstructionsUrl = (language: string) =>
  `https://assets.envirly.pl/intro/${getHelpCentreLanguage(language)}/LCA.mp4`;

export const getHelpCentreContent = (language: string) => {
  return HELP_CENTRE_CONTENT[getHelpCentreLanguage(language)];
};
